// Typed API client for the Worker's /api/* surface.
// Uses the core package's shared types so schema drift shows up at compile time.

import type {
  ConnectorMetricsRead,
  ConnectorRegrantState,
  Framework,
  ParsedCustomerProfile,
  ProfileInput,
  ScopeAnswer,
  ScopePreview,
  SystemScopePreview,
  ProposedChange,
  RuleAuthoringAction,
  RuleAuthoringItemView,
  AuthoringContext,
  JurisdictionTerm,
  CanonicalRole,
  AuthoredRuleInput,
  AuthoredRuleRecord,
  Predicate,
  ProposedChangeSeverity,
  ProposedChangeSourceKind,
  ProposedChangeStatus,
  ProposedChangeTarget,
  ContentBatchRow,
  ProposalProgramKey,
  ParsedProposedChange,
  AssessmentWithMethodology,
  ReadinessAssessment,
  ResearchSource,
  ResearchSourceKind,
  RiskCategory,
  Scorecard,
  RoadmapResult,
  ProgressSnapshot,
  ReadinessOutcomeScore,
  ParsedReadinessOutcome,
  ParsedControl,
  AISystemWithClassifications,
  AISystemInventoryItem,
  RiskItem,
  CustomerRegisterItem,
  CustomerRegisterItemStatus,
  ServiceClassificationType,
  Severity,
  MaturityLevel,
  ServiceClassification,
  MyWorkItem,
  MyWorkSummary,
  QuestionnaireRow,
  QuestionnaireQuestionRow,
  QuestionnaireAnswerRow,
  QuestionnaireRollup,
  DeadlineUrgency,
  // --- Types formerly re-declared locally; now imported from core (schema-drift fix) ---
  AlertCategory,
  AlertSeverity,
  AlertStatus,
  AlertCadence,
  AlertForwardDeliveryMethod,
  ApiTokenScope,
  ApiTokenRoleAtCreation,
  DisputeResolution,
  EngagementArchiveWindow,
  EvidenceRequestSeverity,
  EvidenceRequestStatus,
  QaTargetKind,
  QaAuthorRole,
  QaThreadStatus,
  SigningTopology,
  WebhookStatus,
  WebhookBindingType,
  WebhookDeliveryResult,
  // V98-3-1 / V98-3-2
  ProgramCard,
  ProgramCardsView,
  TodayView,
  WorkCounts,
  BoundaryView,
  ExternalServiceView,
  CoverageDocumentKind,
  ProgramKey,
  // V102-8-1 / V102-8-2
  CmmcScoreResult,
  AffirmationGate,
  AffirmationRecord,
  PoamItem,
  ObjectiveReadiness,
  StaleMet,
  AssessmentDetermination,
  CustomerRequirement,
  CustomerDeadline,
  // V102-3-4
  AssetSuggestion,
  ScopedAsset,
  AssetPeriodMembership,
  ScopeDraftRow,
  ResolvedEvidenceRef,
  ControlImpact,
  ImpactOutcome,
  FiledFollowOn,
  SkippedFollowOn,
} from '@aigovops/core';

// Re-export the above types so existing consumers of api.ts keep working.
export type {
  AlertCategory,
  AlertSeverity,
  AlertStatus,
  AlertCadence,
  AlertForwardDeliveryMethod,
  ApiTokenScope,
  ApiTokenRoleAtCreation,
  DisputeResolution,
  EngagementArchiveWindow,
  EvidenceRequestSeverity,
  EvidenceRequestStatus,
  QaTargetKind,
  QaAuthorRole,
  QaThreadStatus,
  SigningTopology,
  WebhookStatus,
  WebhookBindingType,
  WebhookDeliveryResult,
};

const BASE = ''; // same-origin; Worker proxies /api to itself and Vite proxies during dev

export async function req<T>(path: string, init?: RequestInit): Promise<T> {
  // CSRF: state-changing calls need X-CSRF-Token. Injected globally by
  // installCsrfFetchInterceptor() in main.tsx, not here — keeps a
  // single install point that also covers the scattered inline
  // `await fetch('/api/...', { method: 'POST' })` sites.
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let message = res.statusText;
    let parsed: unknown;
    try {
      const body = (await res.json()) as { error?: string; detail?: string } | null;
      parsed = body;
      if (body?.error) {
        message = body.detail ? `${body.error} — ${body.detail}` : body.error;
      }
    } catch {
      /* swallow */
    }
    throw new ApiError(res.status, message, parsed);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/** Text responses (CSV export, plain-text summaries) with the same error handling as req(). */
export async function reqText(path: string): Promise<string> {
  const res = await fetch(`${BASE}${path}`);
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: string; detail?: string } | null;
      if (body?.error) message = body.detail ? `${body.error} — ${body.detail}` : body.error;
    } catch { /* swallow */ }
    throw new ApiError(res.status, message, null);
  }
  return res.text();
}

export class ApiError extends Error {
  /** `body` is the parsed JSON error body when the server sent one (field errors, codes). */
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

// --- Health ---
export const health = () => req<{ ok: boolean }>('/api/health');

// --- Register ---
export type RegisterListParams = {
  category?: string;
  severity?: string;
  status?: string;
  framework?: string;
  jurisdiction?: string;
  classification?: string;
  search?: string;
  limit?: number;
  offset?: number;
};
export function listRegister(params: RegisterListParams = {}) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') q.append(k, String(v));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return req<{ data: RiskItem[]; total: number; limit: number; offset: number }>(
    `/api/register${suffix}`
  );
}
export function getRegisterItem(id: string) {
  return req<{
    item: RiskItem;
    frameworkMappings: Array<{
      framework_id: string;
      framework_name: string;
      control_ref: string;
      control_title: string;
      relationship: string;
    }>;
  }>(`/api/register/${encodeURIComponent(id)}`);
}

// --- Frameworks ---
export const listFrameworks = async (): Promise<Framework[]> =>
  (await req<{ data: Framework[] }>('/api/frameworks')).data;

// --- Classifications ---
export const listClassifications = () =>
  req<ServiceClassification[]>('/api/classifications');

// --- Profiles ---
export function createProfile(input: ProfileInput) {
  return req<{
    profile: ParsedCustomerProfile;
    materialization: { inserted: number; updated: number; deactivated: number };
  }>(`/api/profiles`, { method: 'POST', body: JSON.stringify(input) });
}
export const listProfiles = () =>
  req<{ data: ParsedCustomerProfile[]; total: number }>('/api/profiles');
/**
 * Fetch the single profile attached to a workspace (or null if the
 * workspace hasn't been onboarded yet). Server enforces membership via
 * workspaceContext middleware; a non-member gets 404. Safe replacement
 * for the cross-tenant `listProfiles()` shape.
 */
export const getWorkspaceProfile = (slug: string) =>
  req<{ profile: ParsedCustomerProfile | null }>(
    `/api/w/${encodeURIComponent(slug)}/profile`
  );
/**
 * V112-3-9 (ADR-0099 §4): what a change to the workspace defaults would add or remove, per system. A dry run; any
 * member may call it. Saving stays updateProfile (owner / admin / editor).
 */
export const previewWorkspaceScope = (slug: string, change: Partial<Pick<ProfileInput,
  'geographies' | 'data_types' | 'industry_codes' | 'affected_persons' | 'decision_types' | 'deployer_type' | 'federal_flowdown'>>) =>
  req<ScopePreview>(`/api/w/${encodeURIComponent(slug)}/profile/scope-preview`, { method: 'POST', body: JSON.stringify(change) });
/** V112-3-10: what one system's save would add or remove (dry run, any member). Same body as the classification save. */
export const previewSystemScope = (slug: string, systemId: string, body: Parameters<typeof updateSystemClassification>[2]) =>
  req<SystemScopePreview>(`/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/scope-preview`, { method: 'POST', body: JSON.stringify(body) });
export function getProfile(id: string) {
  return req<{ profile: ParsedCustomerProfile; applicable_frameworks: Framework[] }>(
    `/api/profiles/${encodeURIComponent(id)}`
  );
}
/** V112-3-10: `scope_reason` (≤ 280) is stamped on the workspace defaults the save changes. */
export function updateProfile(id: string, patch: Partial<ProfileInput> & { scope_reason?: string }) {
  return req<{
    profile: ParsedCustomerProfile;
    materialization: { inserted: number; updated: number; deactivated: number };
  }>(`/api/profiles/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

// --- Assessments ---
// PR-Cluster-1-a (docs/ux-design-plan.md §10): server-side spread
// composes a `methodology: MethodologyPin` block onto every readiness
// response per §2.3 of uc-2-provenance-api-spec.md. `getAssessment`
// returns the widened type so the Dashboard's methodology-pin
// affordance can render without a follow-on `/api/methodology/versions`
// round-trip.
export const getAssessment = (profileId: string) =>
  req<AssessmentWithMethodology>(`/api/assessments/${encodeURIComponent(profileId)}`);
export const getScorecard = (profileId: string) =>
  req<Scorecard>(`/api/assessments/${encodeURIComponent(profileId)}/scorecard`);
export const getRoadmap = (profileId: string, limit = 20) =>
  req<RoadmapResult>(
    `/api/assessments/${encodeURIComponent(profileId)}/roadmap?limit=${limit}`
  );
export const getProgress = (profileId: string) =>
  req<ProgressSnapshot>(`/api/assessments/${encodeURIComponent(profileId)}/progress`);

// PR-Cluster-1-b-spa: workspace-scoped readiness history (§3.2
// as-shipped vs restated). Machine + SPA consumers target this URL
// family — enforces workspace scoping at the routing layer.
export type ReadinessHistoryWindow =
  | '4w'
  | '8w'
  | '12w'
  | '26w'
  | '90d'
  | '180d'
  | '1y';
export interface ReadinessHistoryPoint {
  snapshot_id: string;
  taken_at: string;
  as_shipped: { score: number; methodology_version: string | null };
  restated: { score: number | null; methodology_version: string };
}
export interface ReadinessHistoryResponse {
  profile_id: string;
  window: ReadinessHistoryWindow;
  points: ReadinessHistoryPoint[];
  methodology: {
    active_version: string;
    active_effective_at: string;
    formula_url: string;
  };
}
export function getReadinessHistory(
  slug: string,
  profileId: string,
  window: ReadinessHistoryWindow = '12w',
) {
  return req<ReadinessHistoryResponse>(
    `/api/w/${encodeURIComponent(slug)}/readiness/${encodeURIComponent(profileId)}/history?window=${encodeURIComponent(window)}`,
  );
}
export const exportAssessmentJson = (profileId: string) =>
  req<{
    profile: ParsedCustomerProfile;
    assessment: ReadinessAssessment;
    roadmap: RoadmapResult;
    outcomes: ReadinessOutcomeScore[];
    /**
     * §2.4: every export payload carries the methodology pin so
     * downstream consumers (PDF, XLSX, downstream JSON archives)
     * can stamp the version. See docs/uc-2-provenance-api-spec.md.
     */
    methodology: {
      version: string;
      effective_at: string;
      formula_url: string;
    };
  }>(`/api/assessments/${encodeURIComponent(profileId)}/export`);
export function updateItemStatus(
  profileId: string,
  itemId: string,
  body: {
    status: CustomerRegisterItemStatus;
    notes?: string;
    changed_by?: string;
    blocked_reason?: string;
    na_rationale?: string;
  }
) {
  return req<CustomerRegisterItem>(
    `/api/assessments/${encodeURIComponent(profileId)}/items/${encodeURIComponent(itemId)}/status`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}

// XLSX export is a binary blob, not JSON.
export async function exportAssessmentXlsx(profileId: string): Promise<Blob> {
  const res = await fetch(
    `${BASE}/api/assessments/${encodeURIComponent(profileId)}/export.xlsx`
  );
  if (!res.ok) throw new ApiError(res.status, res.statusText);
  return res.blob();
}

// --- Systems ---
export const listSystems = (profileId: string) =>
  req<{ data: AISystemWithClassifications[]; total: number }>(
    `/api/systems?profile_id=${encodeURIComponent(profileId)}`
  );
export function createSystem(profileId: string, input: AISystemInventoryItem) {
  return req<AISystemWithClassifications>('/api/systems', {
    method: 'POST',
    body: JSON.stringify({ profile_id: profileId, ...input }),
  });
}
export function addClassification(
  systemId: string,
  body: {
    classification: ServiceClassificationType;
    is_primary?: boolean;
    maturity_level?: MaturityLevel;
  }
) {
  return req<unknown>(`/api/systems/${encodeURIComponent(systemId)}/classifications`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
export function reclassify(
  classificationRowId: string,
  body: {
    classification: ServiceClassificationType;
    is_primary?: boolean;
    maturity_level?: MaturityLevel;
  }
) {
  return req<unknown>(
    `/api/systems/classifications/${encodeURIComponent(classificationRowId)}/reclassify`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}
export function setMaturity(classificationRowId: string, maturity_level: MaturityLevel) {
  return req<{ ok: boolean }>(
    `/api/systems/classifications/${encodeURIComponent(classificationRowId)}/maturity`,
    { method: 'PATCH', body: JSON.stringify({ maturity_level }) }
  );
}

// --- Controls ---
export const listControls = (params: { family?: string; framework?: string; domain?: string } = {}) => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) q.append(k, v);
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return req<{ data: ParsedControl[]; total: number }>(`/api/controls${suffix}`);
};

// --- Outcomes ---
export const listOutcomes = () =>
  req<{ data: ParsedReadinessOutcome[]; total: number }>('/api/outcomes');
export const getOutcomeScores = (profileId: string) =>
  req<{ profile_id: string; outcomes: ReadinessOutcomeScore[] }>(
    `/api/outcomes/scores/${encodeURIComponent(profileId)}`
  );

// --- Editorial queue (proposed_changes) ---
export type ProposedChangeListParams = {
  status?: ProposedChangeStatus | 'all';
  target_type?: ProposedChangeTarget;
  source_kind?: ProposedChangeSourceKind;
  severity?: ProposedChangeSeverity;
  domain?: string;
  risk_vector?: string;
  breaching_sla?: boolean;
  /** V112-2-10: only proposals flagged as a possible duplicate. */
  possible_duplicate?: boolean;
  order?: 'created_desc' | 'sla_priority';
  /** One program (govramp, fedramp, cmmc, …). */
  program?: ProposalProgramKey;
  /** One content batch: an `impact.batch_id`, or a `rows:<entry>` group. */
  batch?: string;
  limit?: number;
};
export function listProposedChanges(params: ProposedChangeListParams = {}) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === false) continue;
    // The route defaults status to 'pending' when unset, so 'all' means
    // 'don't filter' — omit the param entirely.
    if (k === 'status' && v === 'all') continue;
    q.append(k, String(v));
  }
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return req<{ proposed_changes: ProposedChange[] }>(`/api/proposed-changes${suffix}`);
}
export function getProposedChange(changeId: string) {
  return req<{ change: ProposedChange; parsed: ParsedProposedChange }>(
    `/api/proposed-changes/${encodeURIComponent(changeId)}`
  );
}
export type QueueSummary = {
  pending_total: number;
  pending_critical: number;
  pending_elevated: number;
  pending_routine: number;
  breaching_sla: number;
  seed_awaiting_ratification: number;
  /** Pending rows per program (Program filter). */
  by_program?: Partial<Record<ProposalProgramKey, number>>;
};
export function getQueueSummary() {
  return req<QueueSummary>('/api/proposed-changes/summary');
}
// v10.3: content batches in approval order (GET /api/proposed-changes/batches).
export function listContentBatches() {
  return req<{ batches: ContentBatchRow[]; total_pending: number }>('/api/proposed-changes/batches');
}
export function approveContentBatch(batchId: string, body: { review_notes?: string } = {}) {
  return req<{ applied: number; failed: unknown[]; deferred: unknown[]; remaining: number }>(
    `/api/proposed-changes/batch/${encodeURIComponent(batchId)}/approve`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}
// v10.4 Track G (ADR-0076): needs-authoring items — failed legislation-to-rule drafts.
export type AuthoringCounts = { open: number; blocked_vocabulary: number; unresolved: number };
export function listAuthoringItems() {
  return req<{ items: RuleAuthoringItemView[]; counts: AuthoringCounts }>('/api/proposed-changes/authoring-items');
}
export function getAuthoringSummary() {
  return req<AuthoringCounts>('/api/proposed-changes/authoring-items/summary');
}
/** Endpoint segment per action (POST /api/proposed-changes/authoring-items/:id/<segment>). */
export const AUTHORING_ACTION_PATH: Record<RuleAuthoringAction, string> = {
  retry: 'retry',
  draft_anyway: 'draft-anyway',
  add_statute_text: 'statute-text',
  link_existing: 'link-existing',
  author_rule: 'author',
  request_vocabulary: 'request-vocabulary',
  // V104-G-12: filed with proposeItemTerm (its body is a term, not a note).
  propose_term: 'terms',
  mark_not_applicable: 'not-applicable',
};
export function actOnAuthoringItem(
  itemId: string,
  action: RuleAuthoringAction,
  body: { text?: string; note?: string; concept?: string; linked_rule_ids?: string[] } = {}
) {
  // 202 { started: true } when the Workflow took the re-run; 200 { started: false, outcome } when it ran inline.
  return req<{ item: RuleAuthoringItemView | null; started?: boolean; outcome?: { outcome: 'proposed' | 'escalated' | 'skipped' } }>(
    `/api/proposed-changes/authoring-items/${encodeURIComponent(itemId)}/${AUTHORING_ACTION_PATH[action]}`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}
/** V104-G-12 / ADR-0077: a term proposal from the authoring form or a vocabulary-gap item. */
export interface ItemTermRequest {
  kind: 'role' | 'tier';
  term_key: string;
  label: string;
  canonical: string;
  rank?: number | null;
  citation: string;
  source_url: string;
  definition?: string;
  quote?: string;
}
/** 201 { change_id, term }; 422 { error: 'invalid_term', errors }. The jurisdiction is the item's law's. */
export function proposeItemTerm(itemId: string, body: ItemTermRequest) {
  return req<{ change_id: string; term: JurisdictionTerm; item: RuleAuthoringItemView | null }>(
    `/api/proposed-changes/authoring-items/${encodeURIComponent(itemId)}/terms`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}
// V104-G-10: the rule-authoring form.
export type AuthoringContextResponse = { item: RuleAuthoringItemView } & AuthoringContext;
export function getAuthoringContext(itemId: string) {
  return req<AuthoringContextResponse>(`/api/proposed-changes/authoring-items/${encodeURIComponent(itemId)}/authoring-context`);
}
export type AuthoredRuleRequest = Omit<AuthoredRuleInput, 'predicates'> & { predicates: Predicate; finish?: boolean };
/** 201 on success; 422 bodies carry `error` (no_statute_text / ungrounded / invalid_rule), `errors`, `unmatched` or `issues`. */
export function fileAuthoredRule(itemId: string, body: AuthoredRuleRequest) {
  return req<{ change_id: string; filed: AuthoredRuleRecord[]; resolved: boolean; item: RuleAuthoringItemView | null }>(
    `/api/proposed-changes/authoring-items/${encodeURIComponent(itemId)}/authored-rule`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}
export type ReviewDecision = {
  reviewer: string;
  review_notes?: string;
  /** Security review M1. */
  confirm_self_supplied_source?: boolean;
  /** V112-4-6/4-7 (ADR-0098): the editor's Control impact on a law / court decision, and the duty indices kept ticked. */
  control_impact_outcome?: ImpactOutcome;
  control_impact_kept?: number[];
};
/**
 * V112-4-7 (BL-45, ADR-0098): a law's `impact.control_impact` as the queue reads it. The analysis fields, plus what the
 * approve / apply recorded (the editor's choice, overrides, and the follow-on proposals filed or skipped).
 */
export type ControlImpactView = ControlImpact & {
  chosen?: ImpactOutcome;
  kept?: number[];
  override?: { suggested: ImpactOutcome | null; chosen: ImpactOutcome };
  filed?: FiledFollowOn[];
  skipped?: SkippedFollowOn[];
};
export type ControlImpactFiling = { filed: FiledFollowOn[]; skipped: SkippedFollowOn[] };
/** Re-run analysis (editor / admin): 200 { control_impact }; 404 not_analysable when the row is not a pending law. */
export function rerunControlImpact(changeId: string) {
  return req<{ control_impact: ControlImpactView }>(
    `/api/proposed-changes/${encodeURIComponent(changeId)}/control-impact`,
    { method: 'POST', body: '{}' }
  );
}
/**
 * V112-4-7: correct a pending law's jurisdiction (editor / admin); the analysis re-runs. 422 invalid_jurisdiction,
 * 404 not_correctable.
 */
export function correctProposalJurisdiction(changeId: string, jurisdiction: string) {
  return req<{ jurisdiction: string; control_impact: ControlImpactView | null }>(
    `/api/proposed-changes/${encodeURIComponent(changeId)}/jurisdiction`,
    { method: 'PATCH', body: JSON.stringify({ jurisdiction }) }
  );
}
/**
 * V112-4-7 (D-BL45-3): add the statute's own link to a pending law (editor / admin, https only); the analysis re-runs
 * and reads the primary text. 422 invalid_url, 404 not_correctable.
 */
export function setProposalSourceUrl(changeId: string, url: string) {
  return req<{ source_url: string; primary: boolean; control_impact: ControlImpactView | null }>(
    `/api/proposed-changes/${encodeURIComponent(changeId)}/source-url`,
    { method: 'PATCH', body: JSON.stringify({ url }) }
  );
}
export type ControlImpactStats = { decided: number; matched: number; overridden: number; no_suggestion: number };
/** How often editors keep the suggested Control impact (owner stat). */
export function getControlImpactStats() {
  return req<ControlImpactStats>('/api/proposed-changes/control-impact-stats');
}
export function approveProposedChange(changeId: string, decision: ReviewDecision) {
  return req<ProposedChange>(
    `/api/proposed-changes/${encodeURIComponent(changeId)}/approve`,
    { method: 'POST', body: JSON.stringify(decision) }
  );
}
/** V111-7-6b: a matched pending create becomes an update of the existing item (stays pending). */
export function retargetProposedChange(changeId: string) {
  return req<ProposedChange>(
    `/api/proposed-changes/${encodeURIComponent(changeId)}/retarget`,
    { method: 'POST', body: '{}' }
  );
}
/** V111-7-6b undo: back to creating a new item. */
export function untargetProposedChange(changeId: string) {
  return req<ProposedChange>(
    `/api/proposed-changes/${encodeURIComponent(changeId)}/untarget`,
    { method: 'POST', body: '{}' }
  );
}
export function rejectProposedChange(changeId: string, decision: ReviewDecision) {
  return req<ProposedChange>(
    `/api/proposed-changes/${encodeURIComponent(changeId)}/reject`,
    { method: 'POST', body: JSON.stringify(decision) }
  );
}
export function ratifyProposedChange(changeId: string, body: { review_notes?: string } = {}) {
  return req<ProposedChange>(
    `/api/proposed-changes/${encodeURIComponent(changeId)}/ratify`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}
export function applyProposedChange(
  changeId: string,
  body: { enqueue_propagation?: boolean; tenants?: string[] } = {}
) {
  // V112-4-7: a law / court decision also returns the follow-on proposals its Control impact filed (null otherwise).
  return req<{ change: ProposedChange; applied: unknown; propagation: unknown; control_impact?: ControlImpactFiling | null }>(
    `/api/proposed-changes/${encodeURIComponent(changeId)}/apply`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}
// Body shape for POST /api/proposed-changes (admin-initiated proposals).
// Used by the inline "Deprecate source" action on Queue rows: raise a
// research_source deprecate proposal, then immediately approve+apply it
// so the audit trail lives in proposed_changes but the reviewer doesn't
// have to context-switch to a source-review page.
export type CreateProposedChangeBody = {
  target_type: ProposedChangeTarget;
  target_id?: string;
  change_type: 'create' | 'update' | 'deprecate';
  proposed_state: Record<string, unknown>;
  ai_rationale: string;
  ai_confidence?: number;
  // No source_kind: the server records provenance from the session (admin_edit).
  severity?: ProposedChangeSeverity;
  finding_id?: string;
};
export function createProposedChange(body: CreateProposedChangeBody) {
  return req<ProposedChange>('/api/proposed-changes', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// --- Research sources (editorial-facing list at /app/sources) ---
export type ResearchSourceListRow = ResearchSource & {
  run_count: number;
  failure_count: number;
};
export type ListResearchSourcesParams = {
  kind?: ResearchSourceKind;
  is_active?: boolean;
};
export function listResearchSources(params: ListResearchSourcesParams = {}) {
  const q = new URLSearchParams();
  if (params.kind) q.append('kind', params.kind);
  if (params.is_active !== undefined) q.append('is_active', String(params.is_active));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return req<{ sources: ResearchSourceListRow[] }>(`/api/research/sources${suffix}`);
}
// --- Workspace register overrides (Phase 2b of readiness-scoring) ---
// Per-workspace required-maturity override on a customer_register_items row.
// Layer 1 of the effective-required-level stack (workspace override wins over
// editorial framework target wins over derived-from-evidence-types).
export interface RequiredMaturityBody {
  required_maturity_override: MaturityLevel | null;
  reason: string | null;
}
export function setRequiredMaturity(
  slug: string,
  itemId: string,
  body: RequiredMaturityBody
) {
  return req<{ status: 'ok' }>(
    `/api/w/${encodeURIComponent(slug)}/register/${encodeURIComponent(itemId)}/required-maturity`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}
// Items in this workspace whose required-maturity target hasn't been
// reviewed yet — powers the "New risks — review your target" surface.
export interface NeedsTargetReviewRow {
  item_id: string;
  title: string;
  category: RiskCategory;
  severity: Severity;
  applicable_frameworks: string[];
  expected_evidence_types: string[];
  derived_required: MaturityLevel;
}
export function getNeedsTargetReview(slug: string) {
  return req<{ rows: NeedsTargetReviewRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/register/needs-target-review`
  );
}

// --- Control evidence (V89-6-1) ---
export interface ControlEvidenceItem {
  check_id: string;
  control_id: string;
  status: string;
  observed_at: string;
  evidence_hash_prefix: string | null;
  connector_kind: string;
  claim_text: string | null;
  conformance_score: number | null;
}
export interface ControlEvidenceResponse {
  control_ref: string | null;
  evidence: ControlEvidenceItem[];
  has_evidence: boolean;
  last_checked: string | null;
}
export function getControlEvidence(slug: string, itemId: string) {
  return req<ControlEvidenceResponse>(
    `/api/w/${encodeURIComponent(slug)}/register/${encodeURIComponent(itemId)}/control-evidence`
  );
}

// --- Unified evidence (V89-5-1) ---
export interface UnifiedEvidenceItem {
  id: string;
  source_type: 'manual' | 'connector';
  title: string;
  kind: string;
  timestamp: string;
  file_name: string | null;
  status: string | null;
  connector_kind: string | null;
  evidence_hash: string | null;
  control_id: string | null;
}

export interface UnifiedEvidenceResponse {
  evidence: UnifiedEvidenceItem[];
  total: number;
  has_more: boolean;
}

export function getUnifiedEvidence(
  slug: string,
  opts?: { source?: 'manual' | 'connector'; limit?: number; offset?: number },
) {
  const params = new URLSearchParams();
  if (opts?.source) params.set('source', opts.source);
  if (opts?.limit) params.set('limit', String(opts.limit));
  if (opts?.offset) params.set('offset', String(opts.offset));
  const qs = params.toString();
  return req<UnifiedEvidenceResponse>(
    `/api/w/${encodeURIComponent(slug)}/evidence/unified${qs ? `?${qs}` : ''}`,
  );
}

// --- Register assignment (ADR-0009 / PR-P1-3a+b) ---
// ADR-0009 bullet #5 "My work" query surface — the caller's open
// (todo | in_progress | blocked | review_needed) assigned items, ordered by priority.
export function getMyWork(slug: string) {
  return req<{ items: MyWorkItem[]; total: number; summary: MyWorkSummary }>(
    `/api/w/${encodeURIComponent(slug)}/register/mine`
  );
}
// Bulk assignee projection powering Register.tsx's Assignee column +
// "assignee=me" filter — one request instead of N per-row GET /state
// calls.
export interface RegisterAssigneeRow {
  register_item_id: string;
  assigned_to: { user_id: string; email: string; display_name: string } | null;
}
export function listRegisterAssignees(slug: string) {
  return req<{ assignees: RegisterAssigneeRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/register/assignees`
  );
}

// --- Workspace accreditation targets (Phase 4 of readiness-scoring) ---
// One row per (workspace, framework) pair. Powers the countdown widget
// on framework score cards + the Compliance Settings surface.
export interface AccreditationTarget {
  target_id: string;
  workspace_id: string;
  framework_id: string;
  target_date: string; // YYYY-MM-DD
  mode: 'strict' | 'soft';
  notes: string | null;
  set_by_user_id: string | null;
  set_at: string;
  updated_at: string;
}
export function listComplianceTargets(slug: string) {
  return req<{ targets: AccreditationTarget[] }>(
    `/api/w/${encodeURIComponent(slug)}/settings/compliance/targets`
  );
}
export interface ComplianceTargetBody {
  target_date: string;
  mode?: 'strict' | 'soft';
  notes?: string | null;
}
export function upsertComplianceTarget(
  slug: string,
  frameworkId: string,
  body: ComplianceTargetBody
) {
  return req<{ target: AccreditationTarget }>(
    `/api/w/${encodeURIComponent(slug)}/settings/compliance/targets/${encodeURIComponent(frameworkId)}`,
    { method: 'PUT', body: JSON.stringify(body) }
  );
}
export function deleteComplianceTarget(slug: string, frameworkId: string) {
  return req<{ status: 'ok' }>(
    `/api/w/${encodeURIComponent(slug)}/settings/compliance/targets/${encodeURIComponent(frameworkId)}`,
    { method: 'DELETE' }
  );
}

// Force a scan of one source ("run now"). Editor/admin only server-side.
// Returns 202 with the workflow instance id when accepted.
export function triggerSourceRun(sourceId: string) {
  return req<{ workflow_run_id: string; started_at: string; workflow_kind: string }>(
    `/api/research/sources/${encodeURIComponent(sourceId)}/trigger`,
    { method: 'POST', body: JSON.stringify({}) }
  );
}

// ---- UC-7 Audit trail (PR-Cluster-7-a) --------------------------------
// See docs/uc-2-provenance-api-spec.md §7.1 + §7.3 and ADR-0014. The
// backend routes shipped in Phase D; this is the client wrapper for
// the read surface.

/** Wire shape returned by GET /api/w/:slug/audit. Mirrors
 *  AuditEventRow from @aigovops/core but re-declared here so the web
 *  bundle doesn't drag in the audit-event-service transitive deps
 *  just for a type. */
export interface AuditEventRow {
  event_id: string;
  event_type: string;
  workspace_id: string;
  actor_type: 'user' | 'system' | 'assessor';
  actor_id: string;
  actor_ip_hash: string | null;
  actor_ua: string | null;
  target_kind: string;
  target_id: string;
  before_json: string | null;
  after_json: string | null;
  reason_text: string | null;
  methodology_version: string;
  chained_from: string | null;
  event_hash: string;
  event_signature: string;
  key_fingerprint: string;
  occurred_at: string;
  archived_at: string | null;
}

export interface AuditListOptions {
  since?: string; // ISO-8601
  until?: string; // ISO-8601
  eventType?: string | string[];
  targetKind?: string;
  targetId?: string;
  limit?: number; // capped at 500 server-side
}

export function listAuditEvents(slug: string, opts: AuditListOptions = {}) {
  const params = new URLSearchParams();
  if (opts.since) params.set('since', opts.since);
  if (opts.until) params.set('until', opts.until);
  if (opts.eventType) {
    const et = Array.isArray(opts.eventType) ? opts.eventType.join(',') : opts.eventType;
    params.set('event_type', et);
  }
  if (opts.targetKind) params.set('target_kind', opts.targetKind);
  if (opts.targetId) params.set('target_id', opts.targetId);
  if (opts.limit) params.set('limit', String(opts.limit));
  const qs = params.toString();
  return req<{ events: AuditEventRow[]; count: number }>(
    `/api/w/${encodeURIComponent(slug)}/audit${qs ? `?${qs}` : ''}`
  );
}

export function getAuditEvent(slug: string, eventId: string) {
  return req<{ event: AuditEventRow }>(
    `/api/w/${encodeURIComponent(slug)}/audit/events/${encodeURIComponent(eventId)}`
  );
}

export interface ChainVerifyReport {
  verified: number;
  total: number;
  gaps: Array<{ event_id: string; reason: string }>;
  signing_key_fingerprint: string | null;
  window: { since: string | null; until: string | null; limit: number };
}

export function verifyAuditChain(slug: string, opts: { since?: string; until?: string; limit?: number } = {}) {
  const params = new URLSearchParams();
  if (opts.since) params.set('since', opts.since);
  if (opts.until) params.set('until', opts.until);
  if (opts.limit) params.set('limit', String(opts.limit));
  const qs = params.toString();
  return req<ChainVerifyReport>(
    `/api/w/${encodeURIComponent(slug)}/audit/chain-verify${qs ? `?${qs}` : ''}`
  );
}

// ---- UC-7 §7.5 Webhook admin (PR-Cluster-7-b) -------------------------
// Backend routes live in packages/cloudflare/src/routes/webhook-endpoints.ts
// (owner/admin gated). Every HMAC secret returned in a create + rotate
// response is ONE-TIME — never fetched back from the server; if the
// user misses the copy affordance they rotate.

export interface WebhookEndpointSummary {
  endpoint_id: string;
  url: string;
  label: string;
  prefix: string;
  event_filter: string[];
  status: WebhookStatus;
  binding_type: WebhookBindingType;
  owned_by: string | null;
  created_by: string;
  created_at: string;
  last_delivery_at: string | null;
  last_delivery_status: WebhookDeliveryResult | null;
  rotation_pending_at: string | null;
  rotation_overlap_until: string | null;
}

export interface WebhookDeliveryRow {
  delivery_id: string;
  endpoint_id: string;
  event_id: string;
  workspace_id: string;
  attempted_at: string;
  finished_at: string | null;
  request_signature: string;
  request_timestamp: string;
  response_status: number | null;
  response_ms: number | null;
  result: WebhookDeliveryResult;
  attempt_number: number;
  is_replay: number;
  idempotency_key: string;
}

export function listWebhookEndpoints(slug: string) {
  return req<{ endpoints: WebhookEndpointSummary[] }>(
    `/api/w/${encodeURIComponent(slug)}/settings/webhooks`,
  );
}

export interface CreateWebhookInput {
  url: string;
  label: string;
  event_filter?: string[];
  binding_type?: WebhookBindingType;
}

export interface CreatedWebhookResponse {
  endpoint_id: string;
  url: string;
  label: string;
  prefix: string;
  scopes?: unknown;
  secret_plaintext: string; // ONE-TIME reveal
  status: WebhookStatus;
  binding_type: WebhookBindingType;
  event_filter: string[];
  created_at: string;
}

export function createWebhookEndpoint(slug: string, body: CreateWebhookInput) {
  return req<CreatedWebhookResponse>(
    `/api/w/${encodeURIComponent(slug)}/settings/webhooks`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export type WebhookOverlapHours = 0 | 1 | 24 | 168 | 720;

export function rotateWebhookSecret(
  slug: string,
  endpointId: string,
  overlapHours: WebhookOverlapHours,
) {
  return req<{ secret_plaintext: string; overlap_until: string | null }>(
    `/api/w/${encodeURIComponent(slug)}/settings/webhooks/${encodeURIComponent(endpointId)}/rotate`,
    { method: 'POST', body: JSON.stringify({ overlap_hours: overlapHours }) },
  );
}

export function setWebhookStatus(
  slug: string,
  endpointId: string,
  op: 'pause' | 'resume' | 'disable',
) {
  return req<null>(
    `/api/w/${encodeURIComponent(slug)}/settings/webhooks/${encodeURIComponent(endpointId)}/${op}`,
    { method: 'POST' },
  );
}

export function listWebhookDeliveries(slug: string, endpointId: string, limit = 100) {
  return req<{ deliveries: WebhookDeliveryRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/settings/webhooks/${encodeURIComponent(endpointId)}/deliveries?limit=${limit}`,
  );
}

// PR-Cluster-7-e (P-UC7-4): fire a synthetic test event at one
// endpoint. Emits a real webhook.test audit event server-side +
// dispatches once, bypassing the endpoint's event_filter so a
// subscriber can prove their integration works without opting in
// to any event first.
export function sendWebhookTestEvent(slug: string, endpointId: string) {
  return req<{ delivery_id: string }>(
    `/api/w/${encodeURIComponent(slug)}/settings/webhooks/${encodeURIComponent(endpointId)}/test`,
    { method: 'POST' },
  );
}

// PR-Cluster-7-g (P-UC7-8 R): re-dispatch a specific prior
// delivery to its endpoint. Owner/admin gated. Emits a
// webhook.replayed audit event capturing WHO replayed; the
// dispatched payload is the ORIGINAL event unchanged so payload
// identity (event_id + event_hash) holds.
export function replayWebhookDelivery(slug: string, deliveryId: string) {
  return req<{ delivery_id: string }>(
    `/api/w/${encodeURIComponent(slug)}/settings/webhooks/deliveries/${encodeURIComponent(deliveryId)}/replay`,
    { method: 'POST' },
  );
}

// ---- §7.9 Assessor engagements (PR-Cluster-6-a) -----------------------
// Priya-facing (workspace-owner) surface. Backend at
// packages/cloudflare/src/routes/assessors.ts. Jordan's cross-workspace
// inbox at /api/assessor lands with a later PR that requires the
// assessor session middleware.

export interface AssessorEngagementRow {
  engagement_id: string;
  workspace_id: string;
  snapshot_id: string;
  assessor_user_id: string;
  assessor_firm: string;
  invited_by: string;
  invited_at: string;
  engagement_start: string;
  engagement_end: string;
  archive_window_days: number;
  revoked_at: string | null;
  revoked_by: string | null;
  revoked_reason: string | null;
  event_id_started: string;
  event_id_ended: string | null;
}

export function listAssessorEngagements(slug: string) {
  return req<{ engagements: AssessorEngagementRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/assessors/engagements`,
  );
}

// PR-Cluster-6-l: fetch a single engagement's metadata for the
// workpaper page. Any workspace member may read it; the data is
// workspace-scoped by definition.
export function getAssessorEngagement(slug: string, engagementId: string) {
  return req<{ engagement: AssessorEngagementRow }>(
    `/api/w/${encodeURIComponent(slug)}/assessors/engagements/${encodeURIComponent(engagementId)}`,
  );
}

// PR-Cluster-6-m: active methodology for the workpaper PDF footer
// pin. Any workspace member may read.
export interface ActiveMethodology {
  version: string;
  effective_at: string;
  formula_hash: string;
  approved_by: string;
  approved_at: string;
}
export function getActiveMethodology(slug: string) {
  return req<ActiveMethodology>(
    `/api/w/${encodeURIComponent(slug)}/methodology/active`,
  );
}

// PR-Cluster-6-k: Jordan's cross-workspace inbox. Assessor session
// only (server-side gate — filters by the caller's user_id).
// Response enriched with workspace slug/name + per-engagement
// counters so the /assessor page renders per-row without follow-ups.
export interface AssessorInboxRow extends AssessorEngagementRow {
  workspace_slug: string;
  workspace_name: string;
  active_annotation_count: number;
  active_dispute_count: number;
  open_qa_thread_count: number;
}
export function listAssessorInbox() {
  return req<{ engagements: AssessorInboxRow[] }>('/api/assessor/engagements');
}

export interface InviteAssessorInput {
  assessor_user_id: string;
  assessor_firm: string;
  snapshot_id: string;
  engagement_start: string; // ISO date
  engagement_end: string; // ISO date
  archive_window_days?: EngagementArchiveWindow;
}

export function inviteAssessor(slug: string, body: InviteAssessorInput) {
  return req<{ engagement: AssessorEngagementRow }>(
    `/api/w/${encodeURIComponent(slug)}/assessors/invite`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export function revokeAssessorEngagement(
  slug: string,
  engagementId: string,
  reason: string,
) {
  return req<null>(
    `/api/w/${encodeURIComponent(slug)}/assessors/engagements/${encodeURIComponent(engagementId)}/revoke`,
    { method: 'POST', body: JSON.stringify({ reason }) },
  );
}

export function extendAssessorEngagement(
  slug: string,
  engagementId: string,
  body: { new_engagement_end: string; reason: string },
) {
  return req<null>(
    `/api/w/${encodeURIComponent(slug)}/assessors/engagements/${encodeURIComponent(engagementId)}/extend`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

// ---- §4.3 Workspace API tokens (PR-Cluster-7-d) -----------------------
// Marcus persona's pull-integration story: tokens for Datadog / Grafana /
// GRC-of-record to hit the read API. Backend at
// packages/cloudflare/src/routes/api-tokens.ts (owner/admin gated).
// v1 scope is `read` only per §6.4.

export interface ApiTokenSummary {
  token_id: string;
  label: string;
  prefix: string;
  scopes: string[];
  role_at_creation: ApiTokenRoleAtCreation;
  created_by: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
  revoked_reason: string | null;
}

export interface CreatedApiTokenResponse {
  token_id: string;
  label: string;
  prefix: string;
  scopes: ApiTokenScope[];
  /** V108-1-1: the purpose the token was minted for, when minted by purpose. */
  purpose?: string | null;
  created_at: string;
  plaintext: string; // ONE-TIME reveal
}

/** V108-1-1 (ADR-0087 §2): what a token is for. The purpose fixes its scopes; the UI never offers raw scopes. */
export interface ApiTokenPurpose {
  key: string;
  label: string;
  scopes: string[];
  hint: string;
  /** V108-8-7: how the purpose takes a system. `optional` (setup_cli): without one the token can only register a new system. */
  system?: ApiTokenPurposeSystemMode;
  /** Kept for older servers/clients: true when the purpose takes a system (required or optional). */
  needs_system: boolean;
}
export type ApiTokenPurposeSystemMode = 'required' | 'optional' | 'none';

/** A purpose's system mode; a server without `system` sent only `needs_system`. */
export function purposeSystemMode(p: Pick<ApiTokenPurpose, 'system' | 'needs_system'>): ApiTokenPurposeSystemMode {
  return p.system ?? (p.needs_system ? 'required' : 'none');
}

export function listApiTokenPurposes(slug: string) {
  return req<{ purposes: ApiTokenPurpose[] }>(`/api/w/${encodeURIComponent(slug)}/settings/api/purposes`);
}

export function listApiTokens(slug: string) {
  return req<{ tokens: ApiTokenSummary[] }>(
    `/api/w/${encodeURIComponent(slug)}/settings/api`,
  );
}

/** The CI verification runner's token (ADR-0074 §3, V105-7-2 critic B1): download bundles, post results, nothing else. */
export const VERIFICATION_TOKEN_SCOPES: ApiTokenScope[] = ['verification:read', 'verification:write'];

export function createApiToken(
  slug: string,
  body: { label: string; purpose?: string; system_id?: string },
) {
  return req<CreatedApiTokenResponse>(
    `/api/w/${encodeURIComponent(slug)}/settings/api`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export function revokeApiToken(slug: string, tokenId: string, reason: string) {
  return req<null>(
    `/api/w/${encodeURIComponent(slug)}/settings/api/${encodeURIComponent(tokenId)}?reason=${encodeURIComponent(reason)}`,
    { method: 'DELETE' },
  );
}

// ---- §7.9 Assessor Q&A threads (PR-Cluster-6-b) -----------------------
// Backend at packages/cloudflare/src/routes/qa.ts. Threads open ONLY
// from assessor sessions (server-side gate); workspace members reply
// + mark addressed; owner or assessor resolves. Every message emits
// its own chained audit event.

export interface QaThreadRow {
  thread_id: string;
  workspace_id: string;
  engagement_id: string;
  target_kind: QaTargetKind;
  target_id: string;
  opened_by: string;
  opened_at: string;
  status: QaThreadStatus;
  addressed_at: string | null;
  addressed_by: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  resolved_reason: string | null;
  event_id_opened: string;
}

export interface QaMessageRow {
  message_id: string;
  thread_id: string;
  author_user_id: string;
  author_role: QaAuthorRole;
  posted_at: string;
  body: string;
  evidence_artifact_id: string | null;
  event_id: string;
}

export function listQaThreads(
  slug: string,
  opts: { engagement_id?: string; target_kind?: QaTargetKind; target_id?: string; status?: QaThreadStatus } = {},
) {
  const params = new URLSearchParams();
  if (opts.engagement_id) params.set('engagement_id', opts.engagement_id);
  if (opts.target_kind) params.set('target_kind', opts.target_kind);
  if (opts.target_id) params.set('target_id', opts.target_id);
  if (opts.status) params.set('status', opts.status);
  const qs = params.toString();
  return req<{ threads: QaThreadRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/qa/threads${qs ? `?${qs}` : ''}`,
  );
}

export function getQaThread(slug: string, threadId: string) {
  return req<{ thread: QaThreadRow; messages: QaMessageRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/qa/threads/${encodeURIComponent(threadId)}`,
  );
}

export function postQaMessage(
  slug: string,
  threadId: string,
  body: { body: string; evidence_artifact_id?: string | null },
) {
  return req<{ message: QaMessageRow }>(
    `/api/w/${encodeURIComponent(slug)}/qa/threads/${encodeURIComponent(threadId)}/messages`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export function markQaThreadAddressed(slug: string, threadId: string) {
  return req<null>(
    `/api/w/${encodeURIComponent(slug)}/qa/threads/${encodeURIComponent(threadId)}/mark-addressed`,
    { method: 'POST' },
  );
}

export function resolveQaThread(slug: string, threadId: string, reason: string) {
  return req<null>(
    `/api/w/${encodeURIComponent(slug)}/qa/threads/${encodeURIComponent(threadId)}/resolve`,
    { method: 'POST', body: JSON.stringify({ reason }) },
  );
}

// ---- §7.9 Assessor evidence requests (PR-Cluster-6-c) -----------------
// Backend at packages/cloudflare/src/routes/evidence-requests.ts.
// Open path is assessor-only; provide is workspace-member; close is
// owner or assessor; escalate-to-poam is assessor-only.

export interface EvidenceRequestRow {
  request_id: string;
  workspace_id: string;
  engagement_id: string;
  register_item_id: string;
  opened_by: string;
  opened_at: string;
  status: EvidenceRequestStatus;
  severity: EvidenceRequestSeverity;
  routed_to_user_id: string | null;
  reason_text: string;
  provided_at: string | null;
  provided_by: string | null;
  provided_note: string | null;
  provided_evidence_artifact_id: string | null;
  closed_at: string | null;
  closed_by: string | null;
  closed_reason: string | null;
  poa_m_id: string | null;
  event_id_opened: string;
}

export function listEvidenceRequestsForItem(slug: string, registerItemId: string) {
  return req<{ requests: EvidenceRequestRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/evidence-requests?register_item_id=${encodeURIComponent(registerItemId)}`,
  );
}

// PR-Cluster-6-l: engagement-wide evidence-request list for the
// assessor workpaper. Backend at
// packages/cloudflare/src/routes/evidence-requests.ts (existing
// engagement_id filter branch).
export function listEvidenceRequestsForEngagement(slug: string, engagementId: string) {
  return req<{ requests: EvidenceRequestRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/evidence-requests?engagement_id=${encodeURIComponent(engagementId)}`,
  );
}

export function listMyEvidenceRequests(
  slug: string,
  status: EvidenceRequestStatus = 'open',
) {
  return req<{ requests: EvidenceRequestRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/evidence-requests?assignee=me&status=${encodeURIComponent(status)}`,
  );
}

export function provideEvidenceRequest(
  slug: string,
  requestId: string,
  body: { evidence_artifact_id: string; note?: string },
) {
  return req<null>(
    `/api/w/${encodeURIComponent(slug)}/evidence-requests/${encodeURIComponent(requestId)}/provide`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export function closeEvidenceRequest(slug: string, requestId: string, reason: string) {
  return req<null>(
    `/api/w/${encodeURIComponent(slug)}/evidence-requests/${encodeURIComponent(requestId)}/close`,
    { method: 'POST', body: JSON.stringify({ reason }) },
  );
}

// ---- §7.9 Assessor annotations (PR-Cluster-6-e) -----------------------
// The verdict layer the assessor lays on top of a control (accepted /
// needs_remediation / not_applicable). Layered separately from the
// customer's own status — a customer's register-item state doesn't
// flip when the assessor disagrees. Backend at
// packages/cloudflare/src/routes/assessor-annotations.ts.

export type AssessorAnnotationVerdict = 'accepted' | 'needs_remediation' | 'not_applicable';

export interface AssessorAnnotationRow {
  annotation_id: string;
  workspace_id: string;
  engagement_id: string;
  register_item_id: string;
  verdict: AssessorAnnotationVerdict;
  reason_text: string;
  annotated_by: string;
  annotated_at: string;
  superseded_at: string | null;
  superseded_by_annotation_id: string | null;
  event_id: string;
}

// PR-Cluster-6-h: workspace member's counter-view on an assessor's
// active verdict. Multiple dispute rows per annotation are allowed
// over time (audit trail) but at any moment at most one is active.
export interface AssessorAnnotationDispute {
  dispute_id: string;
  workspace_id: string;
  annotation_id: string;
  disputed_verdict: AssessorAnnotationVerdict;
  disputed_engagement_id: string;
  disputed_by_user_id: string;
  dispute_reason_text: string;
  filing_event_id: string;
  created_at: string;
  resolved_at: string | null;
  resolved_by_user_id: string | null;
  resolution: DisputeResolution | null;
  resolution_event_id: string | null;
  resolution_notes: string | null;
}

export function listAnnotationsForItem(slug: string, registerItemId: string) {
  return req<{
    annotations: AssessorAnnotationRow[];
    /** PR-Cluster-6-h: annotation_id → active dispute row, when any. */
    active_disputes: Record<string, AssessorAnnotationDispute>;
  }>(
    `/api/w/${encodeURIComponent(slug)}/assessor-annotations?register_item_id=${encodeURIComponent(registerItemId)}`,
  );
}

export function disputeAnnotation(
  slug: string,
  annotationId: string,
  body: { reason_text: string },
) {
  return req<{ dispute: AssessorAnnotationDispute }>(
    `/api/w/${encodeURIComponent(slug)}/assessor-annotations/${encodeURIComponent(annotationId)}/dispute`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export function withdrawAnnotationDispute(
  slug: string,
  disputeId: string,
  body: { notes?: string } = {},
) {
  return req<{ dispute: AssessorAnnotationDispute }>(
    `/api/w/${encodeURIComponent(slug)}/assessor-annotations/disputes/${encodeURIComponent(disputeId)}/withdraw`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

// PR-Cluster-6-j: assessor-side dispute acknowledgment. Assessor
// session only (server-side gate). Optional notes carry the
// assessor's rebuttal into the audit trail.
export function acknowledgeAnnotationDispute(
  slug: string,
  disputeId: string,
  body: { notes?: string } = {},
) {
  return req<{ dispute: AssessorAnnotationDispute }>(
    `/api/w/${encodeURIComponent(slug)}/assessor-annotations/disputes/${encodeURIComponent(disputeId)}/acknowledge`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

// PR-Cluster-6-i: workspace-wide dispute inbox. Enriched with the
// annotation + register-item context each row needs to render.
export interface AssessorAnnotationDisputeInboxRow extends AssessorAnnotationDispute {
  annotation_register_item_id: string;
  annotation_annotated_by: string;
  annotation_annotated_at: string;
  annotation_reason_text: string;
  register_item_title: string | null;
}
export function listWorkspaceDisputes(slug: string, opts: { limit?: number } = {}) {
  const qs = opts.limit ? `?limit=${opts.limit}` : '';
  return req<{ disputes: AssessorAnnotationDisputeInboxRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/assessor-annotations/disputes${qs}`,
  );
}

export function listAnnotationsByEngagement(slug: string, engagementId: string) {
  return req<{
    annotations: AssessorAnnotationRow[];
    active_disputes: Record<string, AssessorAnnotationDispute>;
  }>(
    `/api/w/${encodeURIComponent(slug)}/assessor-annotations?engagement_id=${encodeURIComponent(engagementId)}`,
  );
}

export function getAnnotationHistoryForItem(
  slug: string,
  registerItemId: string,
  engagementId: string,
) {
  return req<{ history: AssessorAnnotationRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/assessor-annotations/${encodeURIComponent(registerItemId)}/history?engagement_id=${encodeURIComponent(engagementId)}`,
  );
}

// ---- Questionnaires (P8 / M1-M5, PR-P2-2) -----------------------------
// Backend at packages/cloudflare/src/routes/questionnaires.ts.

export interface QuestionnaireEvidenceCounts {
  total: number;
  attested: number;
  declared: number;
  ai_inferred: number;
  no_match: number;
}

/** List-row shape: the stored row plus read-time-computed fields. */
export interface QuestionnaireListRow extends QuestionnaireRow {
  deadline_urgency: DeadlineUrgency | null;
  evidence_counts: QuestionnaireEvidenceCounts | null;
}

export function listQuestionnaires(slug: string) {
  return req<{ questionnaires: QuestionnaireListRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/questionnaires`,
  );
}

// ---- Register subscribers (V85-6-3 — Sell stage) ----------------------
// Backend at packages/cloudflare/src/routes/subscriptions.ts GET /subscribers.
// Supplier-side view: which buyer workspaces are actively subscribed to
// THIS workspace's register. Auth: workspaceContext() membership read.

export interface RegisterSubscriberRow {
  subscription_id: string;
  buyer_workspace_id: string;
  buyer_name: string;
  buyer_slug: string;
  mode: 'track_latest' | 'pinned';
  ack_sla_days: number;
  created_at: string;
}

export function listRegisterSubscribers(slug: string) {
  return req<{ subscribers: RegisterSubscriberRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/subscriptions/subscribers`,
  );
}

export interface QuestionnaireAssigneeInfo {
  user_id: string;
  email: string;
  display_name: string;
}

export interface QuestionnaireCitedItem {
  item_id: string;
  title: string;
}

export function getQuestionnaire(slug: string, id: string) {
  return req<{
    questionnaire: QuestionnaireRow;
    questions: QuestionnaireQuestionRow[];
    answers: QuestionnaireAnswerRow[];
    users: QuestionnaireAssigneeInfo[];
    citedItems: QuestionnaireCitedItem[];
    requireApprovalRationale: boolean;
  }>(`/api/w/${encodeURIComponent(slug)}/questionnaires/${encodeURIComponent(id)}`);
}

export function createQuestionnairePaste(
  slug: string,
  body: { title: string; source_text: string; due_date?: string },
) {
  return req<{ questionnaire: QuestionnaireRow }>(
    `/api/w/${encodeURIComponent(slug)}/questionnaires`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export async function uploadQuestionnaire(
  slug: string,
  input: { title: string; file: File; due_date?: string },
): Promise<QuestionnaireRow> {
  const form = new FormData();
  form.set('title', input.title);
  if (input.due_date) form.set('due_date', input.due_date);
  form.set('file', input.file);
  // Bypass req() — it always sets Content-Type: application/json, which
  // would strip multipart's boundary. CSRF header is still installed
  // globally by installCsrfFetchInterceptor() (see req()'s comment).
  const res = await fetch(`${BASE}/api/w/${encodeURIComponent(slug)}/questionnaires/upload`, {
    method: 'POST',
    body: form,
  });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: string; detail?: string } | null;
      if (body?.error) message = body.detail ? `${body.error} — ${body.detail}` : body.error;
    } catch {
      /* swallow */
    }
    throw new ApiError(res.status, message);
  }
  return res.json() as Promise<QuestionnaireRow>;
}

export interface XlsxMappingProposal {
  question_col: string | null;
  response_col: string | null;
  section_col: string | null;
  evidence_col: string | null;
  confidence: number;
  rationale?: string;
}

export interface XlsxSample {
  headers: string[];
  sampleRows: Array<Record<string, string>>;
}

export function getMappingProposal(slug: string, id: string) {
  return req<{ proposal: XlsxMappingProposal; sample: XlsxSample }>(
    `/api/w/${encodeURIComponent(slug)}/questionnaires/${encodeURIComponent(id)}/mapping-proposal`,
  );
}

export function confirmMapping(
  slug: string,
  id: string,
  mapping: Pick<XlsxMappingProposal, 'question_col' | 'response_col' | 'section_col' | 'evidence_col'>,
) {
  return req<{ questionnaire: QuestionnaireRow }>(
    `/api/w/${encodeURIComponent(slug)}/questionnaires/${encodeURIComponent(id)}/mapping-confirm`,
    { method: 'POST', body: JSON.stringify(mapping) },
  );
}

// ---- Questionnaires — M5 review loop (PR-P2-4) ------------------------

export function editAnswer(slug: string, id: string, answerId: string, preparerText: string) {
  return req<{ answer: QuestionnaireAnswerRow }>(
    `/api/w/${encodeURIComponent(slug)}/questionnaires/${encodeURIComponent(id)}/answers/${encodeURIComponent(answerId)}`,
    { method: 'PUT', body: JSON.stringify({ preparer_text: preparerText }) },
  );
}

export function approveAnswer(slug: string, id: string, answerId: string, approvalNote?: string) {
  return req<{ answer: QuestionnaireAnswerRow }>(
    `/api/w/${encodeURIComponent(slug)}/questionnaires/${encodeURIComponent(id)}/answers/${encodeURIComponent(answerId)}/approve`,
    { method: 'POST', body: JSON.stringify(approvalNote ? { approval_note: approvalNote } : {}) },
  );
}

export function requestAnswerChanges(slug: string, id: string, answerId: string, reason: string) {
  return req<{ answer: QuestionnaireAnswerRow }>(
    `/api/w/${encodeURIComponent(slug)}/questionnaires/${encodeURIComponent(id)}/answers/${encodeURIComponent(answerId)}/request-changes`,
    { method: 'POST', body: JSON.stringify({ reason }) },
  );
}

export function approveQuestionnaire(slug: string, id: string) {
  return req<{ questionnaire: QuestionnaireRow; approved_answers: number }>(
    `/api/w/${encodeURIComponent(slug)}/questionnaires/${encodeURIComponent(id)}/approve`,
    { method: 'POST' },
  );
}

// ---- Questionnaires — Distribute (PR-P2-4) -----------------------------

export interface QuestionAssignResult {
  status: 'ok' | 'refused';
  question_id: string;
  assigned_to_user_id?: string | null;
  reason?: string;
}

export function assignQuestion(
  slug: string,
  id: string,
  questionId: string,
  assigneeUserId: string | null,
) {
  return req<QuestionAssignResult>(
    `/api/w/${encodeURIComponent(slug)}/questionnaires/${encodeURIComponent(id)}/questions/${encodeURIComponent(questionId)}/assign`,
    { method: 'POST', body: JSON.stringify({ assignee_user_id: assigneeUserId }) },
  );
}

export function bulkAssignQuestions(
  slug: string,
  id: string,
  questionIds: string[],
  assigneeUserId: string,
) {
  return req<{ assigned: QuestionAssignResult[]; refused: QuestionAssignResult[] }>(
    `/api/w/${encodeURIComponent(slug)}/questionnaires/${encodeURIComponent(id)}/questions/bulk-assign`,
    { method: 'POST', body: JSON.stringify({ question_ids: questionIds, assignee_user_id: assigneeUserId }) },
  );
}

export function getMyQuestionnaireRollups(slug: string) {
  return req<{ rollups: QuestionnaireRollup[] }>(
    `/api/w/${encodeURIComponent(slug)}/questionnaires/mine`,
  );
}

export interface EvidenceGapRow {
  risk_item_id: string;
  title: string;
  citation_count: number;
  questionnaire_count: number;
}

export function getEvidenceGaps(slug: string) {
  return req<{ gaps: EvidenceGapRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/questionnaires/evidence-gaps`,
  );
}

// ---- Workspace team members (read) -------------------------------------
// Reused by the Distribute assignee picker. Same endpoint RegisterItemDetail
// already fetches raw; typed here for QuestionnaireDetail.tsx.

export interface WorkspaceMember {
  user_id: string;
  email: string;
  display_name: string;
  role: string;
}

export function listWorkspaceMembers(slug: string) {
  return req<{ members: WorkspaceMember[] }>(
    `/api/w/${encodeURIComponent(slug)}/settings/team/members`,
  );
}

// ---- Composite team settings (V9-4-1) -----------------------------------
// Returns members + invitations + join-requests + orphan-domain-users in
// one round-trip, eliminating 4 sequential fetches from TeamSettings.tsx.

export interface TeamCompositeData {
  members: WorkspaceMember[];
  invitations: TeamInvitation[];
  join_requests: JoinRequestRow[];
  orphan_domain_users: OrphanDomainUserRow[];
}

export function getTeamSettings(slug: string) {
  return req<TeamCompositeData>(
    `/api/w/${encodeURIComponent(slug)}/settings/team`,
  );
}

// ---- UC-4 Alerts (PR-P3-1 backend; inbox UI is PR-P3-2) ---------------

export interface AlertListItem {
  alert_id: string;
  workspace_id: string;
  category: AlertCategory;
  severity: AlertSeverity;
  status: AlertStatus;
  title: string;
  tldr_json: string;
  body: string | null;
  recommended_action: string | null;
  source_kind: string;
  source_id: string;
  source_url: string | null;
  register_item_refs: string;
  deadline_at: string | null;
  created_at: string;
  updated_at: string;
  converted_at: string | null;
  snoozed_until: string | null;
  closed_at: string | null;
  read_at: string | null;
  user_snoozed_until: string | null;
  workspace_slug?: string;
  workspace_name?: string;
}

export interface AlertSubscription {
  subscription_id: string;
  workspace_id: string;
  user_id: string;
  categories: AlertCategory[];
  cadence: AlertCadence;
  channels: string[];
  relevance_targets_only: boolean;
  auto_snooze: boolean;
  created_at: string;
  updated_at: string;
}

/** Critical-only count for the AppShell pill (P0-2-followup-2). */
export function getAlertsCount(slug: string) {
  return req<{ critical: number }>(
    `/api/w/${encodeURIComponent(slug)}/alerts/count`,
  );
}

export function listAlerts(
  slug: string,
  params: {
    status?: AlertStatus | 'all';
    category?: AlertCategory;
    severity?: AlertSeverity;
    unread?: boolean;
    limit?: number;
    offset?: number;
  } = {},
) {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.category) q.set('category', params.category);
  if (params.severity) q.set('severity', params.severity);
  if (params.unread) q.set('unread', '1');
  if (params.limit) q.set('limit', String(params.limit));
  if (params.offset) q.set('offset', String(params.offset));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return req<{ alerts: AlertListItem[]; total: number }>(
    `/api/w/${encodeURIComponent(slug)}/alerts${suffix}`,
  );
}

/**
 * The caller's own memberships. Scoped by session, never by a param —
 * `GET /api/workspaces` resolves `listUserWorkspaces(user_id)` server
 * side. Surfaces that need to know "is this person a consultant with
 * several clients?" ask this rather than inferring it from whatever
 * rows a list endpoint happened to return.
 */
export interface MyWorkspaceRow {
  workspace_id: string;
  slug: string;
  name: string;
  kind: 'personal' | 'team' | 'consultant';
  role: 'owner' | 'admin' | 'editor' | 'viewer' | 'advisor' | 'assessor';
  mfa_verified_at: string | null;
  last_seen_at: string | null;
}

export function listMyWorkspaces() {
  return req<{ workspaces: MyWorkspaceRow[] }>('/api/workspaces');
}

/**
 * Consultant aggregate inbox (PR-P3-4 / nav-ia-v2 §7.6 + §9.6).
 *
 * `slug` is only the ambient workspace the request rides through so
 * `workspaceContext()` can authenticate it — it does NOT scope the
 * result. The server resolves the caller's own memberships from the
 * session and returns alerts from each, every row stamped with its
 * home `workspace_slug` + `workspace_name`.
 */
export function listAggregateAlerts(
  slug: string,
  params: {
    status?: AlertStatus | 'all';
    category?: AlertCategory;
    severity?: AlertSeverity;
    unread?: boolean;
    limit?: number;
    offset?: number;
  } = {},
) {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.category) q.set('category', params.category);
  if (params.severity) q.set('severity', params.severity);
  if (params.unread) q.set('unread', '1');
  if (params.limit) q.set('limit', String(params.limit));
  if (params.offset) q.set('offset', String(params.offset));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return req<{ alerts: AlertListItem[]; total: number }>(
    `/api/w/${encodeURIComponent(slug)}/alerts/aggregate${suffix}`,
  );
}

/**
 * Cross-tenant convert. One request, one fan-out — NOT the client-side
 * `Promise.allSettled` loop the single-workspace bulk-bar uses, because
 * the alerts here belong to different workspaces and each one's
 * editor-role check has to happen against its own tenant. `skipped`
 * carries per-alert refusals (`forbidden`, `not_found`) and must be
 * surfaced: a consultant who is a viewer in one client workspace gets a
 * partial conversion, and silence would read as success.
 */
export function convertAggregateAlerts(slug: string, alertIds: string[]) {
  return req<{
    converted: Array<{
      status: 'ok' | 'refused';
      alert_id: string;
      linked: number;
      reason?: string;
    }>;
    skipped: Array<{ alert_id: string; reason: string }>;
  }>(`/api/w/${encodeURIComponent(slug)}/alerts/aggregate/convert`, {
    method: 'POST',
    body: JSON.stringify({ alert_ids: alertIds }),
  });
}

export function getAlert(slug: string, id: string) {
  return req<{ alert: AlertListItem }>(
    `/api/w/${encodeURIComponent(slug)}/alerts/${encodeURIComponent(id)}`,
  );
}

export function markAlertRead(slug: string, id: string) {
  return req<{ alert: AlertListItem }>(
    `/api/w/${encodeURIComponent(slug)}/alerts/${encodeURIComponent(id)}/read`,
    { method: 'POST' },
  );
}

export function snoozeAlert(slug: string, id: string, until: string | null) {
  return req<{ alert: AlertListItem }>(
    `/api/w/${encodeURIComponent(slug)}/alerts/${encodeURIComponent(id)}/snooze`,
    { method: 'POST', body: JSON.stringify({ until }) },
  );
}

export function convertAlert(slug: string, id: string) {
  return req<{ status: 'ok' | 'refused'; alert_id: string; linked: number; reason?: string }>(
    `/api/w/${encodeURIComponent(slug)}/alerts/${encodeURIComponent(id)}/convert`,
    { method: 'POST' },
  );
}

export function dismissAlert(slug: string, id: string) {
  return req<{ alert: AlertListItem }>(
    `/api/w/${encodeURIComponent(slug)}/alerts/${encodeURIComponent(id)}/close`,
    { method: 'POST' },
  );
}

// ---- External forward flow (PR-P3-3) ----------------------------------

export type AlertForwardState = 'active' | 'opened' | 'expired' | 'revoked';

export interface AlertForwardSummary {
  forward_id: string;
  alert_id: string;
  recipient_email: string;
  note: string | null;
  delivery_method: AlertForwardDeliveryMethod;
  expires_at: string;
  revoked_at: string | null;
  first_opened_at: string | null;
  last_opened_at: string | null;
  open_count: number;
  email_delivery: 'sent' | 'not_configured' | 'error' | null;
  content_sha256: string | null;
  created_by: string;
  created_by_display_name: string | null;
  created_at: string;
  state: AlertForwardState;
}

export interface AlertForwardSendResult {
  forward_id: string;
  recipient_email: string;
  expires_at: string;
  /** Returned to the SENDER only. Copy-link fallback when email is off. */
  packet_url: string;
  email_delivery:
    | { status: 'sent'; message_id: string }
    | { status: 'not_configured' }
    | { status: 'error'; detail: string };
}

/** Mint one signed packet link per recipient. Owner/admin/editor only. */
export function forwardAlertExternal(
  slug: string,
  id: string,
  input: {
    recipients: string[];
    note?: string | null;
    delivery_method?: AlertForwardDeliveryMethod;
  },
) {
  return req<{ status: 'ok'; forwards: AlertForwardSendResult[] }>(
    `/api/w/${encodeURIComponent(slug)}/alerts/${encodeURIComponent(id)}/forward-external`,
    {
      method: 'POST',
      body: JSON.stringify({
        recipients: input.recipients,
        note: input.note ?? null,
        delivery_method: input.delivery_method ?? 'magiclink',
      }),
    },
  );
}

/** Chain-of-custody list for one alert. Any active member. */
export function listAlertForwards(slug: string, id: string) {
  return req<{ forwards: AlertForwardSummary[] }>(
    `/api/w/${encodeURIComponent(slug)}/alerts/${encodeURIComponent(id)}/forwards`,
  );
}

export function revokeAlertForward(slug: string, id: string, forwardId: string) {
  return req<{ forward: AlertForwardSummary }>(
    `/api/w/${encodeURIComponent(slug)}/alerts/${encodeURIComponent(id)}` +
      `/forwards/${encodeURIComponent(forwardId)}/revoke`,
    { method: 'POST' },
  );
}

export interface AlertPacket {
  alert: {
    alert_id: string;
    category: AlertCategory;
    severity: AlertSeverity;
    title: string;
    tldr: string[];
    body: string | null;
    recommended_action: string | null;
    source_url: string | null;
    source_kind: string;
    deadline_at: string | null;
    created_at: string;
    updated_at: string;
  };
  workspace_name: string;
  sender_display_name: string;
  note: string | null;
  sent_at: string;
  expires_at: string;
  /** Pinned when the packet was minted — a value the reader can cite. */
  content_sha256: string;
  /** True when the alert was edited after this packet was sent. */
  content_revised: boolean;
}

export type AlertPacketRefusal = 'not_found' | 'expired' | 'revoked';

export type AlertPacketResult =
  | { status: 'ok'; packet: AlertPacket }
  | { status: 'refused'; reason: AlertPacketRefusal };

/**
 * Resolve a forwarded packet. PUBLIC — no session, no workspace slug;
 * the token in the URL is the credential. Called only by the
 * `/packet/:token` page, which renders outside the app shell.
 *
 * Deliberately NOT routed through `req()`: expired and revoked are
 * expected states of a real link, not failures, and each needs its own
 * copy on screen. `req()` would flatten all three into one error string
 * the page would have to parse back apart. A genuine network or server
 * fault still throws.
 */
export async function getAlertPacket(token: string): Promise<AlertPacketResult> {
  const res = await fetch(`${BASE}/api/forward/${encodeURIComponent(token)}`, {
    headers: { 'Content-Type': 'application/json' },
  });
  if (res.ok) {
    const body = (await res.json()) as { packet: AlertPacket };
    return { status: 'ok', packet: body.packet };
  }
  if (res.status === 404) {
    let reason: AlertPacketRefusal = 'not_found';
    try {
      const body = (await res.json()) as { reason?: AlertPacketRefusal };
      if (body?.reason === 'expired' || body?.reason === 'revoked') reason = body.reason;
    } catch {
      /* fall through to not_found */
    }
    return { status: 'refused', reason };
  }
  throw new ApiError(res.status, res.statusText);
}

// ---- Bulk helpers (AlertsInbox bulk-bar) -------------------------------
// No single-workspace bulk endpoint exists on the backend (only the
// cross-workspace aggregate/convert route batches) — these loop the
// per-alert calls client-side. Promise.allSettled (not Promise.all) so
// one network failure doesn't discard the other N-1 requests that may
// have already succeeded server-side (ux-adversary Flow High finding —
// Promise.all rejects on the first failure with no way to tell which
// of the rest actually landed).

export async function convertAlertBulk(slug: string, ids: string[]) {
  return Promise.allSettled(ids.map((id) => convertAlert(slug, id)));
}

/**
 * Dismiss / snooze addressed per alert's OWN workspace (PR-P3-4).
 *
 * Consultant aggregate mode puts alerts from several tenants in one
 * selection, and `/api/w/:slug/alerts/:id/close` resolves the alert
 * within `:slug` — so sending every id through the ambient slug would
 * 404 each foreign alert. Passing each row's home slug is also the
 * correct authorization shape: the close/snooze guard then runs against
 * the workspace that actually owns the row.
 */
export interface ScopedAlertRef {
  /** The alert's home workspace slug, from the row itself. */
  slug: string;
  id: string;
}

export async function closeAlertBulkScoped(refs: ScopedAlertRef[]) {
  return Promise.allSettled(refs.map((r) => dismissAlert(r.slug, r.id)));
}

export async function snoozeAlertBulkScoped(refs: ScopedAlertRef[], until: string) {
  return Promise.allSettled(refs.map((r) => snoozeAlert(r.slug, r.id, until)));
}

export function getAlertPreferences(slug: string) {
  return req<{ preferences: AlertSubscription }>(
    `/api/w/${encodeURIComponent(slug)}/alerts/preferences`,
  );
}

export function updateAlertPreferences(
  slug: string,
  patch: Partial<Pick<AlertSubscription, 'categories' | 'cadence' | 'channels' | 'relevance_targets_only' | 'auto_snooze'>>,
) {
  return req<{ preferences: AlertSubscription }>(
    `/api/w/${encodeURIComponent(slug)}/alerts/preferences`,
    { method: 'PUT', body: JSON.stringify(patch) },
  );
}

// --- Use Cases (V2-C-9 / UC-6) ---

export interface UseCaseRow {
  use_case_id: string;
  workspace_id: string;
  title: string;
  description: string;
  purpose: string;
  autonomy_level: string;
  status: string;
  taxonomy_type: string | null;
  eu_ai_act_tier: string | null;
  risk_tier: string | null;
  owner_user_id: string;
  created_at: string;
  updated_at: string;
}

export function listUseCases(slug: string, status?: string) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return req<{ use_cases: UseCaseRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/use-cases${qs}`,
  );
}

export function createUseCase(slug: string, input: {
  title: string; description: string; purpose: string;
  autonomy_level: string; data_classes?: string[];
  affected_persons?: string[];
  ai_activities?: string[];
  deployment_context?: Record<string, unknown>;
  system_id?: string;
  deployment_jurisdictions?: string[];
  consequential_decision?: boolean;
  gpai_role?: 'none' | 'provider' | 'deployer';
}) {
  return req<{ use_case: UseCaseRow; classification: Record<string, unknown> }>(
    `/api/w/${encodeURIComponent(slug)}/use-cases`,
    { method: 'POST', body: JSON.stringify(input) },
  );
}

export function submitUseCase(slug: string, id: string) {
  return req<{ use_case: UseCaseRow }>(
    `/api/w/${encodeURIComponent(slug)}/use-cases/${encodeURIComponent(id)}/submit`,
    { method: 'POST' },
  );
}

export function reviewUseCase(slug: string, id: string, input: {
  decision: 'approve' | 'reject' | 'request_changes';
  rationale: string;
}) {
  return req<{ use_case: UseCaseRow }>(
    `/api/w/${encodeURIComponent(slug)}/use-cases/${encodeURIComponent(id)}/review`,
    { method: 'POST', body: JSON.stringify(input) },
  );
}

// --- Discovered Tools (V2-C-17 / UC-5) ---

export interface DiscoveredToolRow {
  tool_id: string;
  name: string;
  vendor_id: string | null;
  status: string;
  user_count: number;
  first_seen: string;
  last_seen: string;
  signal_kinds: string;
}

export function listDiscoveredTools(slug: string, status?: string) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return req<{ tools: DiscoveredToolRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/discovered-tools${qs}`,
  );
}

// --- Risk Acceptances (V2-C-21) ---

export interface RiskAcceptanceRow {
  id: string;
  register_item_id: string;
  closure_status: string;
  target_closure_at: string;
  residual_risk_rationale: string;
  accepted_at: string;
}

export function listRiskAcceptances(slug: string, status?: string) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return req<{ acceptances: RiskAcceptanceRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/risk-acceptances${qs}`,
  );
}

/**
 * V109-1-19 (LM-15): accept a risk (POST, owner / admin; the caller is the acceptor, `owner_user_id`
 * the risk owner). The body names its scope: `system_id` or `all_systems: true`.
 */
export interface CreateRiskAcceptanceBody {
  register_item_id: string;
  profile_id: string;
  owner_user_id: string;
  target_closure_at: string;
  residual_risk_rationale: string;
  control_id?: string;
  system_id?: string;
  all_systems?: true;
}
export function createRiskAcceptance(slug: string, body: CreateRiskAcceptanceBody) {
  return req<{ acceptance: RiskAcceptanceRow }>(
    `/api/w/${encodeURIComponent(slug)}/risk-acceptances`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

// --- Connectors (V2-D-3) ---

export interface ConnectorRow {
  connector_id: string;
  kind: string;
  status: string;
  boundary_declaration: string;
  capabilities_json: string;
  configured_at: string;
  last_run_at?: string | null;
  last_error_message?: string | null;
  /** V108-3-5: the customer environment (production unless labelled). */
  environment?: string | null;
  /** GitHub: the sign-in expired and could not be renewed; offer Reconnect. */
  needs_reconnect?: boolean;
  config_json?: string;
}

export function listConnectors(slug: string) {
  return req<{ connectors: ConnectorRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/connectors`,
  );
}

// --- Control Checks (V2-D-13) ---

export interface ControlCheckRow {
  check_id: string;
  control_id: string;
  status: string;
  observed_at: string;
  collector_version: string;
  emitter_identity?: string | null;
  emitter_signature_verified?: number;
  submission_timestamp?: string | null;
  ci_run_url?: string | null;
  provenance_level?: 'unverified' | 'identity_verified' | 'verified';
  /** LM-21: a Tested rule's result — shown as "Preview", never counted. */
  preview?: boolean;
}

export function listControlChecks(slug: string, controlId?: string, limit?: number) {
  const params = new URLSearchParams();
  if (controlId) params.set('control_id', controlId);
  if (limit) params.set('limit', String(limit));
  const qs = params.toString();
  return req<{ checks: ControlCheckRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/control-checks${qs ? `?${qs}` : ''}`,
  );
}

// --- Findings (V2-D-13) ---

export interface FindingRow {
  finding_id: string;
  control_id: string;
  severity: string;
  status: string;
  sla_deadline_at: string;
  sla_breached: boolean;
  opened_at: string;
  /** The list route returns every column; these two drive the remediation coach (V101-5-4). */
  kind?: string;
  title?: string;
}

export function listFindings(slug: string, status?: string) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return req<{ findings: FindingRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/findings${qs}`,
  );
}

// --- Remediation drafts (V101-5-4 / ADR-0054) ---
// GET any member; POST draft / accept / dismiss owner, admin or editor.

export type RemediationDraftStatus = 'draft' | 'refused' | 'accepted' | 'dismissed' | 'superseded';
export type RemediationRefusalReason = 'unsupported_finding' | 'no_failing_check' | 'no_hint' | 'provider_error' | 'low_confidence' | 'invalid_output';

export interface RemediationIac {
  terraform?: string;
  aws_config_rule?: string;
  gcp_constraint?: string;
  mappings?: Array<{ target_type: string; mapping_version: string; rule_template: unknown }>;
}

export interface RemediationDraft {
  draft_id: string;
  finding_id: string;
  status: RemediationDraftStatus;
  refusal_reason: RemediationRefusalReason | null;
  check_id: string | null;
  evidence_hash: string | null;
  check_key: string | null;
  control_id: string;
  hint_version: number | null;
  hint_reviewed: boolean;
  steps: string[];
  iac: RemediationIac | null;
  doc_url: string | null;
  assessor_reply: string | null;
  provider_id: string | null;
  model: string | null;
  confidence: number | null;
  created_by_user_id: string;
  created_at: string;
  decided_by_user_id: string | null;
  decided_at: string | null;
}

const draftsPath = (slug: string, findingId: string) =>
  `/api/w/${encodeURIComponent(slug)}/findings/${encodeURIComponent(findingId)}/remediation-drafts`;

export function listRemediationDrafts(slug: string, findingId: string) {
  return req<{ drafts: RemediationDraft[] }>(draftsPath(slug, findingId));
}

/** 409 { code: 'no_ai_provider' | 'not_open' }, 429 { code: 'rate_limited' } surface as ApiError (see remediationErrorCode). */
export function draftRemediation(slug: string, findingId: string) {
  return req<{ draft: RemediationDraft; audit_status: 'recorded' | 'unavailable' }>(draftsPath(slug, findingId), { method: 'POST', body: '{}' });
}

/** `keepalive` lets a held dismiss commit while the page is unloading (pagehide). */
export function decideRemediationDraft(slug: string, findingId: string, draftId: string, decision: 'accept' | 'dismiss', opts?: { keepalive?: boolean }) {
  return req<{ draft: RemediationDraft }>(`${draftsPath(slug, findingId)}/${encodeURIComponent(draftId)}/${decision}`,
    { method: 'POST', body: '{}', ...(opts?.keepalive ? { keepalive: true } : {}) });
}

export interface RemediationSummaryEntry {
  /** Latest draft that is not superseded or dismissed. */
  status: 'draft' | 'refused' | 'accepted';
  refusal_reason: RemediationRefusalReason | string | null;
  decided_by_user_id: string | null;
  decided_at: string | null;
  /** Whether any draft on the finding was ever accepted as its plan. */
  plan_accepted: boolean;
}

/** One request for every finding's coach state (Findings rows, Operate chips). Any member. */
export function getRemediationSummary(slug: string) {
  return req<{ summary: Record<string, RemediationSummaryEntry> }>(`/api/w/${encodeURIComponent(slug)}/findings/remediation-summary`);
}

// --- Workspace AI policy (ADR-0020; GET any member, PUT owner) ---

export interface AiPolicy {
  allowed_providers: string[];
  default_provider_id: string;
  federal_boundary_only: boolean;
  updated_at: string | null;
}
export interface AiProviderOffer {
  provider_id: string;
  display_name: string;
  /** e.g. 'in_boundary' | 'vendor_us' — the provider's declared data boundary. */
  boundary: string;
  can_draft: boolean;
  /** ADR-0073: FedRAMP-authorized for this workload (re-checked against Marketplace coverage). */
  authorized?: boolean;
  /** ADR-0073: the workspace's own provider (V105-2-6). */
  customer_owned?: boolean;
}

export function getAiPolicy(slug: string) {
  return req<{ policy: AiPolicy; providers: AiProviderOffer[] }>(`/api/w/${encodeURIComponent(slug)}/ai-policy`);
}

/** 422 { error } when the policy is invalid (surfaces as ApiError.message). */
export function putAiPolicy(slug: string, body: Omit<AiPolicy, 'updated_at'>) {
  return req<{ policy: AiPolicy; providers: AiProviderOffer[]; audit_status: 'recorded' | 'unavailable' }>(
    `/api/w/${encodeURIComponent(slug)}/ai-policy`, { method: 'PUT', body: JSON.stringify(body) });
}

// ---- ADR-0073: data mode + prompt-field classes (V105-2-1, 2-3) ----------------------

export type DataMode = 'standard' | 'federal';
export interface DataModeView {
  workspace_id: string;
  data_mode: DataMode;
  data_mode_set_at: string | null;
  data_mode_set_by_user_id: string | null;
  /** The organization is on the federal plan (mode locked on). */
  locked_by_org: boolean;
  scope: 'inference_and_egress_partition';
}

/** Any member reads. */
export function getDataMode(slug: string) {
  return req<{ data_mode: DataModeView }>(`/api/w/${encodeURIComponent(slug)}/settings/data-mode`);
}

/** Owner only; standard → federal only (409 on downgrade, 422 on a wrong confirm slug). */
export function putDataMode(slug: string, body: { data_mode: DataMode; confirm_slug?: string }) {
  return req<{ data_mode: DataModeView; changed: boolean; audit_status: string }>(
    `/api/w/${encodeURIComponent(slug)}/settings/data-mode`, { method: 'PUT', body: JSON.stringify(body) });
}

export type PromptDataClass = 'reference' | 'tenant' | 'federal';
export interface FieldClassRow {
  field_key: string;
  label: string;
  owner: string;
  default_class: PromptDataClass;
  effective_class: PromptDataClass;
  raisable_to: PromptDataClass[];
  overrides: Array<{ override_id: string; class: PromptDataClass; reason: string; set_by_user_id: string; set_at: string }>;
}

/** Any member reads. */
export function getFieldClasses(slug: string) {
  return req<{ fields: FieldClassRow[] }>(`/api/w/${encodeURIComponent(slug)}/settings/field-classes`);
}

/** Owner / admin; raise-only (409 field_class_not_raised otherwise). */
export function raiseFieldClass(slug: string, body: { field_key: string; class: PromptDataClass; reason: string }) {
  return req<{ fields: FieldClassRow[]; audit_status: string }>(
    `/api/w/${encodeURIComponent(slug)}/settings/field-classes`, { method: 'POST', body: JSON.stringify(body) });
}

// ---- ADR-0073 §7: federal parity (V105-2-7; any member) -------------------------------

export interface ParityActionView { label: string; href: string; role: 'owner' | 'admin' | 'member' }
export interface FeatureParity {
  feature: string;
  label: string;
  /** What runs for this workspace's data now. */
  state: 'ai' | 'deterministic' | 'off';
  served_by: 'authorized' | 'customer' | 'platform' | null;
  action: ParityActionView | null;
}
export interface FederalParityView {
  data_mode: 'standard' | 'federal';
  federal_boundary_only: boolean;
  authorized_provider_configured: boolean;
  features: FeatureParity[];
}
export function getFederalParity(slug: string) {
  return req<FederalParityView>(`/api/w/${encodeURIComponent(slug)}/federal-parity`);
}

/** The `action` on a 409 federal_off (ADR-0073 §7), when the server sent one. */
export function federalOffAction(e: unknown): ParityActionView | null {
  if (!(e instanceof ApiError)) return null;
  const b = e.body as { code?: unknown; action?: unknown } | null | undefined;
  if (b?.code !== 'federal_off' || !b.action || typeof b.action !== 'object') return null;
  const a = b.action as Partial<ParityActionView>;
  return typeof a.label === 'string' && typeof a.href === 'string' && a.href.startsWith('/w/') ? { label: a.label, href: a.href, role: (a.role as ParityActionView['role']) ?? 'admin' } : null;
}

// ---- ADR-0073 §6: customer-owned AI providers (V105-2-6; owner / admin) ----------------

export type CustomerAiProviderKind = 'vertex_wif' | 'azure_openai' | 'openai_compatible';
export interface CustomerAiProvider {
  ai_provider_id: string;
  kind: CustomerAiProviderKind;
  display_name: string;
  endpoint_url: string | null;
  region: string | null;
  config: { project_id?: string; wif_provider?: string; service_account_email?: string; deployment?: string; api_version?: string };
  model_id: string;
  status: 'pending_eval' | 'active' | 'disabled';
  rerun_reason: string | null;
  has_key: boolean;
  wif_subject: string | null;
  /** Tasks that serve customer content: eval passed AND an owner acknowledged this configuration. */
  enabled_tasks: string[];
  /** Tasks whose latest check passed (they serve only after the owner acknowledgement). */
  eval_passed_tasks: string[];
  /** V105-2-10 M-3: 'stale' = acknowledged, but the endpoint / model / key changed since. */
  owner_ack: { state: 'approved' | 'needed' | 'stale'; acked_by_user_id: string | null; acked_at: string | null };
  evals: Array<{ task: string; score: number; threshold: number; pass: boolean; model_id: string; evaluated_at: string }>;
}
export interface CustomerAiProviderInput {
  kind: CustomerAiProviderKind;
  display_name: string;
  model_id: string;
  endpoint_url?: string;
  region?: string;
  config?: CustomerAiProvider['config'];
  api_key?: string;
}

export function listAiProviders(slug: string) {
  return req<{ providers: CustomerAiProvider[] }>(`/api/w/${encodeURIComponent(slug)}/ai-providers`);
}
export function createAiProvider(slug: string, body: CustomerAiProviderInput) {
  return req<{ provider: CustomerAiProvider }>(`/api/w/${encodeURIComponent(slug)}/ai-providers`, { method: 'POST', body: JSON.stringify(body) });
}
export function patchAiProvider(slug: string, id: string, body: Partial<CustomerAiProviderInput> & { status?: 'disabled' }) {
  return req<{ provider: CustomerAiProvider }>(`/api/w/${encodeURIComponent(slug)}/ai-providers/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) });
}
/** Owner only: acknowledge the provider's current configuration (it then serves the tasks that passed). */
export function approveAiProvider(slug: string, id: string) {
  return req<{ provider: CustomerAiProvider; changed: boolean }>(
    `/api/w/${encodeURIComponent(slug)}/ai-providers/${encodeURIComponent(id)}/approve`, { method: 'POST' });
}
export function evaluateAiProvider(slug: string, id: string) {
  return req<{ provider: CustomerAiProvider; results: Array<{ task: string; score: number; threshold: number; pass: boolean }> }>(
    `/api/w/${encodeURIComponent(slug)}/ai-providers/${encodeURIComponent(id)}/evaluate`, { method: 'POST' });
}

/** The server's machine code on a remediation-draft error, when it sent one. */
export function remediationErrorCode(e: unknown): string | null {
  if (!(e instanceof ApiError)) return null;
  const code = (e.body as { code?: unknown } | null | undefined)?.code;
  return typeof code === 'string' ? code : e.status === 429 ? 'rate_limited' : null;
}

// --- Org-tier (PR-P10-4) ---

export interface OrgDashboardData {
  organization: { organization_id: string; slug: string; display_name: string; plan: string };
  aggregate_score: number;
  /** Prior-period aggregate score (7 days ago). Null when no history. */
  previous_aggregate_score: number | null;
  /** Prior-period open findings count (7 days ago). Null when no history. */
  previous_findings_count: number | null;
  /** Prior-period open incidents count (7 days ago). Null when no history. */
  previous_incidents_count: number | null;
  workspace_count: number;
  workspaces: Array<{ workspace_id: string; slug: string; name: string; score: number; item_count: number; done_count: number }>;
  top_blockers: Array<{ workspace_slug: string; workspace_name: string; ref: string; title: string; severity: string; status: string }>;
}

export function getOrgDashboard(orgSlug: string) {
  return req<OrgDashboardData>(`/api/o/${encodeURIComponent(orgSlug)}/dashboard`);
}

export function getOrgControlHealth(orgSlug: string) {
  return req<{
    totals: { total: number; passing: number; failing: number; error: number; suppressed: number };
    workspaces: Array<{ workspace_id: string; slug: string; name: string; total: number; passing: number; failing: number }>;
  }>(`/api/o/${encodeURIComponent(orgSlug)}/control-health`);
}

// --- V3-8-6: Org-level findings + incidents ---

export function getOrgFindings(orgSlug: string) {
  return req<{
    findings: Array<{
      finding_id: string;
      workspace_slug: string;
      workspace_name: string;
      severity: string;
      status: string;
      control_id: string;
      sla_breached: boolean;
      sla_deadline_at: string;
      opened_at: string;
    }>;
    total: number;
  }>(`/api/o/${encodeURIComponent(orgSlug)}/findings`);
}

export function getOrgIncidents(orgSlug: string) {
  return req<{
    incidents: Array<{
      incident_id: string;
      workspace_slug: string;
      workspace_name: string;
      title: string;
      severity: string;
      status: string;
      jurisdiction: string;
      reported_at: string;
      obligations_count: number;
    }>;
    total: number;
  }>(`/api/o/${encodeURIComponent(orgSlug)}/incidents`);
}

export function getOrgJurisdictions(orgSlug: string) {
  return req<{ jurisdictions: Array<{ jurisdiction: string; legislation_count: number }> }>(
    `/api/o/${encodeURIComponent(orgSlug)}/jurisdictions`,
  );
}

// --- Org Alerts (V3-8-5) ---

export function getOrgAlerts(
  orgSlug: string,
  params: {
    status?: AlertStatus | 'all';
    category?: AlertCategory;
    severity?: AlertSeverity;
    limit?: number;
    offset?: number;
  } = {},
) {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.category) q.set('category', params.category);
  if (params.severity) q.set('severity', params.severity);
  if (params.limit) q.set('limit', String(params.limit));
  if (params.offset) q.set('offset', String(params.offset));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return req<{ alerts: AlertListItem[]; total: number }>(
    `/api/o/${encodeURIComponent(orgSlug)}/alerts${suffix}`,
  );
}

// --- Supplier Portfolio (V2-E-4 / V3-2-10) ---

export function listSupplierPortfolio(orgSlug: string) {
  return req<{ portfolio: Array<{ portfolio_entry_id: string; supplier_display_name: string; risk_tier: string | null; vertical: string | null }> }>(
    `/api/o/${encodeURIComponent(orgSlug)}/suppliers`,
  );
}

/** V3-2-10: Full portfolio list with summary KPIs for the org supplier portfolio page. */
export function listOrgSupplierPortfolio(orgSlug: string) {
  return req<{
    portfolio: Array<{
      portfolio_entry_id: string;
      supplier_display_name: string;
      risk_tier: 'critical' | 'high' | 'medium' | 'low' | null;
      score_band: 'red' | 'amber' | 'green' | null;
      disclosure_status: 'disclosed' | 'pending' | 'declined' | 'none' | null;
      flags: string[];
      renewal_date: string | null;
      last_reviewed_at: string | null;
      /** Workspace this supplier entry belongs to (NAV-4-3). */
      workspace_slug?: string | null;
      workspace_name?: string | null;
    }>;
    summary: {
      total: number;
      red: number;
      amber: number;
      green: number;
      red_delta: number;
      amber_delta: number;
      green_delta: number;
      last_synced_at: string | null;
    } | null;
  }>(`/api/o/${encodeURIComponent(orgSlug)}/suppliers`);
}

/** V3-2-10: Supplier detail for the org supplier detail page. */
export function getOrgSupplierDetail(orgSlug: string, supplierId: string) {
  return req<{
    portfolio_entry_id: string;
    supplier_display_name: string;
    risk_tier: string | null;
    score_band: 'red' | 'amber' | 'green' | null;
    added_at: string | null;
    disclosure_status: string | null;
    undisclosed_ai: { tools: string[]; description: string } | null;
    scorecard: {
      bar_name: string;
      fields_scored: number;
      fields_total: number;
      red_count: number;
      amber_count: number;
      green_count: number;
      fields: Array<{
        field: string;
        score_band: 'red' | 'amber' | 'green';
        score_value: number;
        score_max: number;
        citation_ref: string;
        citation_summary: string;
        citation_detail: string;
      }>;
    } | null;
    disclosure_versions: Array<{
      version: string;
      submitted_at: string;
      source: string;
      content_hash: string | null;
    }>;
    timeline: Array<{
      label: string;
      occurred_at: string | null;
      status: 'done' | 'active' | 'pending';
    }>;
    drift_alerts: Array<{
      field: string;
      old_band: 'red' | 'amber' | 'green';
      new_band: 'red' | 'amber' | 'green';
      reason: string;
      detected_at: string;
    }>;
  }>(`/api/o/${encodeURIComponent(orgSlug)}/suppliers/${encodeURIComponent(supplierId)}`);
}

// --- Incidents (V2-E-10) ---

export interface IncidentRow {
  incident_id: string;
  title: string;
  severity: string;
  status: string;
  reported_at: string;
  jurisdiction: string;
  /** V111-3-5: unnotified regulator deadlines already past (derived at read). */
  overdue_obligations?: number;
}

export function listIncidents(slug: string) {
  return req<{ incidents: IncidentRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/incidents`,
  );
}

export function createIncident(slug: string, input: {
  title: string; severity: string; jurisdiction?: string;
}) {
  return req<{ incident: IncidentRow }>(
    `/api/w/${encodeURIComponent(slug)}/incidents`,
    { method: 'POST', body: JSON.stringify(input) },
  );
}

// --- V111-3 (ADR-0093): incident ledger + stored notification clocks ---

export type LossKind = 'direct' | 'legal' | 'regulatory' | 'remediation';

export interface IncidentDetail extends IncidentRow {
  resolved_at: string | null;
  legal_privileged: number;
  system_id: string | null;
  occurred_at: string | null;
  discovered_at: string | null;
  root_cause: string | null;
  loss_amount_cents: number | null;
  loss_currency: string | null;
  loss_kind: LossKind | null;
  affected_persons_count: number | null;
  data_classes_json: string;
  outcome: string | null;
}

export interface IncidentObligation {
  obligation_id: string;
  incident_id: string;
  regulator: string;
  statute_ref: string;
  clock_start: string;
  deadline_hours: number;
  deadline_kind: 'calendar_hours' | 'business_days';
  deadline_at: string;
  notified_at: string | null;
  /** Derived at read — never stored. */
  status: 'pending' | 'notified' | 'overdue';
}

export interface IncidentPatch {
  status?: 'open' | 'investigating' | 'resolved' | 'closed';
  system_id?: string | null;
  occurred_at?: string | null;
  discovered_at?: string | null;
  root_cause?: string | null;
  loss_amount_cents?: number | null;
  loss_currency?: string | null;
  loss_kind?: LossKind | null;
  affected_persons_count?: number | null;
  data_classes?: string[];
  outcome?: string | null;
}

const incidentsBase = (slug: string) => `/api/w/${encodeURIComponent(slug)}/incidents`;

export function getIncident(slug: string, incidentId: string) {
  return req<{ incident: IncidentDetail; obligations: IncidentObligation[] }>(
    `${incidentsBase(slug)}/${encodeURIComponent(incidentId)}`,
  );
}

export function patchIncident(slug: string, incidentId: string, patch: IncidentPatch) {
  return req<{ incident: IncidentDetail; obligations?: IncidentObligation[] }>(
    `${incidentsBase(slug)}/${encodeURIComponent(incidentId)}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
}

/** `notifiedAt`: when the regulator was actually notified (ISO; never in the future). Defaults to now. */
export function markObligationNotified(slug: string, incidentId: string, obligationId: string, notifiedAt?: string) {
  return req<{ obligation: IncidentObligation; obligations: IncidentObligation[] }>(
    `${incidentsBase(slug)}/${encodeURIComponent(incidentId)}/obligations/${encodeURIComponent(obligationId)}/notified`,
    { method: 'POST', body: JSON.stringify(notifiedAt ? { notified_at: notifiedAt } : {}) },
  );
}

/** Owner/admin: the loss-ledger CSV (privileged incidents aggregate only). */
export function incidentLedgerCsvUrl(slug: string): string {
  return `${incidentsBase(slug)}/export?format=csv`;
}

// --- V111-1 (ADR-0092): declared + observed agent authority ---

export type ScopeClass = 'read' | 'write' | 'execute' | 'financial' | 'communication' | 'admin';
export type AutonomyLevel = 'fully_automated' | 'human_in_the_loop' | 'human_on_the_loop' | 'decision_support' | 'informational';

export interface AuthorityVersion {
  authority_id: string;
  system_id: string;
  version: number;
  autonomy_level: AutonomyLevel;
  tools_json: string;
  action_ceiling_json: string;
  containment_json: string;
  monitoring_owner_user_id: string | null;
  status: 'draft' | 'active' | 'superseded' | 'rejected';
  created_by_user_id: string;
  created_at: string;
  approved_by_user_id: string | null;
  approved_at: string | null;
  /** Users who edited the draft besides its author (none may approve). Absent before migration 708. */
  edited_by_json?: string;
}

export interface AuthorityDeclaration {
  autonomy_level: AutonomyLevel;
  tools: Array<{ name: string; provider_key?: string; scope_class: ScopeClass }>;
  action_ceiling: {
    authorized_action_classes?: ScopeClass[];
    prohibited_actions?: string[];
    limits?: { spend_usd?: number; rows?: number; recipients?: number };
  };
  containment: {
    kill_switch_kind?: string;
    kill_switch_owner_user_id?: string;
    rollback_evidence_ref?: string;
    fallback_mode?: string;
  };
  monitoring_owner_user_id?: string | null;
}

export interface SystemAuthorityView {
  active: AuthorityVersion | null;
  draft: AuthorityVersion | null;
  observed: Array<{ fact_id: string; fact_kind: string; provider_key: string | null; name: string; scope_class: ScopeClass | 'unknown'; summary_confidence: 'exact' | 'approximate'; gone_at: string | null }>;
  drift: { classes: ScopeClass[]; approximate_outside: string[] };
  verification: Array<{ kind: 'tool' | 'action_class'; name: string; scope_class: ScopeClass; verified: boolean }>;
  checks: Array<{ check_key: string; control_id: string; status: 'pass' | 'fail' | 'insufficient_evidence'; details: Record<string, unknown> }>;
}

const authorityBase = (slug: string, systemId: string) =>
  `/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/authority`;

export function getSystemAuthority(slug: string, systemId: string) {
  return req<SystemAuthorityView>(authorityBase(slug, systemId));
}
export function draftSystemAuthority(slug: string, systemId: string, body: AuthorityDeclaration) {
  return req<{ authority: AuthorityVersion }>(authorityBase(slug, systemId), { method: 'POST', body: JSON.stringify(body) });
}
export function updateSystemAuthorityDraft(slug: string, systemId: string, authorityId: string, body: AuthorityDeclaration) {
  return req<{ authority: AuthorityVersion }>(`${authorityBase(slug, systemId)}/${encodeURIComponent(authorityId)}`, { method: 'PUT', body: JSON.stringify(body) });
}
export function approveSystemAuthority(slug: string, systemId: string, authorityId: string) {
  return req<{ authority: AuthorityVersion }>(`${authorityBase(slug, systemId)}/${encodeURIComponent(authorityId)}/approve`, { method: 'POST' });
}
export function rejectSystemAuthority(slug: string, systemId: string, authorityId: string, reason: string) {
  return req<{ authority: AuthorityVersion }>(`${authorityBase(slug, systemId)}/${encodeURIComponent(authorityId)}/reject`, { method: 'POST', body: JSON.stringify({ reason }) });
}

// --- V111-4 (ADR-0093): FAIR-lite scenarios + runs ---

export interface RiskScenarioRunRow {
  run_id: string;
  scenario_id: string;
  seed: number;
  iterations: number;
  methodology_version: string;
  p5: number; p25: number; p50: number; p75: number; p95: number;
  mean: number;
  loss_exceedance_json: string;
  run_at: string;
}

export interface RiskScenarioRow {
  scenario_id: string;
  register_item_id: string;
  system_id: string | null;
  title: string;
  frequency_low: number; frequency_most_likely: number; frequency_high: number;
  magnitude_low: number; magnitude_most_likely: number; magnitude_high: number;
  control_health_factor: number;
  prior_source: 'manual' | 'ledger';
  prior_basis_json: string | null;
  latest_run?: RiskScenarioRunRow | null;
}

export interface LedgerPriors {
  inputs: Pick<RiskScenarioRow, 'frequency_low' | 'frequency_most_likely' | 'frequency_high' | 'magnitude_low' | 'magnitude_most_likely' | 'magnitude_high'>;
  basis: { source: 'incident_ledger'; incident_count: number; window_start: string; window_end: string; system_id: string | null };
}

const scenariosBase = (slug: string) => `/api/w/${encodeURIComponent(slug)}/risk-scenarios`;

export function listRiskScenarios(slug: string, registerItemId: string) {
  return req<{ scenarios: RiskScenarioRow[] }>(`${scenariosBase(slug)}?register_item_id=${encodeURIComponent(registerItemId)}`);
}
export function getLedgerPriors(slug: string) {
  return req<{ priors: LedgerPriors | null }>(`${scenariosBase(slug)}/priors`);
}
export function createRiskScenario(slug: string, body: Record<string, unknown>) {
  return req<{ scenario: RiskScenarioRow }>(scenariosBase(slug), { method: 'POST', body: JSON.stringify(body) });
}
export function runRiskScenario(slug: string, scenarioId: string) {
  return req<{ run: RiskScenarioRunRow }>(`${scenariosBase(slug)}/${encodeURIComponent(scenarioId)}/run`, { method: 'POST', body: '{}' });
}

// --- V111-5 (ADR-0094): the insurability pack ---

export interface PackRisk {
  register_item_id: string;
  /** The risk item the register detail page is keyed by (`/register/:id`). */
  risk_item_id: string;
  /** Systems the risk applies to (pack format /2). */
  system_ids?: string[];
  title: string;
  severity: string;
  status: string;
  citation: { source_type: string; source_url: string | null };
  controls: Array<{ control_id: string; state: string }>;
  uncovered: false | 'no_mitigating_controls' | 'applicability_unresolved';
  residual: { run: { p50: number; p95: number } | null } | null;
  loss_history: { incident_count: number; loss_amount_cents: number } | null;
  acceptances: Array<{ id: string }>;
  outside_selected_sources: boolean;
  /** /3: a register risk that only tracks the legal patchwork; its controls are the legal section (ADR-0114 §3). */
  tracking?: boolean;
}

import type {
  PackLegalSection, PackLegalControlRow, PackLawRow, PackLegalChanges, PackLegalTag, PrivilegeInclusionReason, PackLink,
} from '@aigovops/core';
export type { PackLegalSection, PackLegalControlRow, PackLawRow, PackLegalChanges, PackLegalTag, PrivilegeInclusionReason, PackLink };

/** /3: the owner's privileged inclusion as signed into one pack (internal read). */
export interface PackPrivilegeInclusion {
  determination_ids: string[];
  reason_code: PrivilegeInclusionReason;
  recipients: string[];
  acknowledged_by: string;
  acknowledged_at: string;
}

export interface InsurabilityPackDocument {
  /** gacpro.insurability-pack/1 | /2 | /3 (older packs keep their format). */
  format?: string;
  /** /3 (ADR-0114): the legal section; absent on /1 and /2 packs. */
  legal?: PackLegalSection;
  /** /3: change since the previous signed version (set at signing). */
  changes?: PackLegalChanges;
  privilege_inclusion?: PackPrivilegeInclusion | null;
  generated_at: string;
  cover: {
    organization_name: string;
    rule_sources_mode: string;
    outside_sources_note: string | null;
    counts: { systems: number; risks: number; uncovered_exposures: number; unmapped_obligations: number };
  };
  risks: PackRisk[];
  systems: Array<{ system_id: string; name: string; authority: { declared: unknown | null; self_attested_only: boolean; open_drift: unknown[]; read_gaps: string[] } }>;
  uncovered_exposures: { risks: Array<{ register_item_id: string; title: string; reason: string }>; unmapped_obligations: Array<{ system_id: string | null; duty: string }> };
  loss_ledger: { incident_count: number; privileged: { count: number; loss_amount_cents: number } };
}

export interface InsurabilityPackSummary {
  package_id: string;
  version: number;
  content_sha256: string;
  signed_by: string;
  signed_at: string;
  superseded_by_package_id: string | null;
  /** v11.6: set when the version was withdrawn (signed withdrawal record). */
  withdrawn_at?: string | null;
  withdrawal_reason?: string | null;
}

const packBase = (slug: string) => `/api/w/${encodeURIComponent(slug)}/insurability-pack`;

/** V111-5-2: a signed version as a PDF (member read; a plain download link). */
export function insurabilityPackPdfUrl(slug: string, packageId: string): string {
  return `${packBase(slug)}/packages/${encodeURIComponent(packageId)}/pdf`;
}

export function previewInsurabilityPack(slug: string) {
  /** `carried`: what signing this draft would carry (owners and admins; null otherwise). */
  return req<{ document: InsurabilityPackDocument; can_sign: boolean; carried?: { failing: number; overdue: number } | null }>(`${packBase(slug)}/preview`);
}
export function listInsurabilityPacks(slug: string) {
  return req<{ packages: InsurabilityPackSummary[]; can_sign: boolean }>(`${packBase(slug)}/packages`);
}
/** POST /packages body (strict). D-113-29: `reason` + `acknowledge` when the pack carries open problems. */
export interface SignInsurabilityPackBody {
  reason?: string;
  acknowledge?: Array<'failing' | 'overdue'>;
  /** ADR-0114 §9: privileged determinations included in THIS pack only. */
  include_privileged?: { determination_ids: string[]; reason_code: PrivilegeInclusionReason; recipients: string[]; acknowledged: true };
}
export function signInsurabilityPack(slug: string, body: SignInsurabilityPackBody = {}) {
  return req<{ package: InsurabilityPackSummary }>(`${packBase(slug)}/packages`, { method: 'POST', body: JSON.stringify(body) });
}

/** V116-4-5: a signed withdrawal record (internal read; `withdrawn_by` is a user id). */
export interface PackWithdrawalRecord { package_id: string; reason: string; withdrawn_by: string; withdrawn_at: string }

/** GET /packages/:id — any member: one signed pack, its re-verification and its withdrawal. */
export function getInsurabilityPack(slug: string, packageId: string) {
  return req<{
    package: InsurabilityPackSummary & { content: InsurabilityPackDocument };
    withdrawal: PackWithdrawalRecord | null;
    verification: { valid: boolean; content_sha256_matches: boolean | null; signature_valid: boolean | null; reason?: string };
  }>(`${packBase(slug)}/packages/${encodeURIComponent(packageId)}`);
}
/** POST /packages/:id/withdraw — owner / admin: a signed withdrawal record. */
export function withdrawInsurabilityPack(slug: string, packageId: string, reason: string) {
  return req<{ withdrawal: PackWithdrawalRecord }>(`${packBase(slug)}/packages/${encodeURIComponent(packageId)}/withdraw`, { method: 'POST', body: JSON.stringify({ reason }) });
}
/** GET /links — any member: keyed verify links (never the key). */
export function listPackLinks(slug: string) {
  return req<{ links: PackLink[]; can_manage: boolean }>(`${packBase(slug)}/links`);
}
export interface CreatePackLinkBody {
  package_id: string;
  kind: 'insurer' | 'broker';
  recipient_name: string;
  recipient_email?: string;
  expires_in_days?: number;
}
/** POST /links — owner / admin. `url` carries the key ONCE; it is never readable again. */
export function createPackLink(slug: string, body: CreatePackLinkBody) {
  return req<{ link: PackLink; url: string }>(`${packBase(slug)}/links`, { method: 'POST', body: JSON.stringify(body) });
}
/** POST /links/:id/revoke — owner / admin; ends the link's sessions. */
export function revokePackLink(slug: string, linkId: string, reason?: string) {
  return req<{ link: PackLink }>(`${packBase(slug)}/links/${encodeURIComponent(linkId)}/revoke`, { method: 'POST', body: JSON.stringify(reason ? { reason } : {}) });
}

/**
 * PUBLIC GET /verify/insurability-pack/:id?format=json. Keyless: signature + version state only (`access` null).
 * With a live access session (the HttpOnly cookie a keyed link sets) it adds the reader, the withdrawal record and
 * the projected signed content (owner names removed).
 */
export interface PackVerifyResponse {
  state: 'current' | 'replaced' | 'withdrawn';
  newer_version: { version: number; signed_at: string; package_id?: string } | null;
  withdrawn_at: string | null;
  package_id: string;
  kind: string;
  version: number | null;
  signed_at: string | null;
  content_sha256: string | null;
  signature_valid: boolean;
  content_matches_digest: boolean;
  access: { kind: 'insurer' | 'broker'; recipient_name: string } | null;
  withdrawal?: { reason: string; withdrawn_by: string | null; withdrawn_at: string; signature_valid: boolean } | null;
  content?: InsurabilityPackDocument;
}

/**
 * Reads the public verify JSON (same origin, so the access cookie rides along). A `key` from a keyed link that is
 * still in the address bar (the server normally swaps it before the page loads) is sent once, so the server can
 * start the session. Null = no such pack (404).
 */
export async function fetchPackVerify(id: string, key?: string | null): Promise<PackVerifyResponse | null> {
  const q = new URLSearchParams({ format: 'json' });
  if (key) q.set('k', key);
  const r = await fetch(`${BASE}/verify/insurability-pack/${encodeURIComponent(id)}?${q.toString()}`, {
    headers: { accept: 'application/json' }, credentials: 'same-origin',
  });
  if (r.status === 404) return null;
  if (!r.ok) throw new ApiError(r.status, 'unavailable');
  return (await r.json()) as PackVerifyResponse;
}
/** The keyed reader's PDF (the access cookie is required; the signed PDF behind a status cover page). */
export const packVerifyPdfUrl = (id: string) => `${BASE}/verify/insurability-pack/${encodeURIComponent(id)}/pdf`;

/** V111-5-3: an insurer grant (workspace-bound, no certification profile) + its token. Owner/admin. */
export type InsurerKind = 'ai_inventory' | 'ai_disclosure' | 'loss_history';
export function createInsurerGrant(slug: string, body: { agency_name: string; agency_contact_email: string; package_kinds: InsurerKind[]; expires_at: string }) {
  return req<{ grant: { grant_id: string } }>(`/api/w/${encodeURIComponent(slug)}/cds/grants`, { method: 'POST', body: JSON.stringify({ ...body, profile_id: null }) });
}
export interface CdsGrantRow {
  grant_id: string;
  agency_name: string;
  agency_contact_email: string;
  package_kinds: string[];
  expires_at: string;
  status: 'active' | 'revoked' | 'expired';
  last_access_at: string | null;
  reads: number;
}
export function listCdsGrants(slug: string) {
  return req<{ grants: CdsGrantRow[] }>(`/api/w/${encodeURIComponent(slug)}/cds/grants`);
}
export function revokeCdsGrant(slug: string, grantId: string) {
  return req<{ revoked: boolean }>(`/api/w/${encodeURIComponent(slug)}/cds/grants/${encodeURIComponent(grantId)}/revoke`, { method: 'POST' });
}
export function issueCdsToken(slug: string, grantId: string) {
  return req<{ token_id: string; token: string; token_hint: string }>(`/api/w/${encodeURIComponent(slug)}/cds/grants/${encodeURIComponent(grantId)}/tokens`, { method: 'POST' });
}

// --- AI-system component inventory (V3-9-8 / V3-9-9 · ADR-0025) ---
export interface ComponentRow {
  component_id: string;
  purl_canonical: string;
  name: string;
  version: string | null;
  license_spdx: string | null;
  license_class: 'permissive' | 'weak_copyleft' | 'copyleft' | 'proprietary' | 'unknown';
  scope: 'required' | 'optional' | 'excluded';
  depth: number;
  ai_role: string | null;
  ai_confidence: number | null;
  classification_status: 'recommended' | 'accepted' | 'rejected';
  accepted_by_user_id: string | null;
  accepted_at: string | null;
}

export interface SnapshotRow {
  snapshot_id: string;
  source: 'github_pull' | 'ci_push' | 'upload';
  trust: 'observed' | 'attested';
  format: 'cyclonedx' | 'spdx';
  spec_version: string;
  generation_context: string;
  tool_name: string | null;
  tool_version: string | null;
  component_count: number;
  coverage_score: number | null;
  element_gaps_json: string;
  prior_snapshot_id: string | null;
  published_at: string;
}

export function listSystemComponents(slug: string, systemId: string) {
  return req<{ components: ComponentRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/components`,
  );
}

export function listSystemSnapshots(slug: string, systemId: string) {
  return req<{ snapshots: SnapshotRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/boms`,
  );
}

/** Accept or reject a recommended classification (owner/admin). */
export function setComponentClassification(
  slug: string,
  systemId: string,
  componentId: string,
  status: 'accepted' | 'rejected',
) {
  return req<{ component_id: string; classification_status: string }>(
    `/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/components/${encodeURIComponent(componentId)}`,
    { method: 'PATCH', body: JSON.stringify({ status }) },
  );
}

/** Mint a bom:write token scoped to one system (owner/admin), by the `sbom_push` purpose. One-time reveal. */
export function mintBomToken(slug: string, systemId: string) {
  return req<{ token_id: string; label: string; prefix: string; scopes: string[]; plaintext: string }>(
    `/api/w/${encodeURIComponent(slug)}/settings/api`,
    { method: 'POST', body: JSON.stringify({ label: `CI push · ${systemId}`, purpose: 'sbom_push', system_id: systemId }) },
  );
}

// ---- V5: Workspace-scoped systems (ADR-0027) ----

export interface WsSystemRow {
  system_id: string;
  name: string;
  description: string | null;
  lane: 'buying' | 'building';
  origin: string;
  supplier_id: string | null;
  risk_tier: string | null;
  reporting_profile_id: string | null;
  current_stage: string | null;
  stage_snapshot_json: string | null;
  classified_at: string | null;
  deployment_status: string;
  data_classification_json: string | null;
  exposure_level: string | null;
  affected_person_categories_json: string | null;
  output_destinations_json: string | null;
  classification_json: string | null;
  /** ADR-0099 (migration 761): the inheritable answers the system set itself (JSON array; null = before 761). */
  scope_overrides_json?: string | null;
  /** Owner D4 / D5: who set each own answer, when, an optional reason, and `waiting` on a kept sector (JSON). */
  scope_override_meta_json?: string | null;
}

export function listWorkspaceSystems(slug: string, lane?: string) {
  const q = lane ? `?lane=${encodeURIComponent(lane)}` : '';
  return req<{ systems: WsSystemRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/systems${q}`
  );
}

export function getWorkspaceSystem(slug: string, systemId: string) {
  return req<{ system: WsSystemRow }>(
    `/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}`
  );
}

/** V6-1-12: Fetch computed obligations for a system. */
export function getSystemObligations(slug: string, systemId: string) {
  return req<{
    obligations: {
      id: string;
      rule_id: string | null;
      obligation_json: string | null;
      citation: string | null;
      rationale?: string | null;
      computed_at: string;
      obligation_set_id: string | null;
    }[];
    meta: {
      obligation_set_id: string;
      rules_snapshot_sha256: string;
      vocabulary_version: string;
      computed_at: string;
    } | null;
  }>(
    `/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/obligations`
  );
}

/** V85-1-8: two-register (product + cited) obligation summary payload. */
export interface ObligationSummaryEntry {
  obligation_ref: string;
  framework: string;
  title: string;
  plain_description: string;
  citation?: string;
  statute_ref?: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: 'met' | 'unmet' | 'partial' | 'not_applicable';
}

export interface ObligationSummaryResponse {
  obligation_set_id: string | null;
  system_id: string;
  obligations: ObligationSummaryEntry[];
}

export function getObligationSummary(slug: string, systemId: string) {
  return req<ObligationSummaryResponse>(
    `/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/obligations/summary`
  );
}

/** Downloads the obligation summary PDF (deliverables renderer) and returns it as a Blob. */
export async function downloadObligationSummaryPdf(slug: string, systemId: string): Promise<Blob> {
  const res = await fetch(
    `${BASE}/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/obligations/summary/pdf`
  );
  if (!res.ok) throw new ApiError(res.status, res.statusText);
  return res.blob();
}

/** V85-6-2: per-requirement gate verdicts from the hosted OPA evaluator. */
export interface GateRequirementResult {
  requirement_id: string;
  control_id: string;
  obligation_ref: string;
  citation: string;
  expected_evidence: string;
  severity: string;
  status: 'pass' | 'deny';
  remediation?: string;
}

export interface GateEvaluationResponse {
  allow: boolean;
  results: GateRequirementResult[];
  bundle_id: string;
  system_id: string;
  workspace_id: string;
  audit_status: string;
}

export function evaluateSystemGate(slug: string, systemId: string, facts: Record<string, unknown> = {}) {
  return req<GateEvaluationResponse>(
    `/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/evaluate`,
    { method: 'POST', body: JSON.stringify(facts) }
  );
}

export interface RemediationExplanationResponse {
  requirement_id: string;
  obligation_ref: string;
  citation: string;
  explanation: string;
  accepted_evidence: string[];
  patch_draft?: string;
}

export function explainGateRequirement(slug: string, systemId: string, requirementId: string) {
  return req<RemediationExplanationResponse>(
    `/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/requirements/${encodeURIComponent(requirementId)}/explain`
  );
}

export function createWorkspaceSystem(slug: string, input: {
  name: string;
  description?: string;
  lane: 'buying' | 'building';
  origin: string;
  supplier_id?: string;
  reporting_profile_id?: string;
}) {
  return req<{ system: WsSystemRow }>(
    `/api/w/${encodeURIComponent(slug)}/systems`,
    { method: 'POST', body: JSON.stringify(input) }
  );
}

/** V104-G-12 / ADR-0077 §4: confirm "Your role for this system" (owner / admin). [] clears the answer. */
export function setSystemActorRoles(slug: string, systemId: string, actorRoles: CanonicalRole[]) {
  return req<{ system: WsSystemRow; actor_roles: CanonicalRole[] }>(
    `/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/actor-roles`,
    { method: 'PUT', body: JSON.stringify({ actor_roles: actorRoles }) }
  );
}

export function updateSystemClassification(slug: string, systemId: string, input: {
  data_classification_json?: Record<string, string>;
  exposure_level?: string;
  affected_person_categories?: string[];
  output_destinations?: string[];
  // V86-1-5 / ADR-0034: v2026.09 vocabulary fields.
  decision_role?: string;
  human_oversight_mode?: string;
  deployment_target?: string;
  data_categories?: string[];
  affected_parties?: string[];
  embedded_third_party_ai?: string[];
  // V108-8-19: the Classify step's answers (the obligation rules read these).
  deployment_jurisdictions?: string[];
  affected_persons?: string[];
  data_classes?: string[];
  autonomy_level?: string;
  consequential_decision?: boolean;
  employment_decision?: boolean;
  healthcare_decision?: boolean;
  credit_decision?: boolean;
  sector?: string;
  // V112-3-10: a system's own federal answers (null clears) and its general-purpose AI role.
  deployer_type?: 'federal_agency' | 'government_contractor' | 'other' | null;
  federal_flowdown?: boolean | null;
  gpai_role?: 'none' | 'provider' | 'deployer';
  // V112-3-9 / ADR-0099: hand answers back to the workspace default; an optional reason shown on set answers.
  reset_to_workspace?: ScopeAnswer[];
  scope_reason?: string;
}) {
  return req<{ system: WsSystemRow; obligations?: { count: number } | null }>(
    `/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/classification`,
    { method: 'PATCH', body: JSON.stringify(input) }
  );
}

// ---- V5: Control specs (ADR-0030) ----

export interface ControlSpecRow {
  spec_id: string;
  control_id: string;
  spec_version: string;
  spec_hash: string;
  applicability_json: string;
  scope_selector: string | null;
  accepted_signals_json: string;
  threshold_default_json: string;
  framework_overrides_json: string;
  freshness_max_age_seconds: number | null;
  sufficiency_json: string;
  verdict_policy: string;
  no_data_behaviour: string;
  approved_by: string | null;
  approved_at: string | null;
  changelog_json: string;
  created_at: string;
  superseded_at: string | null;
}

export interface ControlSpecOverrideRow {
  override_id: string;
  workspace_id: string;
  control_id: string;
  spec_version: string;
  threshold_json: string;
  reason: string;
  approved_by: string;
  approved_at: string;
}

export function listControlSpecs(slug: string) {
  return req<{ specs: ControlSpecRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/control-specs`
  );
}

export function listControlSpecOverrides(slug: string) {
  return req<{ overrides: ControlSpecOverrideRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/control-spec-overrides`
  );
}

// ---- V5: Controls for a system ----

export interface ControlRow {
  control_id: string;
  family: string;
  domain: string;
  title: string;
  description: string | null;
  framework_refs: string | null;
}

export function listSystemControls(slug: string, systemId: string) {
  return req<{ controls: ControlRow[]; total: number; applicable: number }>(
    `/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/controls`
  );
}

export function listAllControls(slug: string) {
  return req<{ controls: ControlRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/common-controls/all-controls`
  );
}

// ---- V91-4-4: Control coverage dashboard metric ----

export interface ControlCoverageData {
  addressed: number;
  total: number;
}

export function getControlCoverage(slug: string) {
  return req<ControlCoverageData>(
    `/api/w/${encodeURIComponent(slug)}/control-coverage`,
  );
}

// ---- V5: Monitoring bindings ----

export interface MonitoringBindingRow {
  binding_id: string;
  system_id: string;
  control_id: string;
  spec_version: string | null;
  binding_status: 'active' | 'stale' | 'unbound';
  last_pass_at: string | null;
  consecutive_pass_count: number;
  freshness_expires_at: string | null;
}

export function listSystemMonitoringBindings(slug: string, systemId: string) {
  return req<{ bindings: MonitoringBindingRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/monitoring`
  );
}

// ---- V5: CI check submissions ----

export interface CiCheckVerdictRow {
  check_id: string;
  control_id: string;
  title: string;
  status: string;
  observed_at: string;
  message: string | null;
  severity: string | null;
  citation: string | null;
  remediation: string | null;
}

export interface CiCheckSubmissionRow {
  submission_id: string;
  system_id: string;
  check_key: string;
  tool: string;
  source_format: string;
  commit_sha: string | null;
  parse_status: 'pending' | 'success' | 'error';
  parse_error: string | null;
  submitted_at: string;
  checks: CiCheckVerdictRow[];
}

export function listCiCheckSubmissions(slug: string, systemId: string) {
  return req<{ submissions: CiCheckSubmissionRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/ci-submissions`
  );
}

// ---- V5: Common control policies ----

export interface CommonControlPolicyRow {
  policy_id: string;
  workspace_id: string;
  artifact_id: string | null;
  title: string;
  description: string | null;
  control_ids_json: string;
  scope_type: 'all' | 'classification' | 'manual';
  owner_user_id: string;
  status: string;
  last_attested_at: string | null;
  attestation_cadence_months: number;
  created_at: string;
}

// ---- V102-8-7: Test library + Evidence engagement view ----

/** Columnar row for the library table. One per approved test version. */
export interface LibraryTestRow {
  test_key: string;
  title: string;
  domain: string | null;
  control_id: string | null;
  /** Framework-qualified requirement/objective references, e.g. ["cmmc-l2:3.1.1[a]", "soc2:CC6.2"]. */
  framework_refs: string[];
  /** Count of objectives (or requirements in `single` mode) this test serves. */
  objectives_served: number;
  /** Distinct frameworks across framework_refs. */
  frameworks_count: number;
  /** Primary strategy (`configuration_state`, `behavior_over_period`, ...). */
  primary_strategy: string;
  /** `high` / `partial` / `manual`. */
  automation: string;
  /** `point_in_time` / `window` / `sample` / `current_document`. */
  window_kind: string;
  window_days: number | null;
  window_size: number | null;
  methods: string[];
}

export interface LibraryTestsResponse {
  tests: LibraryTestRow[];
  total: number;
  domains: string[];
  frameworks: string[];
}

export function listLibraryTests(
  slug: string,
  opts?: { domain?: string; framework?: string; automation?: string; q?: string },
) {
  const params = new URLSearchParams();
  if (opts?.domain) params.set('domain', opts.domain);
  if (opts?.framework) params.set('framework', opts.framework);
  if (opts?.automation) params.set('automation', opts.automation);
  if (opts?.q) params.set('q', opts.q);
  const qs = params.toString();
  return req<LibraryTestsResponse>(
    `/api/w/${encodeURIComponent(slug)}/library/tests${qs ? `?${qs}` : ''}`,
  );
}

/** Objective served by a test — surfaced in the drawer's "serves" popover. */
export interface LibraryTestObjective {
  objective_id: string;
  framework_id: string;
  statement: string;
  role: 'primary' | 'corroborating';
}

/** Full test detail — the drawer reads this; hash values are only in `version`. */
export interface LibraryTestDetail {
  test: LibraryTestRow;
  met_criteria: string;
  typical_gap: string | null;
  evidence_sources: string[];
  objectives: LibraryTestObjective[];
  version: {
    test_version_id: string;
    version: number;
    version_hash: string;
    approved_by: string | null;
    approved_at: string | null;
    rationale: string | null;
  };
}

export function getLibraryTest(slug: string, testKey: string) {
  return req<{ detail: LibraryTestDetail }>(
    `/api/w/${encodeURIComponent(slug)}/library/tests/${encodeURIComponent(testKey)}`,
  );
}

/** One (test × asset × period) tuple with its latest observation. */
export interface EngagementEvidenceRow {
  test_key: string;
  test_title: string;
  asset_id: string;
  asset_name: string;
  asset_category: string;
  period_id: string;
  period_label: string;
  window_label: string;
  observed_at: string | null;
  status: 'passing' | 'failing' | 'stale' | 'no_evidence';
  methods: string[];
  evidence_hash: string | null;
  check_id: string | null;
}

export interface EngagementEvidenceResponse {
  rows: EngagementEvidenceRow[];
  total: number;
  tests: Array<{ test_key: string; title: string }>;
  periods: Array<{ period_id: string; label: string }>;
}

export function getEngagementEvidence(
  slug: string,
  opts?: { test?: string; period?: string; asset?: string },
) {
  const params = new URLSearchParams();
  if (opts?.test) params.set('test', opts.test);
  if (opts?.period) params.set('period', opts.period);
  if (opts?.asset) params.set('asset', opts.asset);
  const qs = params.toString();
  return req<EngagementEvidenceResponse>(
    `/api/w/${encodeURIComponent(slug)}/evidence/engagement${qs ? `?${qs}` : ''}`,
  );
}

export function listCommonControlPolicies(slug: string) {
  return req<{ policies: CommonControlPolicyRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/common-controls`
  );
}

export function createCommonControlPolicy(slug: string, input: {
  title: string;
  description?: string;
  control_ids: string[];
  scope_type?: 'all' | 'classification' | 'manual';
  artifact_id?: string;
}) {
  return req<{ policy: CommonControlPolicyRow }>(
    `/api/w/${encodeURIComponent(slug)}/common-controls`,
    { method: 'POST', body: JSON.stringify(input) }
  );
}

// ---- V5: System control worklist ----

export interface SystemControlStateRow {
  id: string;
  system_id: string;
  control_id: string;
  status: 'unaddressed' | 'policy_linked' | 'monitoring_configured' | 'active' | 'attention' | 'not_applicable';
  common_policy_id: string | null;
  system_policy_artifact_id: string | null;
  policy_accepted_at: string | null;
  policy_accepted_by: string | null;
  monitoring_binding_id: string | null;
  owner_user_id: string | null;
  na_rationale: string | null;
}

export interface WorklistSpecPayload {
  spec_id: string;
  spec_version: string;
  source: 'baseline' | 'custom';
  verdict_policy: string;
  freshness_max_age_seconds: number | null;
  accepted_signals: string[];
  has_workspace_override: boolean;
  approved_by: string;
  approved_at: string;
}

export interface WorklistBaselineSpec {
  spec_id: string;
  spec_version: string;
  accepted_signals: string[];
  freshness_max_age_seconds: number | null;
}

export interface WorklistPendingProposal {
  proposal_id: string;
  status: string;
  submitted_at: string;
  source: string;
}

export interface WorklistItem {
  control_id: string;
  family: string;
  domain: string;
  title: string;
  description: string | null;
  state: SystemControlStateRow | null;
  available_policies: Array<{ policy_id: string; title: string; scope_type: string }>;
  framework_refs: string[];
  spec: WorklistSpecPayload | null;
  baseline_spec: WorklistBaselineSpec | null;
  pending_proposal: WorklistPendingProposal | null;
}

export function listSystemWorklist(slug: string, systemId: string) {
  return req<{
    worklist: WorklistItem[];
    counts: Record<string, number>;
    total: number;
    applicable: number;
  }>(
    `/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/worklist`
  );
}

// ---- v11.6 V116-1-3 / ADR-0115: a control's sources (laws, frameworks) ----

export interface ControlLawSource {
  kind: 'law';
  rule_id: string;
  legislation_id: string | null;
  short_name: string;
  citation: string | null;
  url: string | null;
  standing: 'in_force' | 'upcoming' | 'stayed' | 'pending' | 'settled' | 'vacated' | 'annulled' | 'final';
  stay_kind: 'standstill' | 'court_order' | null;
  preemption_risk: 'none' | 'low' | 'high' | null;
  effective_date: string | null;
  applicability?: 'applies' | 'does_not_apply' | 'decision_needed';
  system_ids?: string[];
  counts: boolean;
  /** This law's parameters for the control (ADR-0112), e.g. { audit_interval_months: 12 }. */
  parameters?: Record<string, unknown> | null;
}

export interface ControlFrameworkSource {
  kind: 'framework';
  framework_id: string;
  short_name: string;
  version: string | null;
  url: string | null;
  requirements: Array<{ ref: string; title: string }>;
  counts: true;
}

/** A court case or enforcement action linked to the control (ADR-0113); it informs, never obliges. */
export interface ControlCaseSource {
  kind: 'case';
  case_id: string;
  short_name: string;
  court: string | null;
  decision_date: string | null;
  url: string | null;
  standing: string | null;
  weight: 'binding' | 'persuasive' | 'illustrative' | null;
  effect: 'establishes' | 'broadens' | 'narrows' | 'informs';
  holding_excerpt: string;
  pinpoint: string | null;
  counts: boolean;
}

export type ControlSource = ControlLawSource | ControlFrameworkSource | ControlCaseSource;

/** Sources for up to 100 controls (workspace-scoped). */
export function listControlSources(slug: string, controlIds: string[]) {
  return req<{ sources: Record<string, ControlSource[]> }>(
    `/api/w/${encodeURIComponent(slug)}/controls/sources?ids=${encodeURIComponent(controlIds.join(','))}`
  );
}

export function linkControlPolicy(slug: string, systemId: string, controlId: string, policyId: string) {
  return req<{ state: SystemControlStateRow }>(
    `/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/controls/${encodeURIComponent(controlId)}/link-policy`,
    { method: 'POST', body: JSON.stringify({ policy_id: policyId }) }
  );
}

export function markControlNotApplicable(slug: string, systemId: string, controlId: string, rationale: string) {
  return req<{ state: SystemControlStateRow }>(
    `/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/controls/${encodeURIComponent(controlId)}/not-applicable`,
    { method: 'POST', body: JSON.stringify({ rationale }) }
  );
}

export function applyControlSpec(slug: string, systemId: string, controlId: string, body: { spec_id: string; rationale: string }) {
  return req<{ success: boolean; status: string; control_id: string; spec_id: string }>(
    `/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/controls/${encodeURIComponent(controlId)}/apply-spec`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}

// V91-2-9: propose a customized control spec (goes through editorial queue)
export function proposeControlSpec(slug: string, systemId: string, controlId: string, body: {
  spec_version?: string;
  accepted_signals_json?: Array<{ kind: string }>;
  threshold_default_json?: Record<string, unknown>;
  freshness_max_age_seconds?: number | null;
  verdict_policy?: string;
  rationale: string;
  source_kind: 'baseline_copy' | 'customer_upload';
}) {
  return req<{ proposal_id: string }>(
    `/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/controls/${encodeURIComponent(controlId)}/propose-spec`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}

// V91-3-4: extract control spec from uploaded policy document (BYOP)
export interface ControlSpecExtractionResult {
  control_id: string;
  accepted_signals: string[];
  threshold_suggestions: Record<string, unknown>;
  freshness_suggestion_seconds: number | null;
  verdict_policy: 'all_must_pass' | 'any' | 'weighted';
  rationale: string;
  confidence: number;
  source_excerpts: string[];
}

export async function extractControlSpecFromFile(
  slug: string,
  controlId: string,
  file: File,
): Promise<ControlSpecExtractionResult> {
  const form = new FormData();
  form.append('file', file);
  form.append('control_id', controlId);
  const res = await fetch(
    `${BASE}/api/w/${encodeURIComponent(slug)}/control-specs/extract`,
    { method: 'POST', body: form, credentials: 'include' },
  );
  // The JSON body carries the ADR-0073 federal_off code + action when extraction is off for this data.
  if (!res.ok) throw new ApiError(res.status, `Extract failed: ${res.status}`, await res.json().catch(() => null));
  return res.json() as Promise<ControlSpecExtractionResult>;
}

// V91-2-10: bulk-apply baseline specs to all unaddressed controls
export function bulkApplyBaseline(slug: string, systemId: string, rationale: string) {
  return req<{ applied: number; skipped: number; controls: Array<{ control_id: string; spec_id: string; status: string }> }>(
    `/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/controls/bulk-apply-baseline`,
    { method: 'POST', body: JSON.stringify({ rationale }) }
  );
}

// ---- V5: System dependencies ----

export interface SystemDependencyRow {
  dependency_id: string;
  system_id: string;
  supplier_system_id: string;
  dependency_type: 'infrastructure' | 'model_provider' | 'data_processor' | 'api_service' | 'other';
  notes: string | null;
  supplier_name: string;
  supplier_lane: string;
  supplier_origin: string;
  created_at: string;
}

export function listSystemDependencies(slug: string, systemId: string) {
  return req<{ dependencies: SystemDependencyRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/dependencies`
  );
}

export function addSystemDependency(slug: string, systemId: string, input: {
  supplier_system_id: string;
  dependency_type?: string;
  notes?: string;
}) {
  return req<{ dependency_id: string }>(
    `/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/dependencies`,
    { method: 'POST', body: JSON.stringify(input) }
  );
}

export function removeSystemDependency(slug: string, systemId: string, depId: string) {
  return req<{ deleted: boolean }>(
    `/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/dependencies/${encodeURIComponent(depId)}`,
    { method: 'DELETE' }
  );
}

// ---- V5-8: Supplier compliance ----

export interface SupplierCert {
  framework: string;
  status: 'current' | 'expired' | 'pending' | 'not_applicable';
  valid_through?: string | null;
  report_url?: string | null;
  dpa_url?: string | null;
  signed_at?: string | null;
  notes?: string | null;
}

export interface SupplierDataHandling {
  data_categories?: string[];
  regions?: string[];
  encryption_at_rest?: boolean;
  encryption_in_transit?: boolean;
  retention_policy?: string | null;
  sub_processors?: boolean;
  sub_processor_url?: string | null;
}

export interface SupplierComplianceData {
  certifications: SupplierCert[];
  data_handling: SupplierDataHandling;
}

export interface SupplierComplianceDetailRow {
  portfolio_entry_id: string;
  supplier_display_name: string;
  risk_tier: string | null;
  vertical: string | null;
  contract_renewal_at: string | null;
  notes: string | null;
  compliance: SupplierComplianceData;
  assessment_date: string | null;
  assessor: string | null;
  assessment_rationale: string | null;
  next_review_at: string | null;
  trust_page_url: string | null;
  trust_page_last_checked_at: string | null;
  trust_page_status: string | null;
  status_page_url: string | null;
  incident_contact: string | null;
  dpa_url: string | null;
}

export function getSupplierCompliance(slug: string, entryId: string) {
  return req<{ detail: SupplierComplianceDetailRow }>(
    `/api/w/${encodeURIComponent(slug)}/suppliers/${encodeURIComponent(entryId)}/compliance`
  );
}

export function updateSupplierCompliance(slug: string, entryId: string, compliance: SupplierComplianceData) {
  return req<{ detail: SupplierComplianceDetailRow }>(
    `/api/w/${encodeURIComponent(slug)}/suppliers/${encodeURIComponent(entryId)}/compliance`,
    { method: 'PATCH', body: JSON.stringify({ compliance }) }
  );
}

export function updateSupplierAssessment(slug: string, entryId: string, input: Record<string, unknown>) {
  return req<{ detail: SupplierComplianceDetailRow }>(
    `/api/w/${encodeURIComponent(slug)}/suppliers/${encodeURIComponent(entryId)}/assessment`,
    { method: 'PATCH', body: JSON.stringify(input) }
  );
}

export function updateSupplierProfile(slug: string, entryId: string, input: Record<string, unknown>) {
  return req<{ detail: SupplierComplianceDetailRow }>(
    `/api/w/${encodeURIComponent(slug)}/suppliers/${encodeURIComponent(entryId)}/profile`,
    { method: 'PATCH', body: JSON.stringify(input) }
  );
}

export function sendDisclosureRequest(slug: string, input: {
  domain: string;
  contact_email: string;
  portfolio_entry_id?: string;
}) {
  return req<{ created: unknown[]; skipped: unknown[] }>(
    `/api/w/${encodeURIComponent(slug)}/suppliers/requests`,
    { method: 'POST', body: JSON.stringify({ suppliers: [input] }) }
  );
}

export function checkSupplierTrustPage(slug: string, entryId: string) {
  return req<{ status: string; checked_at: string }>(
    `/api/w/${encodeURIComponent(slug)}/suppliers/${encodeURIComponent(entryId)}/check-trust-page`,
    { method: 'POST' }
  );
}

export function getSupplierDisclosure(slug: string, entryId: string) {
  return req<{ disclosure: { version_id: string; content_hash: string; source: string; created_at: string; content_json: string } | null }>(
    `/api/w/${encodeURIComponent(slug)}/suppliers/${encodeURIComponent(entryId)}/disclosure`
  );
}

export function getSupplierDependents(slug: string, entryId: string) {
  return req<{ dependents: Array<{ system_name: string; lane: string; origin: string; dependency_type: string }> }>(
    `/api/w/${encodeURIComponent(slug)}/suppliers/${encodeURIComponent(entryId)}/dependents`
  );
}

// V89-8-4: Connector → Supplier auto-link

export function linkConnectorToSupplier(slug: string, connectorId: string) {
  return req<{
    linked: boolean;
    portfolio_entry_id?: string | null;
    draft?: { display_name: string; risk_tier: string; vendor_slug: string } | null;
  }>(
    `/api/w/${encodeURIComponent(slug)}/suppliers/from-connector`,
    { method: 'POST', body: JSON.stringify({ connector_id: connectorId }) }
  );
}

export function confirmSupplierFromConnector(slug: string, connectorId: string, vendorSlug: string) {
  return req<{ portfolio_entry_id: string }>(
    `/api/w/${encodeURIComponent(slug)}/suppliers/confirm-connector`,
    { method: 'POST', body: JSON.stringify({ connector_id: connectorId, vendor_slug: vendorSlug }) }
  );
}

// ---- V7-1-8: Signing topology settings (ADR-0037) ----

export type KmsProvider = 'aws_kms' | 'azure_keyvault' | 'gcp_kms' | 'vault_transit';

export interface SigningSettingsResponse {
  topology: SigningTopology;
  kms_provider: KmsProvider | null;
  /** Credential keys only — secret values are never echoed. */
  kms_credentials: Record<string, string>;
}

export interface SigningSettingsPayload {
  topology: SigningTopology;
  kms_provider?: KmsProvider;
  kms_credentials?: Record<string, string>;
}

export function getSigningSettings(slug: string) {
  return req<SigningSettingsResponse>(
    `/api/w/${encodeURIComponent(slug)}/settings/signing`
  );
}

export function putSigningSettings(slug: string, payload: SigningSettingsPayload) {
  return req<{ status: 'ok' }>(
    `/api/w/${encodeURIComponent(slug)}/settings/signing`,
    { method: 'PUT', body: JSON.stringify(payload) }
  );
}

export function testSigningConnection(
  slug: string,
  provider: KmsProvider,
  credentials: Record<string, string>,
) {
  return req<{ ok: boolean; message: string }>(
    `/api/w/${encodeURIComponent(slug)}/settings/signing/test`,
    { method: 'POST', body: JSON.stringify({ provider, credentials }) }
  );
}

// V6-4-6 / ADR-0040: Blueprint Planner

export interface BlueprintSlotObservation {
  claim: string;
  source_type: string;
  source_reference: string;
  claim_strength: string;
  observation_time: string;
  stale_after_days?: number;
}

export interface BlueprintSlotQuestion {
  question: string;
  reason: string;
}

export type SlotPosture = 'verified' | 'attested' | 'registered' | 'none';

export interface BlueprintHeatmapSlot {
  slot_id: string;
  role: string;
  fit: 'native' | 'partial' | 'substitute' | 'missing';
  posture: SlotPosture;
  observations: BlueprintSlotObservation[];
  questions: BlueprintSlotQuestion[];
  effort_band: 'xs' | 's' | 'm' | 'l' | 'xl';
  known_implementations: string[];
}

export interface BlueprintGapOption {
  product_name: string;
  fit_level: 'native' | 'partial' | 'substitute';
  effort_band: 'xs' | 's' | 'm' | 'l' | 'xl';
  obligation_set_ids: string[];
  evidence_parsers: string[];
  regulatory_deadline: string | null;
}

export interface BlueprintGapRow {
  slot_id: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  blocks: string[];
  effort_band: 'xs' | 's' | 'm' | 'l' | 'xl';
  options?: BlueprintGapOption[];
}

export interface BlueprintRoadmapItem {
  id: string;
  gap_slot_id: string;
  system_id: string | null;
  blocked_artifact: string | null;
  priority: number;
  regulatory_deadline: string | null;
  status: 'open' | 'registered' | 'partial' | 'verified';
}

export interface BlueprintDiff {
  gaps_closed: string[];
  gaps_opened: string[];
  severity_changes: Array<{ slot_id: string; old_severity: string; new_severity: string }>;
  roadmap_added: number;
  roadmap_removed: number;
}

export interface BlueprintResponse {
  blueprint: {
    blueprint_id: string;
    heatmap: BlueprintHeatmapSlot[];
    gaps: BlueprintGapRow[];
    roadmap: BlueprintRoadmapItem[];
    summary: {
      total_slots: number;
      native: number;
      partial: number;
      substitute: number;
      missing: number;
    };
    inventory: {
      connectors_scanned: number;
      discovered_tools_scanned: number;
      ci_submissions_scanned: number;
      items_count: number;
    };
    computed_at: string;
  };
}

export function getBlueprint(slug: string) {
  return req<BlueprintResponse>(
    `/api/w/${encodeURIComponent(slug)}/blueprint`
  );
}

export function rerunBlueprint(slug: string) {
  return req<BlueprintResponse>(
    `/api/w/${encodeURIComponent(slug)}/blueprint/rerun`,
    { method: 'POST' }
  );
}

export interface BlueprintExportResult {
  version_id: string;
  verify_url: string;
  diff: BlueprintDiff | null;
}

export function exportBlueprintPackage(slug: string) {
  return req<BlueprintExportResult>(
    `/api/w/${encodeURIComponent(slug)}/blueprint/export`,
    { method: 'POST' }
  );
}

// V89-3-2: Slot evidence endpoint

export interface SlotEvidenceCheck {
  check_id: string;
  control_id: string;
  status: string;
  observed_at: string;
  collector_version: string | null;
  evidence_hash: string | null;
  requirement_ref: string | null;
  claim_text: string | null;
  conformance_score: number | null;
  implementation_status: string | null;
  connector_kind: string;
  framework_controls: Array<{
    framework_id: string;
    control_id: string;
    control_title: string;
  }>;
}

export interface SlotEvidenceResponse {
  slot_id: string;
  checks: SlotEvidenceCheck[];
  posture: SlotPosture;
  last_checked: string | null;
}

export function getSlotEvidence(slug: string, slotId: string) {
  return req<SlotEvidenceResponse>(
    `/api/w/${encodeURIComponent(slug)}/blueprint/slot/${encodeURIComponent(slotId)}/evidence`
  );
}

// V85-6-4: Setup wizard backend (gaps E7, G5)

export interface SetupDetectedSlot {
  slot_id: string;
  role: string;
  detected_by: 'connector' | 'repo_scan' | 'interview' | 'ci_activity';
  fit: 'native' | 'partial' | 'substitute' | 'missing';
  tool_name?: string;
}

export interface SetupMissingSlot {
  slot_id: string;
  role: string;
  hint?: string;
}

export interface SetupSummary {
  detected: SetupDetectedSlot[];
  missing: SetupMissingSlot[];
  ci_provider: 'github_actions' | 'gitlab_ci' | 'jenkins' | 'circleci' | null;
  connectors_scanned: number;
  systems_registered: number;
  obligations_computed: number;
  computed_at: string;
}

export function getSetupSummary(slug: string) {
  return req<SetupSummary>(`/api/w/${encodeURIComponent(slug)}/setup/summary`);
}

export interface GacConfigResponse {
  config_yaml: string;
  ci_provider: string;
  action_ref: string;
  origin: string;
  /** V108-1-3: the repository secret the workflow reads, the facts file path and its starting content. */
  secret_name: string;
  facts_path: string;
  facts_json: string;
  /** Which published GAC Pro signer identity the workflow pins (never repository variables). */
  signer: 'production' | 'non_production';
}

/** Generates a CI workflow config from the server-detected CI provider,
 * the platform-pinned action reference, and this request's own origin —
 * never a client-typed action ref or API address (gap E7). */
export function generateGacConfig(slug: string, systemId: string) {
  return req<GacConfigResponse>(
    `/api/w/${encodeURIComponent(slug)}/setup/gac-config`,
    { method: 'POST', body: JSON.stringify({ system_id: systemId }) }
  );
}

// --- V91-5-5: Org policy governance ---

export function getOrgPolicy(orgSlug: string) {
  return req<{
    policy_source: string;
    policy_source_set_by: string | null;
    policy_source_set_at: string | null;
    caller_role: string | null;
  }>(`/api/o/${encodeURIComponent(orgSlug)}/policy`);
}

export function updateOrgPolicy(orgSlug: string, policySource: string) {
  return req<{
    policy_source: string;
    policy_source_set_by: string | null;
    policy_source_set_at: string | null;
  }>(
    `/api/o/${encodeURIComponent(orgSlug)}/policy`,
    { method: 'PATCH', body: JSON.stringify({ policy_source: policySource }) }
  );
}

// ---- Team settings (TeamSettings.tsx migration) ----------------------------

export interface TeamInvitation {
  invitation_id: string;
  invited_email: string;
  role: string;
  expires_at: string;
  created_at: string;
}

export function listTeamInvitations(slug: string) {
  return req<{ invitations: TeamInvitation[] }>(
    `/api/w/${encodeURIComponent(slug)}/settings/team/invitations`,
  );
}

export interface CreateInvitationResponse {
  accept_url: string;
  invitation: { invited_email: string; role: string };
  email_delivery?: { status: 'sent' | 'not_configured' | 'error'; detail?: string };
}

export function createTeamInvitation(
  slug: string,
  body: { invited_email: string; role: string; is_external?: boolean },
) {
  return req<CreateInvitationResponse>(
    `/api/w/${encodeURIComponent(slug)}/settings/team/invitations`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export function revokeTeamInvitation(slug: string, invitationId: string) {
  return req<void>(
    `/api/w/${encodeURIComponent(slug)}/settings/team/invitations/${encodeURIComponent(invitationId)}`,
    { method: 'DELETE' },
  );
}

export interface JoinRequestRow {
  request_id: string;
  user_id: string;
  email: string;
  display_name: string;
  requester_ip_country: string | null;
  requested_at: string;
}

export function listJoinRequests(slug: string) {
  return req<{ join_requests: JoinRequestRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/settings/team/join-requests`,
  );
}

export function approveJoinRequest(
  slug: string,
  requestId: string,
  body: { role: string; recognized: boolean },
) {
  return req<void>(
    `/api/w/${encodeURIComponent(slug)}/settings/team/join-requests/${encodeURIComponent(requestId)}/approve`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export function denyJoinRequest(slug: string, requestId: string) {
  return req<void>(
    `/api/w/${encodeURIComponent(slug)}/settings/team/join-requests/${encodeURIComponent(requestId)}/deny`,
    { method: 'POST' },
  );
}

export interface OrphanDomainUserRow {
  id: string;
  domain: string;
  user_id: string;
  email: string;
  display_name: string;
  signed_up_at: string;
  notified: boolean;
}

export function listOrphanDomainUsers(slug: string) {
  return req<{ orphans: OrphanDomainUserRow[] }>(
    `/api/w/${encodeURIComponent(slug)}/settings/team/orphan-domain-users`,
  );
}

export function markOrphansNotified(slug: string, orphanIds: string[]) {
  return req<void>(
    `/api/w/${encodeURIComponent(slug)}/settings/team/orphan-domain-users/mark-notified`,
    { method: 'POST', body: JSON.stringify({ orphan_ids: orphanIds }) },
  );
}

export function leaveWorkspace(slug: string) {
  return req<void>(
    `/api/w/${encodeURIComponent(slug)}/settings/team/leave`,
    { method: 'POST' },
  );
}

export function updateTeamMemberRole(slug: string, userId: string, body: { role: string }) {
  return req<{ member: WorkspaceMember }>(
    `/api/w/${encodeURIComponent(slug)}/settings/team/members/${encodeURIComponent(userId)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

export function removeTeamMember(slug: string, userId: string) {
  return req<void>(
    `/api/w/${encodeURIComponent(slug)}/settings/team/members/${encodeURIComponent(userId)}`,
    { method: 'DELETE' },
  );
}

// ---- Register item state + evidence (RegisterItemDetail.tsx migration) ------

export function getRegisterItemState(slug: string, itemId: string) {
  return req<{
    state: {
      assigned_to: { user_id: string; email: string; display_name: string } | null;
      required_maturity_override: {
        level: number;
        reason: string | null;
        set_at: string | null;
        set_by_user_id: string | null;
      } | null;
    };
  }>(
    `/api/w/${encodeURIComponent(slug)}/register/${encodeURIComponent(itemId)}/state`,
  );
}

export function assignRegisterItem(slug: string, itemId: string, assigneeUserId: string | null) {
  return req<void>(
    `/api/w/${encodeURIComponent(slug)}/register/${encodeURIComponent(itemId)}/assign`,
    { method: 'POST', body: JSON.stringify({ assignee_user_id: assigneeUserId }) },
  );
}

export function getRegisterItemEvidence<T = unknown>(slug: string, itemId: string) {
  return req<T>(
    `/api/w/${encodeURIComponent(slug)}/register/${encodeURIComponent(itemId)}/evidence`,
  );
}

// ---- Composite register item detail (V9-4-2) ---------------------------
// Returns item + workspace state + control evidence + frameworks in one
// round-trip, eliminating 5+ sequential fetches from RegisterItemDetail.tsx.

export interface RegisterItemDetailComposite {
  item: RiskItem;
  frameworkMappings: Array<{
    framework_id: string;
    framework_name: string;
    control_ref: string;
    control_title: string;
    relationship: string;
  }>;
  state: {
    assigned_to: { user_id: string; email: string; display_name: string } | null;
    required_maturity_override: {
      level: number;
      reason: string | null;
      set_at: string | null;
      set_by_user_id: string | null;
    } | null;
  } | null;
  control_evidence: ControlEvidenceResponse | null;
  frameworks: Framework[];
}

export function getRegisterItemDetail(slug: string, itemId: string) {
  return req<RegisterItemDetailComposite>(
    `/api/w/${encodeURIComponent(slug)}/register/${encodeURIComponent(itemId)}/detail`,
  );
}

export function listEvidenceArtifacts<T = unknown>(slug: string) {
  return req<T>(
    `/api/w/${encodeURIComponent(slug)}/evidence`,
  );
}

export function attachEvidenceArtifact(
  slug: string,
  artifactId: string,
  body: { risk_item_id: string; attestation_cadence_months: number },
) {
  return req<void>(
    `/api/w/${encodeURIComponent(slug)}/evidence/${encodeURIComponent(artifactId)}/attach`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export function attestEvidenceBinding(
  slug: string,
  itemId: string,
  bindingId: string,
  body: { comment?: string } = {},
) {
  return req<void>(
    `/api/w/${encodeURIComponent(slug)}/register/${encodeURIComponent(itemId)}/evidence/${encodeURIComponent(bindingId)}/attest`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export function setEvidenceMaturityOverride(
  slug: string,
  itemId: string,
  body: { level: number | null; reason?: string },
) {
  return req<void>(
    `/api/w/${encodeURIComponent(slug)}/register/${encodeURIComponent(itemId)}/evidence/maturity-override`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

// ---- Billing (Billing.tsx migration) --------------------------------------

export interface BillingSummaryRow {
  plan_id: string;
  plan_name: string;
  tier: 'free' | 'growth' | 'enterprise';
  status: string;
  cancel_at_period_end: boolean;
  trial_ends_at: string | null;
  current_period_end: string | null;
  archived_at: string | null;
  archive_days_remaining: number | null;
  in_grace: boolean;
}

export function getBillingSummary(slug: string) {
  return req<BillingSummaryRow>(
    `/api/w/${encodeURIComponent(slug)}/billing`,
  );
}

export function startBillingTrial(slug: string, planId: string) {
  return req<{ trial_ends_at?: string }>(
    `/api/w/${encodeURIComponent(slug)}/billing/trial`,
    { method: 'POST', body: JSON.stringify({ plan_id: planId }) },
  );
}

export function cancelBilling(slug: string) {
  return req<void>(
    `/api/w/${encodeURIComponent(slug)}/billing/cancel`,
    { method: 'POST' },
  );
}

export function subscribeByInvoice(
  slug: string,
  body: { customer_name: string; billing_email: string; po_reference?: string; plan_id: string },
) {
  return req<{
    hosted_invoice_url?: string | null;
    invoice_pdf_url?: string | null;
    trial_ends_at?: string | null;
    current_period_end?: string | null;
  }>(
    `/api/w/${encodeURIComponent(slug)}/billing/subscribe-by-invoice`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export function restoreBilling(slug: string) {
  return req<void>(
    `/api/w/${encodeURIComponent(slug)}/billing/restore`,
    { method: 'POST' },
  );
}

// --- Coverage regions (v9.2 / V92-6-1, D-92-3) ---
export type CoverageRegion = 'US' | 'EU' | 'APAC' | 'LATAM' | 'MENA' | 'SSA' | 'GLOBAL';
export const getCoverageRegions = (slug: string) =>
  req<{ coverage_regions: CoverageRegion[] }>(`/api/w/${slug}/reporting-profiles/coverage-regions`);
export const setCoverageRegions = (slug: string, coverage_regions: CoverageRegion[]) =>
  req<{ coverage_regions: CoverageRegion[] }>(
    `/api/w/${slug}/reporting-profiles/coverage-regions`,
    { method: 'PATCH', body: JSON.stringify({ coverage_regions }) },
  );

export interface CoverageImpact {
  coverage_regions: CoverageRegion[];
  total_active_rules: number;
  applicable_rules: number;
  filtered_rules: number;
  by_region: Record<string, number>;
  filtered_regions: { region: string; count: number }[];
}
export const getCoverageImpact = (slug: string) =>
  req<CoverageImpact>(`/api/w/${slug}/reporting-profiles/coverage-impact`);

// ---- V96-2-8: security posture connectors ---------------------------------
// /api/w/:slug/posture-connectors — list: any member; writes: owner/admin.
// Credentials are write-only: nothing here ever reads a secret back.

/**
 * G-1f (D-CI-30): any kind the connector manifest manages on the connections
 * page. A string, narrowed where needed by `isPostureFormKind()`
 * (components/PostureConnections), which reads the shared manifest.
 */
export type PostureConnectorKind = string;

/** ADR-0053 §2: a declared over-privilege, as confirmed on the connector (null = read-only). */
export interface PostureConnectorPrivilege {
  beyondReadOnly: string[];
  reason: string;
  vendorLimitation: string;
  revalidateDays: number;
  declared_at?: string;
  confirmed_at: string;
  confirmed_by?: string;
}

export interface PostureConnector {
  connector_id: string;
  kind: PostureConnectorKind;
  status: 'active' | 'disabled' | 'error';
  display_name: string;
  target: string | null;
  scopes: string[];
  configured_at: string;
  last_run_at: string | null;
  last_error_at: string | null;
  last_error_message: string | null;
  /** `preview` (LM-21): Tested-rule results, read but not counted in pass / fail. */
  checks: { pass: number; fail: number; preview?: number; last_observed_at: string | null };
  /** AWS only (non-secret; pre-fills the fix form). */
  regions?: string[] | null;
  /** Manifest-form kinds: non-secret field values (lists joined with ", ") to pre-fill the fix form. */
  form_values?: Record<string, string> | null;
  /** GCP only: workload identity (no stored secret) or a service account key (weaker). Azure OpenAI: Microsoft admin consent ('multitenant'). */
  auth_mode?: 'wif' | 'sa_key' | 'multitenant' | null;
  /** Azure OpenAI on admin consent (ADR-0062): GAC Pro's app ID, for the Reader role assignments (non-secret). */
  azure_app_id?: string | null;
  /** ADR-0062: the tenant was disconnected from Microsoft; reconnect to resume. */
  azure_disconnected?: boolean;
  /** GCP workload identity only: host project of GAC Pro's pool + service account (non-secret). */
  host_project_id?: string | null;
  host_project_number?: string | null;
  /** Keyless Google Workspace: the admin GAC Pro reads as (non-secret). */
  admin_email?: string | null;
  /** Google Cloud key-based: the service account GAC Pro signs in as (non-secret). */
  service_account_email?: string;
  /** ADR-0053 §2: declared over-privilege (admin-key kinds); null/absent = read-only. */
  privilege?: PostureConnectorPrivilege | null;
  /**
   * Machine-readable reasons the collector attached to this connection's latest checks (a fixed vocabulary:
   * e.g. `cloud_siem_not_in_use`, `datadog_not_connected`). The SPA acts on these, never on message wording.
   */
  reason_codes?: string[];
  /** V112-2-7: why the last whole run failed (couldn't-check vocabulary), or null. */
  run_reason?: string | null;
  /** ADR-0102 §6: the grant predates the manifest's added metric scopes (config checks unaffected; not an error). */
  regrant?: ConnectorRegrantState | null;
  /** ADR-0102 §6: the last run's metric read; `ok` means the new permission is in place. */
  metrics_read?: ConnectorMetricsRead | null;
}

/** V97-1-7: one-time gcloud setup that pins the pool to this connector's subject. */
export interface GcpWifTemplate {
  issuer: string;
  subject: string;
  audience: string;
  attribute_condition: string;
  principal: string;
  roles: string[];
  commands: string[];
}

export interface AwsRoleTemplate {
  trust_policy: unknown;
  managed_policies: string[];
  /** Inline policies the role carries (the secrets deny; V113-2-2 the CloudWatch metric reads). */
  inline_policies?: Array<{ name: string; policy: unknown }>;
  /** ADR-0053 keyless: the IAM OIDC identity provider to add first (absent on the ExternalId path). */
  oidc_provider?: { url: string; client_id_list: string[] };
  terraform?: string;
  /** Keyless: the role the Terraform names (parsed from the saved role ARN). */
  role_name?: string;
  /** Keyless: the role + identity provider already exist (the role has been assumed). */
  role_exists?: boolean;
}

/** Credential + config fields by kind; `kind` is added for create only. */
export type PostureCredentialBody = Record<string, string | string[] | boolean | undefined> & { name?: string; confirm_privilege?: boolean };

export interface PostureFieldError { path: string; message: string }

export const listPostureConnectors = (slug: string) =>
  req<{ connectors: PostureConnector[]; /** Days of log retention this workspace's profile requires (V104-D-5). */ log_retention_target_days?: number }>(`/api/w/${slug}/posture-connectors`);

export const createPostureConnector = (slug: string, kind: PostureConnectorKind, body: PostureCredentialBody) =>
  req<{ connector_id: string; kind: PostureConnectorKind; scopes: string[]; role_template?: AwsRoleTemplate; wif_template?: GcpWifTemplate; credential_strength?: 'weaker'; retire_key_commands?: string[] }>(
    `/api/w/${slug}/posture-connectors`,
    { method: 'POST', body: JSON.stringify({ ...body, kind }) },
  );

export const updatePostureCredentials = (slug: string, connectorId: string, body: PostureCredentialBody) =>
  req<{ connector_id: string; status: 'active'; role_template?: AwsRoleTemplate; wif_template?: GcpWifTemplate; credential_strength?: 'weaker'; retire_key_commands?: string[] }>(
    `/api/w/${slug}/posture-connectors/${encodeURIComponent(connectorId)}/credentials`,
    { method: 'PUT', body: JSON.stringify(body) },
  );

/** V101-3-8: AI resources connections found at the providers a system uses. */
export interface SystemAiResource {
  resource_id: string; kind: string; ai_role: string | null; provider_key: string | null; access_channel: string | null;
  name: string; first_seen_at: string; last_seen_at: string;
  is_new: boolean; change_id: string | null; change_status: string | null;
  /** V104-E-2: "Not used here" on this system (the API always sends these; optional for fixtures). */
  dismissed?: boolean; dismissed_reason?: string | null; dismissed_by?: string | null; dismissed_at?: string | null;
}
export const getSystemAiResources = (slug: string, systemId: string) =>
  req<{ resources: SystemAiResource[] }>(`/api/w/${slug}/systems/${encodeURIComponent(systemId)}/ai-resources`);

/** V104-D-3 (E-2): mark a found AI resource "Not used here" on this system (owner, admin, editor). */
export const dismissSystemAiResource = (slug: string, systemId: string, resourceId: string, reason?: string) =>
  req<{ dismissed: true }>(`/api/w/${slug}/systems/${encodeURIComponent(systemId)}/ai-resources/${encodeURIComponent(resourceId)}/dismissal`,
    { method: 'PUT', body: JSON.stringify(reason ? { reason } : {}) });
export const restoreSystemAiResource = (slug: string, systemId: string, resourceId: string) =>
  req<{ dismissed: false }>(`/api/w/${slug}/systems/${encodeURIComponent(systemId)}/ai-resources/${encodeURIComponent(resourceId)}/dismissal`,
    { method: 'DELETE' });

/** ADR-0062: start Microsoft admin consent for Azure OpenAI; the browser then goes to `authorize_url`. */
export const startAzureConsent = (slug: string, body: { subscription_ids: string[]; name?: string }) =>
  req<{ authorize_url: string }>(`/api/w/${slug}/posture-connectors/azure-consent`, { method: 'POST', body: JSON.stringify(body) });

/** ADR-0062: finish Microsoft admin consent with the state + code the callback put in the URL fragment. */
export const completeAzureConsent = (slug: string, body: { state: string; code?: string; error?: boolean }) =>
  // V112-2-8: other_workspace_* only when the caller is a member of the workspace that holds the tenant.
  req<{ result: string; connector_id?: string; other_workspace_slug?: string; other_workspace_name?: string }>(`/api/w/${slug}/posture-connectors/azure-consent/complete`, { method: 'POST', body: JSON.stringify(body) });

/** ADR-0062: disconnect a Microsoft-consent Azure OpenAI connection (releases the tenant, pauses the row). */
export const disconnectAzureConsent = (slug: string, connectorId: string) =>
  req<{ connector_id: string; status: 'disabled' }>(`/api/w/${slug}/posture-connectors/${encodeURIComponent(connectorId)}/azure-disconnect`, { method: 'POST' });

/** V112-2-6 (BL-25): remove a connection for good; its past checks and evidence stay (owner / admin). */
export const removePostureConnector = (slug: string, connectorId: string) =>
  req<{ connector_id: string; removed: true }>(`/api/w/${slug}/posture-connectors/${encodeURIComponent(connectorId)}`, { method: 'DELETE' });

export const setPostureConnectorStatus = (slug: string, connectorId: string, status: 'active' | 'disabled') =>
  req<{ connector_id: string; status: 'active' | 'disabled' }>(
    `/api/w/${slug}/posture-connectors/${encodeURIComponent(connectorId)}`,
    { method: 'PATCH', body: JSON.stringify({ status }) },
  );

export const runPostureConnector = (slug: string, connectorId: string) =>
  req<{ connector_id: string; workflow_id: string; status: 'queued' }>(
    `/api/w/${slug}/posture-connectors/${encodeURIComponent(connectorId)}/run`,
    { method: 'POST' },
  );

/** ADR-0053 §2: re-confirm a declared over-privilege (owner/admin). */
export const confirmPostureConnectorPrivilege = (slug: string, connectorId: string) =>
  req<{ privilege: PostureConnectorPrivilege }>(
    `/api/w/${slug}/posture-connectors/${encodeURIComponent(connectorId)}/privilege/confirm`,
    { method: 'POST' },
  );

export const getAwsRoleTemplate = (slug: string, connectorId: string) =>
  req<{ role_template: AwsRoleTemplate }>(
    `/api/w/${slug}/posture-connectors/${encodeURIComponent(connectorId)}/aws-role-template`,
  );

export const getGcpWifTemplate = (slug: string, connectorId: string) =>
  req<{ wif_template: GcpWifTemplate }>(
    `/api/w/${slug}/posture-connectors/${encodeURIComponent(connectorId)}/gcp-wif-template`,
  );

export type ScanFormat = 'csv' | 'sarif';

export interface ScanUploadResult {
  vulnerabilities: number;
  findings: { opened: number; updated: number; closed: number };
  status: 'complete' | 'processing';
}

/** Raw file body (not JSON). 422 `{code:'empty_scan'}` → resend with confirmEmpty. */
export const uploadVulnerabilityScan = (
  slug: string, connectorId: string, file: Blob, format: ScanFormat, confirmEmpty = false,
) =>
  req<ScanUploadResult>(
    `/api/w/${slug}/posture-connectors/${encodeURIComponent(connectorId)}/vulnerabilities?format=${format}${confirmEmpty ? '&confirm_empty=true' : ''}`,
    { method: 'POST', body: file, headers: { 'Content-Type': format === 'csv' ? 'text/csv' : 'application/json' } },
  );

// ---- V101-1-9 / V101-1-10: provider suggestions + provider view ------------
// /api/w/:slug/providers — reads: any member; dismiss/restore: owner/admin.

/** `systems` = distinct systems using the provider (SBOM components or listed providers). */
export type ProviderEvidenceSource = 'systems' | 'boundary' | 'supplier' | 'discovered';

export interface ProviderSuggestion {
  connector_kind: string;
  label: string;
  provider_key: string;
  provider_name: string;
  evidence: Array<{ source: ProviderEvidenceSource; count: number }>;
  privilege: boolean;
  /** Hosted channel that surfaced a cloud-connector suggestion (e.g. bedrock), else null. */
  via_channel: string | null;
  /** Only when listed with `system_id`: this system uses the provider. */
  used_here?: boolean;
}

export interface DismissedProviderSuggestion { connector_kind: string; label: string; dismissed_at: string }

export type ProviderBoundaryGap = 'no_report' | 'federal_data_not_authorized';

export interface ProviderView {
  provider: { key: string; name: string; category: string | null };
  components: Array<{ system_id: string; system_name: string; components: number; access_channels: string[] }>;
  systems: Array<{ system_id: string; name: string }>;
  supplier: { portfolio_entry_id: string; name: string; risk_tier: string | null; next_review_at: string | null } | null;
  boundary: { external_service_id: string; name: string; fedramp_status: string; documents: number; gaps: ProviderBoundaryGap[] } | null;
  connectors: Array<{ connector_id: string; kind: string; label: string; status: string; last_run_at: string | null; last_error_message: string | null; privilege: boolean }>;
  suggestion: ProviderSuggestion | null;
}

export const listProviderSuggestions = (slug: string, systemId?: string) =>
  req<{ suggestions: ProviderSuggestion[] }>(`/api/w/${slug}/providers/suggestions${systemId ? `?system_id=${encodeURIComponent(systemId)}` : ''}`);

export const listDismissedProviderSuggestions = (slug: string) =>
  req<{ dismissed: DismissedProviderSuggestion[] }>(`/api/w/${slug}/providers/suggestions/dismissed`);

export const dismissProviderSuggestion = (slug: string, kind: string) =>
  req<{ dismissed: string }>(`/api/w/${slug}/providers/suggestions/${encodeURIComponent(kind)}/dismiss`, { method: 'POST' });

export const restoreProviderSuggestion = (slug: string, kind: string) =>
  req<{ restored: string }>(`/api/w/${slug}/providers/suggestions/${encodeURIComponent(kind)}/dismiss`, { method: 'DELETE' });

export const getProviderView = (slug: string, providerKey: string) =>
  req<ProviderView>(`/api/w/${slug}/providers/${encodeURIComponent(providerKey)}`);

// ---- V96-4-9: SOC 2 Type II readiness ---------------------------------------
// All reads: any member of :slug. Creates: owner/admin.

export type Soc2RequirementStatus = 'met' | 'partial' | 'gap' | 'unmapped';
/** V109-1-12: `couldnt_check` — the newest check errored (GAC Pro couldn't judge it; never a fail). */
export type Soc2ControlState = 'pass' | 'fail' | 'stale' | 'excepted' | 'insufficient' | 'couldnt_check' | 'not_applicable' | 'no_evidence';
/** V109-1-12 (LM-9): a requirement as a check result, beside Pass / Fail. */
export type Soc2CheckState = 'pass' | 'fail' | 'couldnt_check' | 'excepted' | 'no_evidence' | 'not_applicable' | 'unmapped';

export interface Soc2Requirement {
  requirement_id: string;
  ref: string;
  title: string;
  group_ref: string | null;
  status: Soc2RequirementStatus;
  reason: string;
  /** V109-1-12 (absent from older Workers). */
  check_state?: Soc2CheckState;
  /** V109-1-12: reason codes when `check_state` is `couldnt_check` (enablers/couldnt-check.ts). */
  couldnt_check_reasons?: string[];
  /**
   * V109-1-19: one row per reason × source with the crediting controls it leaves unverified — what
   * the row lists and each fix link's subject (absent from older Workers).
   */
  couldnt_check_sources?: Array<{
    reason_code: string; connector_id: string | null; connector_kind: string | null; system_id: string | null; next_collection_at?: string | null; controls: number;
    /** V109-1-19 R3 (R2-B1): the connection's status (error → a new sign-in, disabled → turn it back on). */
    connector_status?: 'active' | 'disabled' | 'error' | null;
    /** V109-1-19 R3 (R2-H3): the test the result was for (attach evidence is pre-filled with it). */
    test_key?: string | null;
  }>;
  /** V109-1-19 R3 (R2-H5): open risk acceptances on this requirement (shown; never clears Couldn't check). */
  risk_acceptances?: RiskAcceptanceSummary[];
  crediting_controls: Array<{ control_id: string; relation: 'subsumes' | 'equivalent' | 'partial'; state: Soc2ControlState; /** 'attested' → a person attests; 'machine' → a connector collects. */ evidence_mode?: 'machine' | 'attested' | null; reason_code?: string }>;
}

/** V109-1-19 R3 (R2-H5): an open risk acceptance as readiness returns it. */
export interface RiskAcceptanceSummary { id: string; until: string; owner_user_id: string; system_id: string | null; control_id: string | null }

/** V109-1-19 R3 (Medium "Notices"): GET /api/w/:slug/notifications — the caller's own notices (any member). */
export interface NoticeRow { notification_id: string; type: string; title: string; body: string | null; href: string | null; read_at: string | null; created_at: string }
export const listNotices = (slug: string, opts: { unread?: boolean; limit?: number } = {}) =>
  req<{ notifications: NoticeRow[]; unread: number }>(`/api/w/${encodeURIComponent(slug)}/notifications?${new URLSearchParams({ ...(opts.unread ? { unread: '1' } : {}), limit: String(opts.limit ?? 5) }).toString()}`);
export const markNoticeRead = (slug: string, id: string) =>
  req<{ ok: true }>(`/api/w/${encodeURIComponent(slug)}/notifications/${encodeURIComponent(id)}/read`, { method: 'POST' });
export const markAllNoticesRead = (slug: string) =>
  req<{ ok: true; marked: number }>(`/api/w/${encodeURIComponent(slug)}/notifications/read-all`, { method: 'POST' });

/**
 * V109-1-19 R3 (R2-H3): "Attach evidence" for a test GAC Pro can't check — the Evidence library upload,
 * linked to the test (owner / admin / editor; the server checks the test exists). PDF, DOCX, MD, TXT, PNG, JPG.
 */
export async function uploadEvidenceForTest(slug: string, input: { testKey: string; file: File; title: string }) {
  const form = new FormData();
  form.set('type', 'technical');
  form.set('title', input.title);
  form.set('file', input.file);
  form.set('test_key', input.testKey);
  const res = await fetch(`${BASE}/api/w/${encodeURIComponent(slug)}/evidence/upload`, { method: 'POST', body: form }); // raw-fetch-ok: FormData multipart upload
  if (!res.ok) {
    let message = res.statusText;
    let parsed: unknown;
    try { parsed = await res.json(); const b = parsed as { detail?: string; error?: string; errors?: Array<{ message: string }> }; message = b?.errors?.[0]?.message ?? b?.detail ?? b?.error ?? message; } catch { /* not JSON */ }
    throw new ApiError(res.status, message, parsed);
  }
  return (await res.json()) as { artifact: { artifact_id: string; title: string } };
}

export interface Soc2Readiness {
  framework_id: string;
  counts: Record<Soc2RequirementStatus, number> & { total: number };
  /** V109-1-12: requirements by check result; Couldn't check is its own bucket (absent from older Workers). */
  check_counts?: Record<Soc2CheckState, number> & { total: number };
  couldnt_check_reasons?: Record<string, number>;
  readiness_pct: number;
  coverage_pct: number;
  computed_at: string;
  requirements: Soc2Requirement[];
  /** V109-5-5: Couldn't check split — stale Met (aged-out passes) and indeterminate (absent from older Workers). */
  quality?: { stale_met: number; indeterminate: number };
  /** V109-5-5: run, catalog version, baseline and counted scope the numbers were computed from. */
  stamps?: { run_id: string | null; run_finished_at: string | null; catalog_version: string | null; baseline: { framework_id: string; certification_class: string | null }; counted_environments: string[] };
}

export interface AuditPeriodRow {
  period_id: string;
  profile_id: string;
  report_type: 'type1' | 'type2';
  period_start: string;
  period_end: string;
  status: 'planned' | 'fieldwork' | 'closed';
  frozen_at: string | null;
  created_at: string;
}

export interface CertificationProfileRow {
  profile_id: string;
  /** Any program in the manifest registry (ADR-0050). */
  program: ProgramKey;
  report_type: 'type1' | 'type2' | null;
  status: 'active' | 'archived';
  target_date: string | null;
  created_at: string;
}

export interface PolicyAckStatusSummary {
  policies: Array<{ version_id: string; policy_key: string; title: string; completion_pct: number; members_required: number; acknowledged: number }>;
  overall_pct: number | null;
  required_total: number;
  acknowledged_total: number;
}

export interface PbcListResponse {
  period_id: string;
  counts: { on_file: number; partial: number; needed: number; total: number };
  items: Array<{ key: string; ref: string; requirement_title: string; request: string; status: 'on_file' | 'partial' | 'needed'; missing: string[] }>;
}

export const getSoc2Readiness = (slug: string, includeOptional = false) =>
  req<Soc2Readiness>(`/api/w/${slug}/frameworks/soc2/readiness${includeOptional ? '?optional=1' : ''}`);

/**
 * V98-3 review U5: KSI readiness for FedRAMP 20x (any member; the same FrameworkReadiness shape as SOC 2).
 * `cls` narrows to the program's certification class (A–D).
 */
export const getFrameworkReadiness = (slug: string, frameworkId: string, cls?: string | null) =>
  req<Soc2Readiness>(`/api/w/${encodeURIComponent(slug)}/frameworks/${encodeURIComponent(frameworkId)}/readiness${cls ? `?class=${encodeURIComponent(cls)}` : ''}`);

export const listAuditPeriods = (slug: string) =>
  req<{ periods: AuditPeriodRow[] }>(`/api/w/${slug}/audit-periods`);

export const createAuditPeriod = (slug: string, body: { profile_id: string; period_start: string; period_end?: string }) =>
  req<{ period: AuditPeriodRow }>(`/api/w/${slug}/audit-periods`, { method: 'POST', body: JSON.stringify(body) });

export const listCertificationProfiles = (slug: string) =>
  req<{ profiles: CertificationProfileRow[] }>(`/api/w/${slug}/certification-profiles`);

export const createSoc2Profile = (slug: string, reportType: 'type1' | 'type2' = 'type2') =>
  req<{ profile: CertificationProfileRow }>(`/api/w/${slug}/certification-profiles`, {
    method: 'POST', body: JSON.stringify({ program: 'soc2', report_type: reportType }),
  });

export const getPolicyAckStatus = (slug: string) =>
  req<PolicyAckStatusSummary>(`/api/w/${slug}/policies/status`);

export const getPbcList = (slug: string, periodId: string) =>
  req<PbcListResponse>(`/api/w/${slug}/audit-periods/${encodeURIComponent(periodId)}/pbc`);

export const freezeAuditPeriod = (slug: string, periodId: string) =>
  req<{ period: AuditPeriodRow }>(`/api/w/${slug}/audit-periods/${encodeURIComponent(periodId)}/freeze`, { method: 'POST' });

export const closeAuditPeriod = (slug: string, periodId: string) =>
  req<{ period: AuditPeriodRow }>(`/api/w/${slug}/audit-periods/${encodeURIComponent(periodId)}/close`, { method: 'POST' });

export interface Soc2PolicyTemplate {
  key: string;
  title: string;
  related_criteria: string[];
  related_controls: string[];
  body_markdown: string;
}

export const listSoc2PolicyTemplates = (slug: string) =>
  req<{ templates: Soc2PolicyTemplate[] }>(`/api/w/${slug}/policies/templates`);

export const publishSoc2Policy = (slug: string, body: { policy_key: string; title: string; body_markdown: string; requires_acknowledgment?: boolean }) =>
  req<{ policy: { version_id: string; policy_key: string; title: string; version: number } }>(`/api/w/${slug}/policies`, { method: 'POST', body: JSON.stringify(body) });

/** Correct a planned (not yet started) audit window. 409 once fieldwork began. */
export const updateAuditPeriod = (slug: string, periodId: string, body: { period_start: string; period_end?: string }) =>
  req<{ period: AuditPeriodRow }>(`/api/w/${slug}/audit-periods/${encodeURIComponent(periodId)}`, { method: 'PATCH', body: JSON.stringify(body) });

/** Owner/admin attests an attested-mode control; 422 `not_attestable` for machine controls. */
export const attestSoc2Control = (slug: string, body: { control_id: string; summary: string; evidence_sha256?: string | null }) =>
  req<{ check_id: string; control_id: string }>(`/api/w/${slug}/attestations/control`, { method: 'POST', body: JSON.stringify(body) });

export interface PublishedPolicy {
  version_id: string;
  policy_key: string;
  title: string;
  version: number;
  body_markdown: string;
  body_sha256: string;
  requires_acknowledgment: number;
  published_at: string | null;
}

export const listPublishedSoc2Policies = (slug: string) =>
  req<{ policies: PublishedPolicy[] }>(`/api/w/${slug}/policies`);

export const listMyPolicyAcknowledgments = (slug: string) =>
  req<{ acknowledgments: Array<{ version_id: string; body_sha256: string; acknowledged_at: string }> }>(`/api/w/${slug}/policies/mine`);

export const acknowledgeSoc2Policy = (slug: string, versionId: string, bodySha256: string) =>
  req<{ created: boolean }>(`/api/w/${slug}/policies/${encodeURIComponent(versionId)}/acknowledge`, { method: 'POST', body: JSON.stringify({ body_sha256: bodySha256 }) });

// ---- V98-3-1 / V98-3-2: programs, Today viewer, shared counts ------------------
// GET reads: any member of :slug (server scopes counts to the caller).
// Writes on /programs/*: owner/admin or the program's lead (server-enforced).

export type { ProgramCard, ProgramCardsView, TodayView, WorkCounts, BoundaryView, ExternalServiceView, CoverageDocumentKind };

export const listPrograms = (slug: string) =>
  req<ProgramCardsView>(`/api/w/${encodeURIComponent(slug)}/programs`);

export const getToday = (slug: string) =>
  req<TodayView>(`/api/w/${encodeURIComponent(slug)}/today`);

export const getMyCounts = (slug: string) =>
  req<WorkCounts>(`/api/w/${encodeURIComponent(slug)}/me/counts`);

export const getProgramBoundary = (slug: string, program: string, periodId?: string | null) =>
  req<{ boundary: BoundaryView }>(
    `/api/w/${encodeURIComponent(slug)}/programs/${encodeURIComponent(program)}/boundary${periodId ? `?period_id=${encodeURIComponent(periodId)}` : ''}`,
  );

export const listProgramExternalServices = (slug: string, program: string) =>
  req<{ external_services: ExternalServiceView[] }>(
    `/api/w/${encodeURIComponent(slug)}/programs/${encodeURIComponent(program)}/external-services`,
  );

// ---- V102-8-1 / V102-8-2: CMMC scorecard reads --------------------------------------------
// Any member of :slug. The program (key or profile id) and the period resolve inside the
// session workspace; a planned program is 404 unless the workspace's staging preview is on.

export type { CmmcScoreResult, AffirmationGate, AffirmationRecord, PoamItem, ObjectiveReadiness, StaleMet, AssessmentDetermination, CustomerRequirement, CustomerDeadline };
export type { AffirmationKind, PoamStatus, SprsEntry } from '@aigovops/core';

const periodPath = (slug: string, program: string, periodId: string) =>
  `/api/w/${encodeURIComponent(slug)}/programs/${encodeURIComponent(program)}/periods/${encodeURIComponent(periodId)}`;

export const getProgramScore = (slug: string, program: string, periodId: string) =>
  req<{ score: CmmcScoreResult }>(`${periodPath(slug, program, periodId)}/score`);

export const getObjectiveReadiness = (slug: string, program: string, periodId: string) =>
  req<{ readiness: ObjectiveReadiness }>(`${periodPath(slug, program, periodId)}/objective-readiness`);

export const listProgramPoam = (slug: string, program: string, periodId: string) =>
  req<{ poam: PoamItem[] }>(`${periodPath(slug, program, periodId)}/poam`);

/**
 * V102-8-3 (PR B F1): put a reviewed Not met result on the POA&M — the existing
 * POST …/periods/:periodId/poam (program lead or owner/admin; server-enforced).
 */
export const openProgramPoamItem = (slug: string, program: string, periodId: string, body: { determination_id: string }) =>
  req<{ item: PoamItem }>(`${periodPath(slug, program, periodId)}/poam`, { method: 'POST', body: JSON.stringify(body) });

/** V102-8-3: the workspace's findings, for the per-requirement count (any member; same read as the Findings page). */
export const listWorkspaceFindings = (slug: string) =>
  req<{ findings: Array<{ finding_id: string; control_id: string | null; status: string; detail?: string | null; title?: string | null }> }>(`/api/w/${encodeURIComponent(slug)}/findings`);

export const getAffirmationGate = (slug: string, program: string, periodId: string) =>
  req<{ gate: AffirmationGate }>(`/api/w/${encodeURIComponent(slug)}/affirmations/gate?program=${encodeURIComponent(program)}&period_id=${encodeURIComponent(periodId)}`);

/** V102-8-2: the Met results the gate counts as stale (any member). */
export const listStaleResults = (slug: string, program: string, periodId: string) =>
  req<{ stale: StaleMet[] }>(`${periodPath(slug, program, periodId)}/stale`);

/** Determinations of the period (any member); `state=proposed` = the ones awaiting review. */
export const listDeterminations = (slug: string, program: string, periodId: string, state?: 'proposed' | 'reviewed' | 'superseded') =>
  req<{ determinations: AssessmentDetermination[] }>(`${periodPath(slug, program, periodId)}/determinations${state ? `?state=${state}` : ''}`);

/** Review a proposed determination: program lead or owner/admin; reviewer ≠ proposer (403 `sod`), server-enforced. */
export const reviewDetermination = (slug: string, program: string, periodId: string, determinationId: string) =>
  req<{ determination: AssessmentDetermination }>(`${periodPath(slug, program, periodId)}/determinations/${encodeURIComponent(determinationId)}/review`, { method: 'POST' });

// ---- V102-8-5: recording a determination from a cell -------------------------------------------

/** The judgment a recorder submits (no workspace / profile / kind: the server takes them from the session). */
export interface DeterminationJudgment {
  result: 'met' | 'not_met' | 'not_applicable' | 'no_evidence';
  gap_type?: 'not_documented' | 'not_implemented' | 'not_operating' | 'not_effective' | null;
  rationale?: string | null;
  evidence_refs?: Array<{ kind: string; id: string }>;
}

/** The review hand-off on a determination row (GET …/determinations). */
export interface DeterminationTrail {
  review_request?: { reviewer_user_id: string; requested_by: string; at: string; official: boolean } | null;
  sent_back?: { by: string; at: string; reason: string } | null;
}

/** Record a result for one cell: the objective's control owner, the program lead or owner/admin (403 otherwise). */
export const proposeDetermination = (slug: string, program: string, periodId: string, body: DeterminationJudgment & { objective_id: string; asset_id?: string | null }) =>
  req<{ determination: AssessmentDetermination }>(`${periodPath(slug, program, periodId)}/determinations`, { method: 'POST', body: JSON.stringify(body) });

/** Propose a replacement for a reviewed result (same writers); the original counts until the replacement is reviewed. */
export const supersedeDetermination = (slug: string, program: string, periodId: string, determinationId: string, body: DeterminationJudgment) =>
  req<{ determination: AssessmentDetermination }>(`${periodPath(slug, program, periodId)}/determinations/${encodeURIComponent(determinationId)}/supersede`, { method: 'POST', body: JSON.stringify(body) });

/** Send a proposal back with what should change: program lead or owner/admin; never its preparer (403 `sod`). */
export const sendBackDetermination = (slug: string, program: string, periodId: string, determinationId: string, reason: string) =>
  req<{ determination: AssessmentDetermination; sent_back: { by: string; at: string; reason: string } }>(
    `${periodPath(slug, program, periodId)}/determinations/${encodeURIComponent(determinationId)}/send-back`, { method: 'POST', body: JSON.stringify({ reason }) });

export interface ReviewerCandidateRow {
  user_id: string;
  basis: 'program_lead' | 'owner' | 'admin';
  official: boolean;
  eligible: boolean;
  reason: 'preparer' | 'absent' | 'audit_firm' | null;
  absent_until: string | null;
}

/** Who should review (peers first, the affirming official last): the recorder, the program lead or owner/admin. */
export const getDeterminationReviewers = (slug: string, program: string, periodId: string, determinationId: string) =>
  req<{ determination_id: string; candidates: ReviewerCandidateRow[]; routed_to: string | null; official_only: boolean }>(
    `${periodPath(slug, program, periodId)}/determinations/${encodeURIComponent(determinationId)}/reviewers`);

/** Ask one candidate to review (a preparer → 403 `sod`; anyone else ineligible → 422 `ineligible`). */
export const requestDeterminationReview = (slug: string, program: string, periodId: string, determinationId: string, reviewerUserId: string) =>
  req<{ review_request: NonNullable<DeterminationTrail['review_request']> }>(
    `${periodPath(slug, program, periodId)}/determinations/${encodeURIComponent(determinationId)}/request-review`, { method: 'POST', body: JSON.stringify({ reviewer_user_id: reviewerUserId }) });

/** One cell's options (any member): this workspace's evidence for the objective, and whether the caller may record / review. */
export const getDeterminationOptions = (slug: string, program: string, periodId: string, objectiveId: string, assetId: string | null) =>
  req<{ objective_id: string; asset_id: string | null; can_record: boolean; can_review: boolean; evidence: ResolvedEvidenceRef[] }>(
    `${periodPath(slug, program, periodId)}/determination-options?objective_id=${encodeURIComponent(objectiveId)}${assetId ? `&asset_id=${encodeURIComponent(assetId)}` : ''}`);

/** Tests mapped to the program's objectives (any member): control → objectives, used to filter findings by requirement. */
export interface ProgramTestRow {
  test_key: string;
  title: string;
  control_id: string | null;
  domain?: string | null;
  /** The version judging the period (pinned at Audit, else current); shown behind Verify. */
  version?: {
    test_version_id: string; version: number; basis: 'pinned' | 'current'; primary_strategy: string; corroborating_strategy: string | null;
    methods: string[]; approved_at: string | null; version_hash: string;
  } | null;
  objectives: Array<{
    objective_id: string;
    role: string;
    /** The effective per-objective criteria (plain fields). */
    strategy?: {
      met_criteria: string; typical_gap: string | null; window: { kind: string; days?: number; size?: number };
      scope: 'per_asset' | 'org_wide'; automation: 'high' | 'partial' | 'manual'; primary_strategy: string; evidence_sources: string[]; overridden: boolean;
    } | null;
  }>;
}
export const listProgramTests = (slug: string, program: string, periodId: string) =>
  req<{ period_id: string; tests: ProgramTestRow[] }>(`${periodPath(slug, program, periodId)}/tests`);

// ---- V102-8-2 / H3-13: customer (prime) deadlines (PR #48) ------------------------------------
// Read: any member (with period_id each one is evaluated against that period's score).
// Writes: program lead or owner/admin (server-enforced, 403 otherwise).

export interface CustomerRequirementInput {
  customer_name: string;
  required_level: '1' | '2';
  required_status: 'met' | 'conditional' | 'final';
  required_by: string;
  contract_ref?: string | null;
  note?: string | null;
}

const creqPath = (slug: string, program: string) => `/api/w/${encodeURIComponent(slug)}/programs/${encodeURIComponent(program)}/customer-requirements`;

export const listCustomerRequirements = (slug: string, program: string, periodId: string) =>
  req<{ requirements: CustomerRequirement[]; deadlines: CustomerDeadline[] }>(`${creqPath(slug, program)}?period_id=${encodeURIComponent(periodId)}`);

export const createCustomerRequirement = (slug: string, program: string, body: CustomerRequirementInput) =>
  req<{ requirement: CustomerRequirement }>(creqPath(slug, program), { method: 'POST', body: JSON.stringify(body) });

export const updateCustomerRequirement = (slug: string, program: string, id: string, body: CustomerRequirementInput) =>
  req<{ requirement: CustomerRequirement }>(`${creqPath(slug, program)}/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) });

export const archiveCustomerRequirement = (slug: string, program: string, id: string) =>
  req<{ requirement: CustomerRequirement }>(`${creqPath(slug, program)}/${encodeURIComponent(id)}/archive`, { method: 'POST' });

/**
 * V102-8-3: upload evidence FOR an objective ("Upload for 3.x.x"): stored with its SHA-256 and the
 * objective's test. owner / admin / editor or the program lead (server-enforced). Multipart, so it
 * bypasses req()'s JSON header; the CSRF header is added by the global fetch interceptor.
 */
export async function uploadObjectiveEvidence(slug: string, program: string, periodId: string, input: { objectiveId: string; file: File; title?: string; testKey?: string | null }) {
  const form = new FormData();
  form.set('objective_id', input.objectiveId);
  form.set('file', input.file);
  if (input.title) form.set('title', input.title);
  // V109-1-19 R3 (R2-H3): the test GAC Pro couldn't check (one of the objective's; the server checks).
  if (input.testKey) form.set('test_key', input.testKey);
  const res = await fetch(`${BASE}${periodPath(slug, program, periodId)}/evidence`, { method: 'POST', body: form });
  if (!res.ok) {
    let message = res.statusText;
    let parsed: unknown;
    try { parsed = await res.json(); const b = parsed as { error?: string; detail?: string }; if (b?.error) message = b.detail ? `${b.error} — ${b.detail}` : b.error; } catch { /* not JSON */ }
    throw new ApiError(res.status, message, parsed);
  }
  return (await res.json()) as { artifact: { artifact_id: string; title: string }; objective_id: string; test_key: string | null };
}

export const listAffirmations = (slug: string, program: string) =>
  req<{ affirmations: Array<AffirmationRecord & { sprs_entry: import('@aigovops/core').SprsEntry | null }> }>(
    `/api/w/${encodeURIComponent(slug)}/affirmations?program=${encodeURIComponent(program)}`,
  );

// ---- V102-8-6: POA&M close + affirmation flow ---------------------------------------------
// closePoamItem: owner/admin or program_lead (server-enforced); the closer can never be the official.
export const closePoamItem = (slug: string, poamId: string, body: { official_user_id: string }) =>
  req<{ item: PoamItem; affirmation: AffirmationRecord | null }>(
    `/api/w/${encodeURIComponent(slug)}/poam/${encodeURIComponent(poamId)}/close`,
    { method: 'POST', body: JSON.stringify(body) },
  );

export interface RequestAffirmationBody {
  program: string;
  period_id: string;
  kind: import('@aigovops/core').AffirmationKind;
  poam_id?: string | null;
  official_user_id: string;
  expected_completion_date?: string | null;
  assessment_date?: string | null;
}

/** Freeze and request the official's signature. owner/admin or program_lead; preparer != official (409 sod). */
export const requestAffirmation = (slug: string, body: RequestAffirmationBody) =>
  req<{ affirmation: AffirmationRecord }>(`/api/w/${encodeURIComponent(slug)}/affirmations`, {
    method: 'POST', body: JSON.stringify(body),
  });

/** Stamp affirmed_at / next_due_at once the named official has signed; obligation + POA&M clock follow. */
export const finalizeAffirmation = (slug: string, affirmationId: string) =>
  req<{ affirmation: AffirmationRecord }>(
    `/api/w/${encodeURIComponent(slug)}/affirmations/${encodeURIComponent(affirmationId)}/finalize`,
    { method: 'POST' },
  );

/** "Mark as entered in SPRS" (customer action; GAC Pro never submits). Signed records only, once. */
export const recordSprsEntry = (
  slug: string,
  affirmationId: string,
  body: { entered_on: string; confirmation_artifact_id?: string | null; note?: string | null },
) =>
  req<{ sprs_entry: import('@aigovops/core').SprsEntry }>(
    `/api/w/${encodeURIComponent(slug)}/affirmations/${encodeURIComponent(affirmationId)}/sprs-entry`,
    { method: 'POST', body: JSON.stringify(body) },
  );

export const addSystemToBoundary = (slug: string, program: string, body: { system_id: string; period_id?: string | null; reason?: string }) =>
  req<{ membership: { membership_id: string } }>(
    `/api/w/${encodeURIComponent(slug)}/programs/${encodeURIComponent(program)}/boundary/systems`,
    { method: 'POST', body: JSON.stringify(body) },
  );

/** V108-8-17: undo an add from Setup (owner / admin / program lead; a reason is required). */
export const removeBoundaryMembership = (slug: string, program: string, membershipId: string, reason: string) =>
  req<{ membership: { membership_id: string } }>(
    `/api/w/${encodeURIComponent(slug)}/programs/${encodeURIComponent(program)}/boundary/memberships/${encodeURIComponent(membershipId)}/remove`,
    { method: 'POST', body: JSON.stringify({ reason }) },
  );

export const addExternalService = (slug: string, program: string, body: { name: string; provider_key?: string; link_system_ids?: string[] }) =>
  req<{ external_service: { external_service_id: string } }>(
    `/api/w/${encodeURIComponent(slug)}/programs/${encodeURIComponent(program)}/external-services`,
    { method: 'POST', body: JSON.stringify(body) },
  );

export const addCoverageDocument = (
  slug: string, program: string, serviceId: string,
  body: { kind: CoverageDocumentKind; covers_from: string; covers_to: string; reference: string },
) =>
  req<{ document: { document_id: string } }>(
    `/api/w/${encodeURIComponent(slug)}/programs/${encodeURIComponent(program)}/external-services/${encodeURIComponent(serviceId)}/documents`,
    { method: 'POST', body: JSON.stringify(body) },
  );

/** Owner/admin: start a program (any active program in the manifest registry, ADR-0050). */
export const createProgram = (
  slug: string,
  body: { program: ProgramKey; report_type?: 'type1' | 'type2'; certification_class?: 'A' | 'B' | 'C'; path?: '20x' | 'rev5'; program_options?: Record<string, unknown> },
) =>
  req<{ profile: CertificationProfileRow }>(`/api/w/${encodeURIComponent(slug)}/certification-profiles`, {
    method: 'POST', body: JSON.stringify(body),
  });

/** V108-8-15: a program's key fields — definitions, current values and what a change does now (any member). */
export interface ProgramFieldRow {
  key: string;
  label: string;
  input: 'choice' | 'text' | 'date';
  values: ReadonlyArray<{ value: string; label: string }>;
  hint: string | null;
  target: 'column' | 'option';
  default: string | null;
  optional: boolean;
  value: string | null;
  on_change: 'update' | 'new_period' | 'locked' | 'tier_change';
}
export interface ProgramFieldsView { program: string; profile_id: string; has_period: boolean; fields: ProgramFieldRow[] }
export interface ProgramFieldChangeResult {
  program: string;
  profile_id: string;
  changed: Array<{ key: string; label: string; from: string | null; to: string | null }>;
  new_period: { previous_period_id: string | null; previous_period: { period_id: string; period_start: string; period_end: string; status: string } | null;
    period: { period_id: string; period_start: string; period_end?: string }; carried: number; not_carried: number;
    /** Security review M-3: only own and automated reviewed results carry; assessor and Type I design results stay. */
    carry?: { carried: number; left_assessor: number; left_type1_design: number; left_other: number };
    previous_change?: { period_id: string; status_before: string; status_after: string; window_before: [string, string]; window_after: [string, string] } | null;
    effective_from?: string } | null;
  retention_days_raised_to: number | null;
  fields: Record<string, string | null>;
}
export const getProgramFields = (slug: string, profileId: string) =>
  req<ProgramFieldsView>(`/api/w/${encodeURIComponent(slug)}/certification-profiles/${encodeURIComponent(profileId)}/program-fields`);
/** Owner / admin. Once a period exists, a SOC 2 type / FedRAMP class change starts a new period (carried results). */
export const putProgramFields = (slug: string, profileId: string, body: { fields: Record<string, string | null>; effective_from?: string; dry_run?: boolean; close_fieldwork?: boolean }) =>
  req<{ change: ProgramFieldChangeResult; preview?: boolean }>(`/api/w/${encodeURIComponent(slug)}/certification-profiles/${encodeURIComponent(profileId)}/program-fields`, {
    method: 'PUT', body: JSON.stringify(body),
  });

/** Program lead or owner/admin: open a period (FedRAMP / ISO assessment window ≤ 12 months). */
export const createProgramPeriod = (slug: string, program: string, body: { period_start: string; period_end?: string }) =>
  req<{ period: { period_id: string } }>(
    `/api/w/${encodeURIComponent(slug)}/programs/${encodeURIComponent(program)}/periods`,
    { method: 'POST', body: JSON.stringify(body) },
  );

/** Program lead or owner/admin: move a non-SOC 2 period into fieldwork. */
export const startProgramFieldwork = (slug: string, program: string, periodId: string) =>
  req<{ period: { period_id: string } }>(
    `/api/w/${encodeURIComponent(slug)}/programs/${encodeURIComponent(program)}/periods/${encodeURIComponent(periodId)}/fieldwork`,
    { method: 'POST' },
  );

// ADR-0055 / V101-8-5: customer-run collectors — list: any member; writes: owner/admin.
export interface CustomerCollector {
  collector_id: string;
  name: string;
  status: 'awaiting_registration' | 'registered' | 'reporting' | 'silent' | 'revoked';
  key_fingerprint: string | null;
  key_registered_at: string | null;
  last_seen_at: string | null;
  last_version: string | null;
  cadence_seconds: number | null;
  out_of_date: boolean;
  created_at: string;
  revoked_at: string | null;
  /** Kinds it has reported (its configured targets). */
  kinds: string[];
  /** Expiry of an unused install code, if any. */
  code_expires_at: string | null;
  /** V105-7-6: `verification_runner` = a manual verification runner (posts verification results only). */
  purpose?: 'collector' | 'verification_runner';
}

export const listCustomerCollectors = (slug: string) =>
  req<{ collectors: CustomerCollector[] }>(`/api/w/${slug}/collectors`);

/** Owner / admin. `purpose: 'verification_runner'` registers a manual verification runner (V105-7-6), which can post verification results only. */
export const createCustomerCollector = (slug: string, name: string, purpose: 'collector' | 'verification_runner' = 'collector') =>
  req<{ collector: CustomerCollector; registration_code: string; expires_at: string }>(`/api/w/${slug}/collectors`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(purpose === 'collector' ? { name } : { name, purpose }),
  });

export const rotateCustomerCollector = (slug: string, collectorId: string) =>
  req<{ registration_code: string; expires_at: string }>(`/api/w/${slug}/collectors/${encodeURIComponent(collectorId)}/rotate`, { method: 'POST' });

export const revokeCustomerCollector = (slug: string, collectorId: string) =>
  req<{ collector: CustomerCollector }>(`/api/w/${slug}/collectors/${encodeURIComponent(collectorId)}/revoke`, { method: 'POST' });

// V102-3-4: CMMC Scope stage.
export const listAssetSuggestions = (slug: string) =>
  req<{ suggestions: AssetSuggestion[] }>(`/api/w/${slug}/assets/suggestions`);

export const listPeriodScope = (slug: string, program: string, periodId: string) =>
  req<{ assets: ScopedAsset[] }>(`/api/w/${slug}/programs/${encodeURIComponent(program)}/periods/${encodeURIComponent(periodId)}/assets`);

export const setScopeDraft = (slug: string, assetId: string, body: { period_id: string; category: string; rationale?: string | null }) =>
  req<{ draft: ScopeDraftRow }>(`/api/w/${slug}/assets/${encodeURIComponent(assetId)}/draft`,
    { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });

export const setScopeCategory = (slug: string, program: string, periodId: string, assetId: string, body: { category: string; rationale?: string | null }) =>
  req<{ membership: AssetPeriodMembership }>(`/api/w/${slug}/programs/${encodeURIComponent(program)}/periods/${encodeURIComponent(periodId)}/assets/${encodeURIComponent(assetId)}`,
    { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });

export const removeScopeAsset = (slug: string, program: string, periodId: string, assetId: string, reason: string) =>
  req<{ removed: true }>(`/api/w/${slug}/programs/${encodeURIComponent(program)}/periods/${encodeURIComponent(periodId)}/assets/${encodeURIComponent(assetId)}`,
    { method: 'DELETE', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ reason }) });
